import { FieldFilterPopover } from './FieldFilterPopover.js';
import type { FieldCondition, QueryField } from '../views/field-query.js';
import { resoudreElementsCadre } from '@taki/data-model/cadre-canvas';
import type { DeclarationCadreCanvas } from '@taki/data-model/cadre-canvas';
import { useFondCadre } from '../cadre-canvas.js';
import type { NavigationDeSurface, ChoixDeSurface } from '../cadre-canvas.js';
import { createContext, Fragment, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactNode, Ref } from 'react';
import { cn } from '../lib/cn.js';
import { useLoadingState } from '../lib/useLoadingState.js';
import { Button } from './Button.js';
import { EnTetesDeLaFiche } from './BarreDEtapes.js';
import type { EtapesDuGabarit, SuiviDuGabarit } from './fiche-contrat.js';
import { SuiviDeFiche } from './SuiviDeFiche.js';
import { SkeletonListRows } from './Skeleton.js';
import type { ToolPopoverItem } from './ToolPopover.js';
import { SortPopover } from './SortPopover.js';
import {
  IconChatTwoBubblesOval,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClose,
  IconFilter2,
  // La PILE DE FEUILLES de l'entree « Modeles » du popover de filtre : le set
  // n'a pas d'autre glyphe qui dise « ce dont on tire des copies ».
  IconLayers1,
  IconLineArrowTransferHorizontal1,
  IconPadlockSquare1,
  IconPen1,
  IconPlus,
  IconSearch,
  IconFileBookmark,
} from '../icons/index.js';
// LES GLYPHES DU FONDATEUR (dossier `icons/custom/`, dessines par lui, ecrits
// a la main). Le prototype valide du 07/08/2026 rend la rangee 3 et le tiroir
// avec CEUX-LA, pas avec des Streamline : l'etoile simple ouvre le tiroir (3b),
// l'etoile a PLUS en fabrique un (4b), et les fleches, le partage et le "..."
// tiennent la rangee 3c au niveau fiche. Ils vivent hors de `icons/generated/`
// (efface a chaque regeneration du set) et leur viewBox n'est pas 24 : le
// pourquoi tient dans l'entete de `../icons/custom/index.ts`.
import {
  IconArchive,
  IconFav,
  IconFavAddBigBig,
  IconFullSpaced,
  IconLeft,
  IconLeftBack,
  IconOpenArrow,
  IconRight,
} from '../icons/custom/index.js';
import {
  ConfirmDialog,
  ArchiveConfirmDialog,
  RemoveConfirmDialog,
  // LE TEXTE DE LA SORTIE D'UN BROUILLON, ecrit une fois la-bas comme les
  // autres : le chassis l'OUVRE, il ne le redige pas.
  brouillonAbandonConfirm,
} from './ConfirmDialog.js';
// LES FENETRES DU LOT (chantier 3_0). Elles sont ECRITES ailleurs et MONTEES
// ici, a la racine de l'arbre rendu : la rangee 3 est `relative z-10`, donc
// elle ouvre un contexte d'empilement dont un overlay ne sortirait plus. Le
// piege a deja ete paye trois fois dans ce pack (les poignees de colonne, la
// colonne de choix, et cette barre) ; il ne se repaie pas ici.
// Les deux fenetres de CONFIRMATION, elles, sont celles du chantier 2_0
// (`ConfirmDialog.tsx`), reprises telles quelles avec un NOMBRE : il n'existe
// pas de troisieme forme de confirmation dans ce produit.
import { BulkEditDialog } from './BulkEditDialog.js';
// `BULK_MAX` EST IMPORTE, JAMAIS RECOPIE. Le plafond appartient au moteur de
// lot ; le chassis, lui, doit l'ANNONCER avant d'ouvrir quoi que ce soit (voir
// `bulkAvailability` dans le corps du composant). Deux copies du meme nombre
// divergeraient au premier ajustement, et la barre laisserait alors passer un
// lot que la fenetre refuserait APRES la confirmation destructrice.
import { BulkRunner, BULK_MAX } from './BulkRunner.js';
/*
 * LES MODELES DE FICHES, ecrits ailleurs et montes ici. La fenetre a champ
 * (« Enregistrer comme modele ») et la confirmation de retrait vont a la RACINE
 * de l'arbre, jamais dans la rangee 3, pour la meme raison que les fenetres du
 * lot. Le MENU des modeles, lui, est un panneau et non une fenetre : il vit
 * dans la rangee 3a, ancre sous le bouton scinde « Ajouter ». Le PORT vient du
 * contexte (`useModelesPort`) : le chassis ne connait ni le SDK ni la famille,
 * il ne connait que le jeu que la porte declare.
 */
import { TemplateNameDialog } from './TemplateNameDialog.js';
import { TemplateMenu, TemplateRemoveDialog } from './TemplateMenu.js';
import { raisonDeRetraitDuModele, raisonDesModeles, useModelesPort } from '../lib/templates.js';
import type { ModeleDeFiche } from '../lib/templates.js';
import type { ValeurImposee } from '../lib/liens.js';
import type { RetourDeCreation } from '../lib/appAction.js';
/*
 * L'ETAT DU BROUILLON DE FICHE, DECLARE PAR LE MOTEUR ET LU ICI.
 *
 * UN SEUL ENDROIT LE DECLARE (`views/useScreen.ts`), et c'est celui qui le
 * TIENT : le chassis n'en rend que deux boutons, un rang et une fenetre. Une
 * copie du type ici aurait diverge du premier champ ajoute, en silence, et le
 * chassis aurait alors rendu un etat que le moteur ne pose plus.
 *
 * `import type` ET PAS UN IMPORT ORDINAIRE : `useScreen.ts` importe deja ce
 * fichier (les types d'action et de canvas). Un import de valeur fermerait le
 * cycle a l'execution ; un import de type est efface a la compilation, et les
 * deux modules restent chacun a leur place.
 */
import type { BrouillonState } from '../views/useScreen.js';
import type { CheminDePorte } from '../views/porteDans.js';
import { ViewSelector } from '../views/ViewSelector.js';
import type {
  BulkEditField,
  LinkedDoor,
  ProjectedRow,
  SaveState,
  ScreenFavorite,
  SortLevel,
  ViewId,
  ViewOption,
} from '../views/contract.js';

/**
 * Navigation fournie par le shell a l'ecran d'app (contexte, pour ne pas cabler
 * chaque app). `onBack` : si fourni, la puce d'icone de l'app (zone 1a) devient
 * un bouton retour (l'icone se change en fleche au survol) ; le shell y renvoie
 * vers la derniere app visitee, sinon vers le home. `backLabel` : libelle du
 * tooltip natif du bouton (ex. "Retour a Contact", sinon "Home").
 *
 * A NE PAS CONFONDRE avec `ScreenLayoutProps.onRecordExit`, qui ferme seulement
 * la FICHE ouverte et vit dans la rangee 3. Ici, on quitte l'APP entiere. Dans
 * tout ce fichier, le mot "back" est reserve a cette sortie-la.
 */
export interface ScreenNav {
  onBack?: () => void;
  backLabel?: string;
}
const ScreenNavContext = createContext<ScreenNav>({});
export function ScreenNavProvider({
  value,
  children,
}: {
  value: ScreenNav;
  children: ReactNode;
}): JSX.Element {
  return <ScreenNavContext.Provider value={value}>{children}</ScreenNavContext.Provider>;
}

/**
 * LA PUCE D'ICONE DE LA ZONE 1a, ET LE GESTE DE RETOUR QU'ELLE PORTE.
 *
 * ELLE EST EXTRAITE POUR ETRE MONTEE DEUX FOIS, pas pour ranger le fichier. Le
 * gabarit la rend en tete du rail de toute app ; le STUDIO tient son propre
 * rail (decision du fondateur : il ne monte pas `ScreenLayout`) et rendait donc
 * une puce DECORATIVE, sans le geste. Deux puces, un seul geste : c'est celui-ci
 * qui se monte des deux cotes, jamais un second ecrit a cote.
 *
 * ELLE LIT `ScreenNavContext` ELLE-MEME, et c'est ce qui fait le partage : le
 * shell pose `onBack` une fois pour tout ce qui vit sous son `<Outlet />` (les
 * apps, le hub Parametres, le studio), et n'importe quelle puce montee dedans
 * devient un bouton retour sans qu'on lui cable quoi que ce soit. Hors contexte,
 * elle reste une pastille muette : c'est le meme defaut qu'avant.
 *
 * AU SURVOL, l'icone se change en fleche back ; `backLabel` sert de tooltip
 * natif (« Retour a Contact », sinon « Home »).
 */
export function AppIconChip({
  icon,
  className,
  style,
}: {
  /** L'icone rendue au repos. Absente : rien du tout, pas une puce vide. */
  icon?: ReactNode;
  /**
   * REMPLACE LA PALETTE PAR DEFAUT (fond + encre), pas les mesures. Le studio y
   * pose sa signature a l'encre noire ; une app n'y touche pas.
   */
  className?: string;
  /** Idem, pour une palette qui se dit en jetons CSS plutot qu'en utilitaires. */
  style?: CSSProperties;
}): JSX.Element | null {
  const { onBack, backLabel } = useContext(ScreenNavContext);
  if (icon == null) return null;
  if (onBack == null) {
    /* Puce d'icone de l'app : UNE par app, en tete du rail (1a), toujours celle
       de l'app courante, jamais remplacee (G1). */
    return (
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-nav-app [&_svg]:h-[var(--puce-rail-glyphe)] [&_svg]:w-[var(--puce-rail-glyphe)]',
          'bg-puce-rail text-nav-muted',
          className,
        )}
        style={style}
      >
        {icon}
      </span>
    );
  }
  /* Le shell fournit `onBack` : la puce d'icone devient un bouton retour. Au
     survol, l'icone se change en fleche back ; au clic, retour a la derniere app
     visitee ou au home. */
  return (
    <button
      type="button"
      onClick={onBack}
      title={backLabel}
      aria-label="Retour"
      className={cn(
        'group flex h-8 w-8 shrink-0 items-center justify-center rounded-nav-app [&_svg]:h-[var(--puce-rail-glyphe)] [&_svg]:w-[var(--puce-rail-glyphe)]',
        'bg-puce-rail text-nav-muted transition-colors',
        'hover:bg-nav-selected-hover hover:text-nav-ink active:bg-nav-press',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-focus',
        className,
      )}
      style={style}
    >
      <span className="flex items-center justify-center group-hover:hidden">{icon}</span>
      <span className="hidden items-center justify-center group-hover:flex">
        <IconChevronRight size={18} className="rotate-180" />
      </span>
    </button>
  );
}

/**
 * Le placeholder de la recherche (2b) quand ni la porte ni l'ecran n'en
 * declarent. Aucune IA n'est cablee derriere : c'est un placeholder, la
 * promesse appartient au shell (Lia de Taki), jamais a l'ecran. La constante
 * vit ICI (c'est le chassis qui la rend) ; `useScreen` la re-exporte pour la
 * resolution de porte.
 */
export const DEFAULT_SEARCH_PLACEHOLDER = 'Rechercher...';

/**
 * ScreenLayout : le gabarit d'ecran STANDARD des apps MyTaki (gabarit v2).
 *
 * REGLE D'OR (cage IA) : tout ecran d'app utilise ce gabarit. Mode d'emploi
 * complet : docs/GABARIT-ECRAN.md. Une app remplit des zones nommees via des
 * props structurees ; elle ne reconstruit JAMAIS ce squelette a la main.
 *
 * LE PLAN DES ZONES (dicte par le fondateur, 04/08/2026). Le dock (D) est au
 * shell, hors gabarit :
 *
 *   ┌──────────┬────────────────────────────────────────────────┐
 *   │ [] 1a    │ 2a Titre    2b Recherche             2c Stats  │
 *   │          ├────────────────────────────────────────────────┤
 *   │ 1b Menu  │ 3a Actions           3b Outils  3c Vues        │
 *   │  = les   ├────────────────────────────────────────────────┤
 *   │  PORTES  │ 4  Tiroir : 4a tags . 4b garder . 4c favoris   │
 *   │          ├────────────────────────────────────────────────┤
 *   │          │ 5  Contenu (seule zone qui defile)             │
 *   └──────────┴────────────────────────────────────────────────┘
 *
 * Correspondance V1 -> V2 (regle de stabilite : un nouvel emplacement est une
 * lettre de plus dans sa bande, jamais une renumerotation ; 5 ne change jamais
 * de sens) : 1=1 . 2=2a . 3=2c . 4=2b+3b+4 . 4bis=3c . 6=3a . 7=SUPPRIMEE.
 *
 * - Obligatoires : 2a (title) et 5 (children).
 * - Le rail gauche (1) est TOUJOURS rendu et S'ARRETE AU MENU : identite de
 *   l'app (puce + nom) puis les entrees. Plus aucune action n'y vit : la
 *   zone 6 d'avant est la rangee 3a, la zone 7 est supprimee.
 * - Zone 1b : chaque entree designe une PORTE, un JEU DE DONNEES de l'app
 *   (la zone 1 dit QUEL JEU, la zone 4 dit QUELLE TRANCHE). Libelle + compteur
 *   mono, jamais d'icone ; actif = nav-selected + encre + aria-current. Pour un
 *   ecran V1 sans portes, rien ne change : les entrees restent ses sections.
 * - En-tete (2) : grid 3 colonnes minmax(0,1fr) / minmax(9rem,22rem) /
 *   minmax(0,1fr). Les stats (2c) ne cedent jamais, la recherche (2b) cede
 *   jusqu'a 9rem, le titre (2a) tronque en dernier. Sans stat, la grille reste
 *   la meme : le titre ne bouge pas.
 * - Rangee 3 : les ACTIONS de la porte a gauche (3a), un vide au milieu, le
 *   groupe d'OUTILS (3b : filtre, tri, etoile) puis le groupe EPINGLE (3c). La
 *   rangee reste affichee dans TOUS les etats de la zone 5 ET aux deux niveaux.
 *   Le groupe epingle garde exactement sa place, seul son CONTENU suit le
 *   niveau : au niveau collection le SELECTEUR DE VUES (jamais rendu sous
 *   condition, et il ne porte QUE les vues de collection) ; au niveau fiche le
 *   CURSEUR de fiche, un filet, puis les gestes SUR la fiche, pendant que la
 *   partie gauche devient la puce de sortie + l'indicateur d'enregistrement.
 * - Zone 4 : le TIROIR (tags + garder + favoris), replie par defaut, ouvert
 *   par le moteur au premier tag et par l'etoile ensuite. Il n'existe qu'au
 *   niveau collection et que si la porte se tranche (`sections`), a UNE
 *   exception pres : sur une porte sans tranches, le MODE ARCHIVES le fait
 *   apparaitre deploye tant qu'il dure, pour porter le tag qui explique la
 *   liste, et le fait redisparaitre en s'eteignant.
 * - L'OUTIL FILTRE (3b) EST TOUJOURS RENDU depuis le chantier 2_0. Son
 *   catalogue n'est jamais vide : le socle y injecte, tout en bas et derriere
 *   un filet, l'entree "Archives" que toute porte de toute app offre. Sur une
 *   porte sans tranches, le popover ne montre QUE cette entree.
 * - Etats standard de la zone 5 (priorite) : error > loading > empty >
 *   children ; squelette temporise par useLoadingState. ECRAN A VUES NATIVES :
 *   ne passer NI loading, NI error, NI empty, le ViewHost tient la meme
 *   priorite dans la zone 5.
 * - Scroll : uniquement la zone 5 (et le menu 1b si tres long).
 * - Le parent (route d'app du shell) doit donner une hauteur : le gabarit
 *   occupe h-full.
 *
 * RETRO-COMPATIBILITE V1, non negociable : les ecrans d'avant les portes
 * compilent et fonctionnent SANS modification. Leurs declarations changent de
 * place, pas de sens : `quickActions` se rend en 3a (plus dans le rail),
 * `onSearchChange` se rend en 2b (plus dans l'ancienne zone 4), `filters` se
 * rend en pastilles a choix unique dans la rangee 3 (l'habit V1, remplace par
 * `tools` une fois l'ecran migre). La prop `fixedActions` (zone 7) est
 * SUPPRIMEE : le grep du chantier a confirme qu'aucune app ne l'utilisait.
 */

/**
 * Entree du menu de l'app (zone 1b) : une PORTE pour un ecran v2, une section
 * libre pour un ecran V1.
 *
 * Pas d'icone : l'identite visuelle de l'app vit a UN seul endroit, la puce
 * d'icone en tete du rail (zone 1a, prop `appIcon`), jamais repetee.
 */
/** Le lieu app/space ne limite plus le cadre : chaque element natif se garde
 * individuellement. Les anciens keeps restent des raccourcis de lecture.
 * Une View Canvas n'occupe que la zone 5. Le dock reste au shell (G23).
 */
export type ScreenCanvas = DeclarationCadreCanvas & { level: 'app' | 'space' };

export interface ScreenMenuItem {
  /** Id stable de l'entree (utilise par `activeMenuId` / `onMenuSelect`). */
  id: string;
  /** Libelle affiche. */
  label: string;
  /**
   * LA PROFONDEUR de l'entree (0 par defaut) : une sous-page d'une App Canvas
   * qui garde le rail (`keeps: ['1b']`). Le gabarit decale le libelle d'un
   * cran par niveau, plafonne a `NIVEAU_DE_RAIL_MAX` ; une porte n'en a pas.
   */
  niveau?: number;
  /**
   * L'entree a des enfants, et ils sont deplies (`true`) ou replies
   * (`false`) : le gabarit pose le chevron du disclosure devant le libelle,
   * et `onBasculer` le retourne. Absent : une feuille, sans chevron.
   */
  deplie?: boolean;
  /** Replie / deplie les enfants (voir `deplie`). */
  onBasculer?: () => void;
  /**
   * RENOMMER EN PLACE (retour R4 du fondateur) : un double-clic sur l'entree
   * ouvre l'edition de son libelle dans le rail, Entree ou la sortie du champ
   * enregistre, Echap annule. Absent : le double-clic ne fait rien (pas le
   * droit, ou rien a renommer).
   */
  onRenommer?: (label: string) => void;
  /**
   * Un PREFIXE du titre 2a, dans la couleur de l'org, comme celui d'un mode
   * (« Archives : ») : « Fiche : Ada Lovelace » du centre de communication
   * (maquette hub-b). Ignore quand un mode pose deja le sien.
   */
  prefixe?: string;
  /**
   * Compteur optionnel affiche a droite, en mono. Calcule sur le jeu COMPLET
   * de la porte et GELE : il ne bouge ni au filtrage ni a la recherche.
   */
  count?: number | string;
  /**
   * Un BROUILLON attend dans cette entree (un message commence, pas envoye) :
   * un petit crayon discret, a droite du label, avant le compteur. Jamais sur
   * l'entree active, dont le texte est deja sous les yeux.
   */
  brouillon?: boolean;
  /**
   * Icone optionnelle rendue a gauche du label. Usage EXCEPTIONNEL, reserve
   * au hub Parametres du shell : les apps ne mettent JAMAIS d'icone dans leur
   * menu (regle inchangee, l'identite visuelle vit dans la puce `appIcon`).
   * Seule autre exception : la PHOTO d'une personne (P22, l'`EntreeDeRail`
   * d'un message direct), une donnee et non une icone d'app. Depuis le
   * 13/10/2026, une App Canvas peut aussi en poser une par `EntreeDeRail.icone`
   * (Smart Notes, a la demande du fondateur).
   */
  icon?: ReactNode;
  /**
   * OU l'entree se pose dans le rail. `'main'` (le defaut, et le seul que les
   * apps utilisent) : dans la liste, a la suite des precedentes. `'bottom'` :
   * en PIED de rail, apres un espace flexible et un filet discret.
   *
   * Le pied existe pour ce qui n'est pas une porte de l'ecran mais un acces
   * annexe qu'on ne veut pas voir se melanger aux portes. Le gabarit ne sait
   * rien de ce que le pied contient : c'est un placement, pas une categorie, et
   * aucune condition d'environnement ne vit ici.
   *
   * L'espace flexible s'ecrase quand la liste deborde : le pied reste alors
   * colle a la suite, dans la zone qui defile, plutot que de pousser le rail.
   */
  placement?: 'main' | 'bottom';
  /**
   * LE GROUPE de l'entree (chantier `centre-communication`) : les entrees d'un
   * meme groupe se suivent, et le gabarit pose un EN-TETE (petites capitales,
   * `--ink-400`) avant la premiere, avec un « + » facultatif qui cree une
   * entree du groupe (`onAjouter`). Usage reserve au centre de communication
   * du shell, dont le rail porte des CONVERSATIONS groupees par source (Lia,
   * Canaux, Messages directs), et au hub Parametres (Personnel, Organisation,
   * Communication, Apps ; sans « + ») : une app, elle, n'a qu'une liste de
   * portes.
   * Un groupe dont le « + » est absent n'a pas d'en-tete cliquable ; un
   * groupe « a venir » le dit par `ajouterRaison` (G16), le « + » reste rendu
   * mais inerte.
   */
  groupe?: { id: string; label: string; onAjouter?: () => void; ajouterLabel?: string; ajouterRaison?: string };
}

/** Statistique de l'en-tete (zone 2c). */
export interface ScreenStat {
  /** Valeur mise en avant (ex. "128", "2 min"). */
  value: string;
  /** Libelle sous la valeur (ex. "contacts"). */
  label: string;
}

/**
 * Un filtre tel que le gabarit le rend : pastille V1 (prop `filters`) ou
 * entree du popover de l'outil filtre (prop `tools`, catalogue de la porte).
 * La forme est un superset de ce que `useScreen().filters` rend : un ecran le
 * passe tel quel des deux cotes.
 */
export interface ScreenFilter {
  /** Id stable du filtre. */
  id: string;
  /** Libelle affiche (pastille ou entree de popover). */
  label: string;
  /**
   * Combien de lignes de la porte BRUTE ce filtre retient (compteur gele,
   * regle "un compteur ne bouge pas quand on filtre"). Rendu en detail mono
   * dans le popover de l'outil filtre ; ignore par les pastilles V1.
   */
  count?: number;
}

/**
 * Une cle de tri proposee par l'ecran (popover de l'outil tri, zone 3b).
 * `key` est une cle de colonne ou de role, la MEME que celle des niveaux de
 * `sortStack` : c'est elle qui fait le pont avec l'en-tete du tableau.
 */
export interface ScreenSortOption {
  key: string;
  label: string;
}

/**
 * LA FENETRE QU'UNE ACTION DEMANDE AVANT DE SE FAIRE.
 *
 * Quand une `ScreenAction` porte un `confirm`, c'est LE CHASSIS qui ouvre la
 * fenetre et n'appelle `onClick` qu'APRES confirmation : l'appelant (une porte,
 * un ecran, le moteur) ne gere aucune fenetre et ne peut donc pas en oublier
 * une. C'est aussi ce qui garantit qu'un meme geste porte la meme fenetre d'un
 * bout a l'autre du produit.
 *
 * Les deux fenetres du produit se composent avec `archiveConfirm(n)` et
 * `removeConfirm(n)` (`ConfirmDialog.tsx`), qui tiennent les textes et l'accord
 * du nombre : n'ecris pas ces phrases a la main dans une app.
 */
export interface ScreenConfirm {
  /** `neutral` : rien de definitif. `danger` : la seule fenetre rouge du produit. */
  tone: 'neutral' | 'danger';
  /** La question, en tete ("Archiver cette fiche ?"). */
  title: string;
  /**
   * Le corps ("Elle quitte les listes. Tu la retrouveras dans les archives.").
   *
   * UN TABLEAU REND UN PARAGRAPHE PAR ENTREE (`ConfirmDialog` le sait deja
   * faire). Une seule chaine reste la forme ordinaire ; deux paragraphes
   * servent quand la fenetre pose un FAIT puis sa CONSEQUENCE, et qu'un point
   * au milieu d'une longue phrase ne les separe pas assez (« Fiche non
   * creee »).
   */
  body: string | string[];
  /** Libelle du bouton qui fait le geste ("Archiver"). */
  confirmLabel: string;
  /** Libelle de la sortie. Defaut "Annuler", qui porte le focus par defaut. */
  cancelLabel?: string;
}

/**
 * LA FENETRE A CHAMP D'UNE ACTION : une question qui attend un MOT.
 *
 * ELLE EXISTE POUR LA MEME RAISON QUE `ScreenConfirm`, et elle se sert au meme
 * endroit : quelque chose doit se passer ENTRE le clic et le geste, et ce
 * quelque chose est une fenetre. Le declarant (le moteur, une app) dit ce qu'il
 * demande ; le chassis ouvre, tient l'attente et l'echec, et rend la reponse.
 *
 * `onSubmit` VIT ICI ET PAS SUR L'ACTION parce que c'est lui qui recoit la
 * SAISIE : `onClick` ne prend aucun argument, et lui en donner un pour un seul
 * cas obligerait les cinquante autres actions a s'expliquer sur un parametre
 * qu'elles ignorent.
 *
 * REJETER, C'EST GARDER LA FENETRE OUVERTE avec la saisie en place et
 * `errorText` dessous. Un `onSubmit` qui resout ferme la fenetre. C'est le seul
 * contrat, et il tient parce qu'une saisie perdue en silence est le pire des
 * deux resultats.
 */
export interface ScreenPrompt {
  /** Le titre de la fenetre ("Enregistrer comme modele"). */
  title: string;
  /** Le libelle du champ, au-dessus de lui ("Nom du modele"). */
  fieldLabel: string;
  /** Ce que le champ porte a l'ouverture. Vide par defaut. */
  defaultValue?: string;
  /** Texte fantome du champ, quand un exemple aide plus qu'un libelle. */
  placeholder?: string;
  /** Libelle du bouton qui fait le geste ("Enregistrer"). */
  confirmLabel: string;
  /** Libelle de la sortie. Defaut "Annuler". */
  cancelLabel?: string;
  /** La phrase montree DANS la fenetre quand `onSubmit` rejette. */
  errorText?: string;
  /**
   * LE GESTE, avec la valeur SAISIE (deja debarrassee de ses espaces de bord).
   * Il rejette pour dire l'echec : la fenetre reste alors ouverte.
   */
  onSubmit(value: string): void | Promise<void>;
}

/** Action de la rangee 3a. */
export interface ScreenAction {
  /** Id stable de l'action. */
  id: string;
  /**
   * Libelle du bouton. Plafond visuel : la rangee 3a est en h 2rem et ne coupe
   * jamais un libelle (G13) ; elle defile au-dela, le libelle complet reste en
   * title. Pour une action `kind: 'add'`, ce libelle est IGNORE (voir kind).
   */
  label: string;
  /** Donnee courte a cote du libelle, comme la duree d'un week-end masque. */
  detail?: string;
  /**
   * Icone optionnelle (icone @taki/ui conseillee, size 15). Ignoree pour une
   * action `kind: 'add'` : l'icone d'ajout est IconPlus, figee (regle G5).
   */
  icon?: ReactNode;
  /**
   * LA REGLE "+ AJOUTER" (fondateur, 04/08/2026), encodee dans le socle : une
   * action declaree `kind: 'add'` rend son libelle UNIVERSEL elle-meme
   * ("Ajouter", jamais "Nouveau materiel" ni "Ajouter item"), en PREMIER, en
   * lime, icone IconPlus. L'app declare le geste, le gabarit impose l'habit.
   */
  kind?: 'add';
  /**
   * CETTE ACTION D'AJOUT EST TENUE PAR LE SOCLE : elle n'ouvre aucune fenetre
   * d'app, elle ouvre une FICHE VIDE en zone 5 (le brouillon `~nouveau`).
   * L'assemblage la pose sur une action `kind: 'add'` declaree SANS `fn`
   * (`taki.screens.json`), et c'est le MOTEUR qui remplace alors son `onClick`
   * par `ouvrirBrouillon` (`views/useScreen.ts`).
   *
   * LE CHASSIS N'EN CHANGE PAS LE RENDU, et c'est voulu : le bouton se rend
   * exactement comme n'importe quel « + Ajouter » (meme lime, meme libelle
   * universel, meme place). Ce drapeau dit QUI tient le geste, pas de quoi il a
   * l'air. Une app d'avant ce chantier, qui porte encore son propre `onClick`,
   * ne le porte pas et continue de marcher a l'identique. Le chassis le LIT a
   * un seul endroit : la fleche des modeles (23/09/2026) ouvre ce meme
   * brouillon, pre-rempli, et ne se rend donc que sur une action qu'il tient.
   */
  brouillon?: boolean;
  /**
   * CETTE ACTION ECRIT DANS LA BASE, sans etre un ajout : la lecture seule
   * l'eteint donc, avec sa raison, comme « + Ajouter ». Par defaut une action de
   * porte est presumee LIRE (exporter, imprimer), et reste allumee.
   */
  ecrit?: boolean;
  /**
   * Variante visuelle. Par defaut, une action de la rangee 3a est SECONDAIRE :
   * surface blanche a leger contour (`--border`). Le proto v2d les voulait sans
   * cadre, le fondateur a corrige au premier essai (04/08) : un libelle sans
   * cadre se lit comme du texte pose dans le vide, pas comme un bouton.
   * `primary` = promotion en lime, possible sur decision motivee mais JAMAIS
   * deux limes sur un ecran :
   * s'il y a une action `kind: 'add'`, c'est elle le lime et toute autre
   * `primary` est rendue secondaire.
   */
  variant?: 'primary' | 'secondary';
  /**
   * COLLEE A DROITE DE LA RANGEE 3A (Flux, 13/10/2026) : les gestes d'AIDE et
   * de NAVIGATION d'un ecran (« Support », « Exécutions » du builder, comme
   * dans Activepieces) se rangent au bout de la rangee, apres un espace, et
   * jamais entre les gestes de travail. Toujours secondaire. Absent : a sa
   * place dans l'ordre declare.
   */
  cote?: 'droite';
  /**
   * Callback au clic.
   *
   * LE CONTEXTE EST FACULTATIF, ET IL N'EST PAS LA SAISIE. Une action « + »
   * declenchee depuis une CARTE LIEE cree une fiche du jeu lie : elle vient
   * donc avec ce que cette creation recoit d'office (le champ du lien, l'id de
   * la fiche parente). Ce n'est pas un mot saisi par l'utilisateur (`prompt`
   * porte ca, et lui seul) : c'est ce que le geste sait deja.
   *
   * UNE ACTION QUI N'EN A QUE FAIRE NE S'EXPLIQUE PAS : `() => void` s'assigne
   * ici sans un mot, et c'est le cas des cinquante autres.
   *
   * LE CHASSIS L'APPELLE SANS ARGUMENT (`onClick={() => action.onClick()}`, et
   * jamais `onClick={action.onClick}`) : passe tel quel a un bouton, il
   * recevrait l'EVENEMENT du navigateur comme contexte. Qui declenche l'action
   * avec un contexte le fait exprès, et de nulle part ailleurs.
   */
  onClick: (contexte?: {
    defaults?: ValeurImposee[];
    /**
     * LA FICHE VIDE AJOUTERA UN ENFANT A CE MODELE (pivot du 10/09/2026), au
     * lieu de creer une fiche reelle. Meme forme que
     * `PendingAppAction.enfantDe`, relaye tel quel par le moteur jusqu'a
     * `ouvrirBrouillon`. Absent partout ailleurs.
     */
    enfantDe?: { modele: string; lien: LinkedDoor; libelle: string };
    /**
     * LA FICHE CREEE REMPLIRA CE CHAMP, puis on y reviendra : le « Ajouter »
     * d'un champ de lien. Meme forme que `PendingAppAction.retour`.
     */
    retour?: RetourDeCreation;
  }) => void;
  /** Action desactivee (grisee, non cliquable). */
  disabled?: boolean;
  /**
   * LA RAISON de la desactivation, rendue en `title` quand `disabled` (G16 et
   * G18 : un controle eteint dit POURQUOI, et il ne disparait jamais). Phrase
   * complete, pas un mot : "Cette porte ne sait pas supprimer une fiche."
   */
  disabledReason?: string;
  /**
   * LA FENETRE A OUVRIR AVANT D'AGIR. Presente : le chassis l'ouvre et n'appelle
   * `onClick` qu'apres confirmation. Absente : le clic agit tout de suite.
   *
   * Lue aujourd'hui pour les gestes du menu "..." (`recordMenuActions`). Une action
   * de la rangee 3a qui la porterait serait servie par le meme chemin.
   *
   * DEUX FORMES, ET LA SECONDE EST PARESSEUSE. Un objet : la fenetre est connue
   * d'avance, rien ne bouge. Une FABRIQUE `() => Promise<ScreenConfirm | null>` :
   * le chassis l'attend au clic, parce que la fenetre a besoin d'un fait que
   * seul le serveur connait (« combien de fiches pointent celle-ci ? »). Le
   * calculer a chaque rendu aurait fait une lecture serveur par frappe, pour une
   * fenetre qu'on n'ouvre presque jamais.
   *
   * `null` VEUT DIRE REFUSE : aucune fenetre ne s'ouvre, `onClick` n'est pas
   * appele, et c'est la FABRIQUE qui a deja dit pourquoi (la pastille de la
   * rangee 3, `SaveState.reason`). Le chassis ne fabrique aucune phrase : il ne
   * saurait pas laquelle.
   */
  confirm?: ScreenConfirm | (() => Promise<ScreenConfirm | null>);
  /**
   * LA FENETRE A CHAMP A OUVRIR AVANT D'AGIR, quand le geste a besoin d'un MOT
   * et pas d'un oui (« Enregistrer comme modele » : il lui faut un nom).
   * Presente, elle GAGNE sur `confirm` et sur `onClick` : le chassis ouvre la
   * fenetre et appelle `prompt.onSubmit(valeur)`, jamais `onClick`. Une action
   * qui porte les deux demanderait deux fenetres pour un seul geste.
   */
  prompt?: ScreenPrompt;
  /**
   * L'ENTREE SE CONFIRME ELLE-MEME, DANS LE MENU (« Copier l'ID MCP », qui dit
   * « Copié ») : apres le clic, le menu « ... » reste ouvert ce nombre de
   * millisecondes, le temps de lire l'accuse que l'entree affiche, puis se
   * referme. Le socle n'a pas de toast, et un menu qui se ferme au clic
   * emportait l'accuse avant qu'on le voie (recette du 27/10). Un clic dehors
   * ou Echap le ferment plus tot, comme toujours. Absent : le menu se ferme au
   * clic.
   */
  garderLeMenuOuvert?: number;
}

/**
 * UNE ENTREE DU MENU « ... » D'UN CANVAS (`canvasMenuActions`) : une action,
 * et le cas echeant l'etat qu'elle bascule.
 */
export interface CanvasMenuAction extends ScreenAction {
  /**
   * Present : l'entree est un INTERRUPTEUR (« Archives »), rendue en
   * `menuitemcheckbox` avec `aria-checked`, a l'encre pleine quand elle est
   * active (G17 : un etat qu'on choisit se marque a l'encre, jamais en lime).
   * Absent : un geste ordinaire.
   */
  checked?: boolean;
}

/**
 * L'ETAT DES OUTILS ET DU TIROIR (zones 3b + 4), tel que le moteur le tient.
 *
 * La forme est calquee sur `useScreen()` : un ecran v2 passe son etat TEL QUEL
 * (`tools={screen}`), rien a recabler champ par champ ; le surplus de l'etat
 * d'ecran est ignore. Tout est optionnel : un champ absent eteint le geste
 * correspondant (un ecran sans favoris ne rend ni etoile ni tiroir de
 * favoris). Le gabarit ne DECIDE rien ici : l'ouverture du tiroir au premier
 * tag, la remise a zero au changement de porte et la correspondance exacte
 * d'un favori vivent dans le moteur.
 */
export interface ScreenToolset {
  /**
   * Le catalogue de filtres de la porte active (popover de l'outil filtre).
   * `useScreen().filters` s'y passe tel quel (les compteurs suivent).
   */
  filters?: ScreenFilter[];
  filterFields?: QueryField[];
  fieldConditions?: FieldCondition[];
  pagination?: 'client' | 'server';
  saveFieldCondition?(condition: FieldCondition): void;
  fieldSortOptions?: ScreenSortOption[];
  /** Les filtres actifs, dans l'ordre de pose : les tags 4a. */
  activeFilterIds?: string[];
  /** Pose ou retire un filtre du catalogue (popover 3b). */
  toggleFilter?(id: string): void;
  /** Retire UN filtre (la croix d'un tag 4a). Repli : `toggleFilter`. */
  removeFilter?(id: string): void;
  /** Retire tous les filtres (l'entree de sortie du popover). */
  clearFilters?(): void;
  /** La pile de tri, ORDONNEE : les tags de tri 4a. */
  sortStack?: SortLevel[];
  /** Clefs de tri autorisees par la vue active. Absentes : tout le catalogue. */
  sortKeys?: readonly string[];
  /** Un clic du popover de tri : ajoute un niveau en bout, le retire au second clic. */
  toggleSort?(key: string): void;
  /** Inverse le sens d'UN niveau (le corps d'un tag de tri). */
  invertSort?(key: string): void;
  /** Retire UN niveau (la croix d'un tag de tri). */
  removeSort?(key: string): void;
  /** Vide la pile (l'entree de sortie du popover, "Ordre d'origine"). */
  clearSort?(): void;
  /**
   * Vide les filtres ET la pile de tri EN UN SEUL RENDU : le mot "Effacer
   * filtres" de la zone 4a. Ce n'est pas `clearFilters()` puis `clearSort()`,
   * deux setState en cascade feraient clignoter la liste. Rien d'autre ne
   * bouge : ni la recherche, ni la porte, ni l'element ouvert. Absent = le mot
   * n'est pas rendu.
   */
  clearAll?(): void;
  /** Le tiroir (zone 4) est-il deplie. */
  drawerOpen?: boolean;
  /** L'etoile (3b) : ouvre / referme le tiroir a la demande. */
  setDrawerOpen?(open: boolean): void;
  /** Les favoris de la porte active (presets puis favoris gardes), zone 4c. */
  favorites?: ScreenFavorite[];
  /** Le favori qui correspond EXACTEMENT a l'etat courant, allume en 4c. */
  activeFavoriteId?: string | null;
  /** Applique un favori (pose ses filtres et sa pile de tri). */
  applyFavorite?(id: string): void;
  /**
   * Oublie un favori GARDE par l'utilisateur (croix des pastilles `user-*` de
   * la zone 4c, revelee au survol). Les presets de la porte ne se retirent
   * jamais : le chassis ne rend la croix que sur les favoris utilisateur, et
   * le moteur ignore de toute facon un id de preset.
   */
  forgetFavorite?(id: string): void;
  /** Garde l'etat courant en favori (le bouton 4b). */
  keepFavorite?(): void;
  /** Le bouton 4b est-il actif. Faux = desactive, sa raison en title. */
  canKeepFavorite?: boolean;
  /**
   * LE MODE ARCHIVES est-il allume. Ce n'est PAS un filtre de plus : le mode
   * decide SEUL du jeu, il passe AVANT tout le reste. Allume, on voit les
   * fiches archivees et RIEN D'AUTRE, et les filtres ne s'y appliquent pas (une
   * archive est hors du travail courant, la trancher par statut n'aurait pas de
   * sens). C'est pour ca qu'il vit ICI et pas dans `activeFilterIds`.
   */
  archivesMode?: boolean;
  /**
   * Allume / eteint le mode. Absent : l'entree "Archives" du popover se rend
   * quand meme, ETEINTE avec sa raison (G16) : elle est promise dans toutes les
   * apps, un ecran ne la fait pas disparaitre en oubliant de la brancher.
   *
   * ENTRER passe par une FENETRE (le chassis l'ouvre), SORTIR non : entrer
   * change entierement ce que l'ecran montre, sortir est reversible et sans
   * consequence, redemander serait de la ceremonie.
   */
  setArchivesMode?(on: boolean): void;
  /**
   * LE MODE MODELES est-il allume. JUMEAU DU MODE ARCHIVES, au meme niveau et
   * pour la meme raison : ce n'est pas un filtre de plus, il decide SEUL du jeu
   * montre. Allume, on voit les MODELES de la porte et rien d'autre, et les
   * filtres ne s'y appliquent pas (les filtres trient des fiches par statut, un
   * modele n'en a pas).
   *
   * LES DEUX MODES SONT EXCLUSIFS, et ce n'est pas au chassis de le tenir :
   * c'est l'etat d'ecran qui l'arbitre (`setArchivesMode` eteint l'autre, et
   * reciproquement), une seule fois, la ou l'adresse se lit aussi
   * (`readModesParams`). Le chassis se contente de ne jamais rendre deux tags
   * de mode a la fois, ce qui suit tout seul de cette exclusivite.
   */
  modelesMode?: boolean;
  /**
   * Allume / eteint le mode Modeles. Absent : l'entree « Modeles » du popover
   * se rend quand meme ETEINTE avec sa raison, exactement comme l'entree
   * Archives (G16), mais elle ne se rend du tout que sur une porte dont les
   * modeles sont CONFIGURES (voir `FilterPopover`).
   *
   * ENTRER passe par une FENETRE, SORTIR non : meme partage que les archives,
   * et pour la meme raison (entrer change entierement ce que l'ecran montre,
   * sortir est reversible et sans consequence).
   */
  setModelesMode?(on: boolean): void;
  /**
   * LES GESTES RARES DE LA FICHE (menu "..." de la rangee 3c) : Archiver /
   * Desarchiver et Supprimer, construits par le moteur depuis les verbes de la
   * porte (`useScreen().recordMenuActions`).
   *
   * ILS PASSENT PAR `tools` ET C'EST LE POINT. La promesse du chantier 2_0 est
   * "dans toutes les apps, sur toutes les portes, sans qu'aucune app n'ecrive
   * une ligne" : tant que ces gestes n'arrivaient que par la prop explicite, un
   * ecran qui passait `tools={screen}` sans y penser gardait un menu "..." vide
   * pendant que l'ecran d'a cote proposait Archiver et Supprimer. Meme menu,
   * meme geste, deux vocabulaires selon l'app. Passe par `tools`, un ecran deja
   * migre les recoit sans rien recabler.
   *
   * LA PROP `recordMenuActions` DE `ScreenLayoutProps` RESTE UNE SURCHARGE :
   * fournie, elle gagne ; absente, c'est ce champ qui sert.
   */
  recordMenuActions?: ScreenAction[];
  /**
   * LA SORTIE D'UNE FICHE ARRIVEE PAR UN LIEN (`useScreen().sortie`), absente
   * partout ailleurs.
   *
   * Presente, elle REMPLACE ce que la puce de sortie DIT et ce qu'elle FAIT :
   * le nom de la fiche d'ou l'on vient (« Sony FX9 n°1 ») au lieu du nom de la
   * vue de retour, et le retour a cette fiche-la au lieu de la collection. Une
   * SEULE puce dans les deux cas : ce champ ne l'ajoute pas, il la reecrit.
   *
   * Elle passe par `tools` et pas par une prop, comme `recordMenuActions` avant
   * elle : aucun ecran n'a une ligne a changer, et aucun ne peut faire
   * disparaitre le geste en oubliant de le brancher.
   */
  sortie?: {
    /** Le titre de la fiche d'ou l'on vient. Jamais vide. */
    libelle: string;
    /** Le clic : ramener a cette fiche-la. */
    retour(): void;
  };
  /**
   * LA PORTE OUVERTE DANS UNE FICHE (`useScreen().chemin`, `porteDans.ts`),
   * `null` ou absente ailleurs. Presente, le gabarit AJOUTE la puce du retour
   * au debut (« Liste : Projets ») devant la sortie, et sur la collection
   * filtree l'acces a la fiche parente ; le titre de la zone 2 dit ce qu'on
   * voit (« Tâches de Projet Test »). Sur une fiche, l'etape d'avant reste la
   * puce de sortie, que `sortie` reecrit.
   */
  chemin?: CheminDePorte | null;
  /* ------------------------------------------------------------------ */
  /* LA CREATION EN FICHE (chantier `creation-en-fiche`, 09/09/2026)     */
  /*                                                                    */
  /* « + Ajouter » n'ouvre plus une fenetre : il ouvre une FICHE VIDE en */
  /* zone 5, et rien n'est ecrit en base tant qu'on n'a pas clique       */
  /* « Creer ». Tout arrive par `tools={screen}`, comme les gestes de    */
  /* fiche avant : un ecran deja migre recoit les deux boutons et la     */
  /* fenetre de sortie sans recabler une ligne, et aucune app ne peut    */
  /* les faire disparaitre en oubliant une prop.                         */
  /*                                                                    */
  /* LE CHASSIS NE DECIDE RIEN ICI. Il ne sait pas ce qui est saisi, ni  */
  /* ce qui manque, ni si la sortie doit demander : il rend l'etat que   */
  /* le moteur tient et appelle les verbes.                              */
  /* ------------------------------------------------------------------ */

  /**
   * LA FICHE EN COURS DE CREATION, ou `null` / absent partout ailleurs. Sa
   * PRESENCE est ce qui fait passer la rangee 3 du niveau fiche en mode
   * creation : « Creer » et « Annuler » a la place de rien, le rang « nouveau »
   * a la place du compteur.
   */
  brouillon?: BrouillonState | null;
  /**
   * OUVRIR UNE FICHE VIDE. Pour « + Ajouter », le chassis ne l'appelle pas
   * lui-meme : c'est le moteur qui le pose sur l'action d'ajout
   * `brouillon: true`. Il est ici pour qu'une surface d'ecran (le raccourci du
   * dock) le trouve avec le reste de l'etat, et pour le MENU DES MODELES, le
   * seul endroit ou le chassis l'appelle (23/09/2026) : choisir un modele ouvre
   * la fiche vide pre-remplie par lui (`depuisModele`). Rend `false` quand
   * l'ouverture est refusee (acces, mode, porte sans jeu), et le moteur a alors
   * deja dit pourquoi.
   */
  ouvrirBrouillon?(contexte?: {
    porte?: string;
    defaults?: ValeurImposee[];
    depuisModele?: ModeleDeFiche;
  }): boolean;
  /** Le bouton « Creer » : c'est ici, et seulement ici, que la fiche naît. */
  creerBrouillon?(): Promise<void>;
  /**
   * Le bouton « Annuler ». Il ne ferme pas forcement : sur une fiche TOUCHEE,
   * le moteur pose `sortieEnAttente` et la fenetre « Fiche non creee » repond a
   * sa place.
   */
  annulerBrouillon?(): void;
  /** La fenetre « Fiche non creee », bouton « Quitter sans creer ». */
  confirmerSortieDuBrouillon?(): void;
  /** La meme fenetre, bouton « Revenir » (et sa sortie par Echap ou le voile). */
  reprendreBrouillon?(): void;

  /**
   * La porte active : seul `sections` est lu. `false` = cette porte ne se
   * tranche pas : l'etoile, le CATALOGUE de filtres et le tiers des RACCOURCIS
   * du tiroir (4b + 4c, filet compris) sont masques ; le groupe d'outils garde
   * le tri ET l'outil filtre, qui ne porte alors que l'entree Archives.
   * `useScreen().door` s'y passe tel quel.
   *
   * ATTENTION, LA REGLE A CHANGE AU CHANTIER 2_0 : l'outil filtre n'est plus
   * masque sur une porte sans tranches ni sur un catalogue vide. Son catalogue
   * n'est JAMAIS vide, puisque le socle y injecte l'entree Archives ; sur une
   * porte sans tranches, le popover ne montre QUE cette entree. Le tiroir, lui,
   * se rend aussi sur une telle porte tant que le mode est allume, pour porter
   * le tag qui explique la liste.
   */
  door?: {
    sections: boolean;
    /**
     * LE LIBELLE DE LA PORTE ACTIVE (`useScreen().door.label`). Le chassis n'en
     * fait qu'UNE chose : nommer le jeu en tete du menu des modeles (« Modeles
     * de Materiel »). Absent, le menu dit « cette porte » : les modeles sont
     * ranges PAR JEU, et cette ligne est ce qui explique pourquoi ceux d'a cote
     * n'y sont pas.
     */
    label?: string;
    /**
     * LE JEU DE LA PORTE (`useScreen().door.jeu`) : la famille du SDK et le
     * sous-type de l'app. Le chassis n'en fait qu'UNE chose : demander au port
     * des modeles ceux de CE jeu, pour le menu du bouton scinde « Ajouter ».
     * Absent, la fleche du bouton scinde se rend GRISEE avec sa raison (G16) :
     * proposer des modeles sans savoir lesquels ouvrirait une liste qui melange
     * deux apps.
     */
    jeu?: { family: string; type?: string } | null;
    /**
     * CETTE PORTE ALLUME-T-ELLE LES MODELES (`useScreen().door.modeles`) ?
     * Absent ou `false` : la fleche du bouton scinde se rend GRISEE avec sa
     * raison (G16), elle ne disparait pas. Le jeu dit OU ranger un modele, pas
     * si la porte en veut : c'est cette clef qui le dit, porte par porte.
     */
    modeles?: boolean;
  };
  /**
   * LE SIGNAL « UN MODELE DE PLUS » (`useScreen().modelesRevision`) : monte de un
   * a chaque modele enregistre depuis le menu de la fiche. Le chassis relit
   * alors la liste des modeles de la porte, sinon le modele tout juste
   * enregistre n'apparaissait dans le menu qu'apres un rechargement de la page.
   */
  modelesRevision?: number;

  /* ------------------------------------------------------------------ */
  /* LA SELECTION DE LIGNES ET LE LOT (chantier 3_0)                     */
  /*                                                                    */
  /* Tout arrive par `tools={screen}`, comme `recordMenuActions` au      */
  /* chantier 2_0 : le moteur pose ces champs au PREMIER niveau de son   */
  /* etat, et un ecran deja migre recoit la barre de lot sans recabler   */
  /* une seule ligne. Tout est optionnel : un champ absent eteint le     */
  /* geste correspondant, il ne casse pas l'ecran.                       */
  /*                                                                    */
  /* LE CHASSIS NE BOUCLE JAMAIS sur un lot. Il MONTE les fenetres et    */
  /* leur passe les ids ; l'ecriture sequentielle, la progression, le    */
  /* compte rendu et le rejeu des echecs vivent dans `BulkRunner`.       */
  /* ------------------------------------------------------------------ */

  /**
   * Les ids des lignes COCHEES. Non vide = la rangee 3 change de nature et
   * devient la BARRE DE LOT (son troisieme etat, apres collection et fiche).
   *
   * L'invariant du moteur, le plus important de ce chantier : une ligne qui
   * sort de l'ecran sort de la selection. Le chassis ne le tient pas, il en
   * DEPEND : il affiche ce que le moteur lui donne, et le compte ne parle
   * donc jamais de fiches qu'on ne voit plus.
   */
  selection?: string[];
  /**
   * Annule la selection : la croix de la barre, et `Echap`. Absente, la croix
   * se rend ETEINTE avec sa raison (G16) : un etat sans sortie serait une
   * nasse, on ne le cache pas, on le dit.
   */
  clearSelection?(): void;
  /**
   * LA SURFACE QUI PEUT CHOISIR AUJOURD'HUI. `'table'` : le tableau porte les
   * cases. `null` : aucune surface ne peut choisir (la selection n'existe QU'EN
   * TABLEAU, arbitrage du fondateur du 07/08/2026 : agir sur un lot demande de
   * VOIR les valeurs sur lesquelles on agit, et le tableau est la seule vue qui
   * les montre toutes).
   *
   * LE CHASSIS S'EN SERT POUR REFUSER, JAMAIS POUR ALLUMER : voir
   * `bulkActive` dans le corps du composant. Un champ absent ne doit pas
   * pouvoir eteindre la barre en silence.
   */
  selectionLevel?: 'table' | null;
  /**
   * LES CHAMPS MODIFIABLES de la vue active, pour la fenetre "Modification
   * groupee". Vide : la fenetre le DIT, elle ne s'ouvre pas vide.
   */
  bulkEditFields?: BulkEditField[];
  /**
   * LES TROIS PRIMITIVES A DOCUMENT UNIQUE. Le SDK n'expose pas d'ecriture
   * groupee, et chaque document doit passer sa validation Zod et poser son
   * `updatedBy` : le lot s'ecrit donc UNE FICHE A LA FOIS. C'est `BulkRunner`
   * qui boucle, jamais le chassis et jamais le moteur.
   */
  archiveOne?(id: string): Promise<void>;
  removeOne?(id: string): Promise<void>;
  writeCell?(id: string, fieldKey: string, value: unknown): Promise<void>;
  /**
   * CE QUE LA PORTE SAIT FAIRE, pour GRISER AVEC SA RAISON (G16), jamais
   * cacher. `null` = le geste est disponible ; une chaine = la RAISON, ecrite
   * pour un utilisateur et pas pour un developpeur (lecon du chantier 2_0 :
   * jamais "l'app doit declarer remove()").
   *
   * Champ absent : les trois gestes sont consideres DISPONIBLES. Le defaut
   * inverse eteindrait toute la barre au premier ecran qui oublie de le
   * remplir, sans qu'aucun build ne rougisse.
   */
  bulkAvailability?: { archive: string | null; remove: string | null; edit: string | null };
  /**
   * POURQUOI LES GESTES D'ECRITURE SONT ETEINTS SUR CETTE PORTE
   * (`useScreen().raisonAcces`), ou `null` / absent quand ils sont vivants.
   *
   * LE CHASSIS NE LE CALCULE PAS, IL LE LIT. La regle (le role d'org, les
   * derogations par porte) vit dans `lib/acces.tsx`, le moteur la pose une fois
   * pour tous les gestes qu'il decide ; le chassis rend les DEUX qui restent :
   * « Ajouter » et la fleche des modeles. Deux calculs de la meme chose
   * divergeraient, et le premier symptome serait un bouton actif a cote d'un
   * bouton eteint pour la meme raison.
   *
   * ABSENT = RIEN N'EST GRISE. Le defaut inverse eteindrait l'ajout de tout
   * ecran qui ne passe pas `tools`, sans qu'aucun build ne rougisse ; et ce
   * n'est de toute facon pas ici que la securite se joue, mais dans les rules.
   */
  raisonAcces?: string | null;
  /**
   * LES LIGNES PROJETEES DE LA VUE, lues pour UNE seule chose : donner un NOM
   * a chaque fiche du lot ("Elles NOMMENT les fiches tombees"). `title` d'une
   * `ProjectedRow` n'est jamais vide, il retombe sur l'id.
   *
   * LE NOM EST LU AU CLIC ET GELE, jamais relu apres l'ecriture : les fiches
   * ecrites ont deja quitte la vue a ce moment-la, et le compte rendu
   * afficherait des identifiants exactement pour les lignes qu'il faut pouvoir
   * rattraper.
   *
   * `useScreen().rows` s'y passe tel quel : le nom existait deja, aucune
   * declaration nouvelle a ecrire cote moteur.
   */
  rows?: ProjectedRow[];
}

/*
 * LA PASTILLE DE STATUT RETIREE (chantier `fiche-suivi`, question ouverte de
 * l'atelier tranchee le 27/10, `docs/socle/fiche-suivi.md` section 2) : quand
 * le champ de la rangee 3 (`etapes`) est celui de la pastille a droite du
 * titre de la fiche, la pastille ne se rend plus. Une information, une place.
 * Le gabarit le sait (il recoit `etapes`), la vue fiche le rend : un contexte
 * pose autour de la zone 5 les relie, sans prop de plus dans le contrat des
 * vues.
 */
const PastilleDeStatutRetireeContext = createContext(false);

/** Vrai quand la rangee 3 montre deja le champ de la pastille de statut : la fiche ne la rend pas. */
export function usePastilleDeStatutRetiree(): boolean {
  return useContext(PastilleDeStatutRetireeContext);
}

export interface ScreenLayoutProps {
  /* ---- Hors gabarit : les canvas (G23) ---- */
  /**
   * CE QUE CET ECRAN REPREND AU GABARIT quand il en sort (regle G23). Absente
   * (le cas de la quasi-totalite des ecrans) : l'ecran est NATIVE, tout se rend
   * comme avant. La derogation se demande AVANT au fondateur et se DECLARE dans
   * la fiche `taki.app.json` de l'app (cle `canvas`) : cette prop la MET EN
   * OEUVRE, elle ne l'accorde pas.
   *
   * Pour une porte custom, ne pas construire cet objet a la main : le moteur le
   * sert deja resolu (`useScreen().door.canvas`), et il vaut `null` sur une
   * porte native. C'est pourquoi `null` est ACCEPTE ici au meme titre que
   * l'absence : l'ecran passe `canvas={screen.door.canvas}` tel quel, sans
   * ternaire ni etalement conditionnel. Le garde-fou de CI
   * (`scripts/check-canvas.mjs`) lit cette prop pour verifier que l'ecran qui
   * sort du gabarit est bien celui que la fiche declare : une ecriture
   * detournee le rendrait aveugle.
   */
  canvas?: ScreenCanvas | null;

  /* ---- Zone 1 : rail (identite + menu). S'arrete au menu. ---- */
  /** Nom de l'app affiche en tete du rail. */
  appName?: string;
  /**
   * Icone de l'app, rendue en tete du rail (zone 1a), a gauche du nom.
   * UNE par app, identique sur tous les ecrans, jamais remplacee (G1).
   * Icone @taki/ui, size 18 conseille.
   */
  appIcon?: ReactNode;
  /** Entrees du menu (les portes). Zone rendue seulement si non vide. */
  menuItems?: ScreenMenuItem[];
  /** Présentation des sous-entrées ; absente, le rail conserve son rendu habituel. */
  menuHierarchyStyle?: 'line';
  /** Id de l'entree active (aplat nav-selected, encre pleine). */
  activeMenuId?: string;
  /** Callback quand une entree du menu est cliquee. */
  onMenuSelect?: (id: string) => void;

  /* ---- Zone 2a : titre (OBLIGATOIRE) ---- */
  /**
   * Titre de repli de l'ecran : le h1 affiche le label de l'entree de menu
   * active (`activeMenuId`) quand elle existe, sinon cette prop.
   */
  title: string;
  /** Contenu du titre quand la surface ajoute une précision à son nom. */
  titleContent?: ReactNode;

  /* ---- Zone 2b : la recherche, centree sur l'ecran ---- */
  /**
   * Callback de saisie : sa presence AFFICHE le champ (h 2rem, icone
   * etincelle), au centre de l'en-tete. L'app filtre elle-meme son contenu
   * avec la valeur recue (`useScreen().setSearch`).
   */
  onSearchChange?: (value: string) => void;
  /**
   * Valeur controlee du champ. Optionnel : sans lui, le gabarit gere la saisie
   * en interne (le champ reste fonctionnel), tout en remontant chaque frappe a
   * `onSearchChange`. Le fournir pour un controle total (reset, valeur externe).
   */
  searchValue?: string;
  /**
   * Placeholder du champ, celui de la PORTE active pour un ecran v2
   * (`useScreen().door.searchPlaceholder`). Defaut : `DEFAULT_SEARCH_PLACEHOLDER`.
   */
  searchPlaceholder?: string;

  /* ---- Zone 2c : stats ---- */
  /**
   * Les stats de la porte active, alignees a droite. PLAFOND DE 3, tenu ici :
   * au-dela, le surplus n'est pas rendu. Sans stat, l'en-tete garde sa grille
   * (l'equivalent React du `[hidden]` du proto) : le titre ne bouge pas.
   */
  headerStats?: ScreenStat[];

  /* ---- Zone 3a : les actions de la porte ---- */
  /**
   * Les actions de la PORTE ACTIVE (`useScreen().door.actions` pour un ecran
   * v2), rendues en rangee au-dessus du contenu, 3 par porte maximum. La regle
   * "+ Ajouter" et la regle du lime unique sont tenues ici (voir
   * `ScreenAction.kind`). Le nom `quickActions` est garde tel quel pour que
   * les ecrans V1 compilent sans modification : memes declarations, nouvel
   * emplacement (le rail n'a plus d'actions).
   */
  quickActions?: ScreenAction[];
  /**
   * Les actions de la VUE active (`useScreen().viewActions`), rendues APRES
   * les actions de l'ecran, TOUJOURS en secondaire, quoi qu'elles declarent :
   * le lime unique appartient a l'ecran, jamais a une vue. C'est la seule
   * porte d'une vue vers la rangee 3a.
   */
  viewActions?: ScreenAction[];
  /**
   * LE MENU « ... » D'UN CANVAS QUI GARDE 3b (regle G23, depuis le 05/10/2026).
   * Une surface n'a ni catalogue de filtres ni tri : ce qu'elle garde de 3b,
   * c'est le menu des gestes RARES tout a droite de la rangee, la place exacte
   * du « ... » d'une fiche et du menu des modes d'une porte. Les entrees sont
   * des `ScreenAction` ordinaires (confirmation et fenetre a champ servies par
   * le chassis), plus `checked` pour une entree qui BASCULE un etat (Archives) :
   * elle se rend alors en `menuitemcheckbox`, a l'encre quand elle est active
   * (G17). Ignore sur un ecran NATIVE et sur un canvas qui ne garde pas 3b.
   * Un tableau VIDE rend le menu quand meme, qui dit alors qu'il n'a rien
   * (G16) ; `undefined` ne le rend pas.
   */
  canvasMenuActions?: CanvasMenuAction[];
  canvasActionGroups?: ReadonlyArray<NavigationDeSurface | ChoixDeSurface>;
  canvasActionOrder?: readonly string[];
  canvasMenuReplacesModes?: boolean;
  /**
   * LE TITRE QU'UNE APP CANVAS POSE EN 2a (regle G23, depuis le 05/10/2026,
   * retour 7 du fondateur : l'en-tete du canal rejoint celui du gabarit). Le
   * nom de l'app reste a gauche, toujours (le dernier repere de lieu, section
   * 9) ; ce titre le SUIT, a l'encre pleine, apres un separateur attenue
   * (« Canaux / general »). `null` ou absent : le nom de l'app seul. Ignore
   * sur un ecran NATIVE, ou 2a porte la porte active.
   */
  canvasTitle?: string | null;
  /**
   * LE SUIVI DE L'OBJET AFFICHE (chantier `chatter` du 05/10/2026, renomme et
   * redessine par le chantier `fiche-suivi` du 27/10/2026 ; contrat
   * `SuiviDuGabarit`, `fiche-contrat.ts`, doctrine `docs/socle/fiche-suivi.md`).
   * C'est le GABARIT qui le rend, pilote par cette prop : l'app n'ecrit rien.
   *
   * - AU NIVEAU FICHE : le RAIL DE SUIVI au bord droit de la zone 5 (3rem,
   *   pleine hauteur, le pendant du dock) et le VOLET qu'il ouvre (24rem, la
   *   fiche recule), dessines d'apres l'atelier du fondateur
   *   (`docs/maquettes/atelier-2610/README.md`, section 4). Le rail en tient
   *   lieu de bouton : la rangee 3 d'une fiche n'en a pas.
   * - AILLEURS (une porte, une App Canvas qui pose une fiche) : le bouton
   *   « Suivi » de la rangee 3, cote outils (3b), juste avant le groupe
   *   epingle, meme boite que « Filtres 2 », meme pastille de nombre. Il
   *   appelle `onOuvrir` (le centre de communication). Une App Canvas n'a pas
   *   a garder la rangee pour l'avoir (G23 : outil du socle, pas zone reprise).
   *
   * `compte` : le nombre d'entrees (`null` tant qu'il n'est pas lu, aucun
   * nombre affiche) ; `raison` : le rail ou le bouton reste VISIBLE et eteint
   * avec sa raison (G16). Absent ou `null` : rien n'est rendu.
   */
  suivi?: SuiviDuGabarit | null;
  /**
   * LES EN-TETES DE LA RANGEE 3 D'UNE FICHE (chantier `fiche-suivi`, contrat
   * `EtapesDuGabarit`), dans l'ordre de la porte : chacun un champ liste en
   * mode `flux` (les etapes en chevrons) ou `selection` (les etiquettes),
   * cote a cote, alignes a gauche juste apres la puce de sortie, qui se
   * partagent la place libre. Lus au niveau fiche seulement, jamais sur un
   * brouillon (la rangee garde la place de « Creer » et « Annuler »). Un
   * en-tete qui porte `remplaceLaPastille` retire la pastille de statut de la
   * fiche (`usePastilleDeStatutRetiree`). Absent, `null` ou vide : rien apres
   * la sortie, comme avant.
   */
  etapes?: readonly EtapesDuGabarit[] | null;
  /**
   * UNE ICONE DEVANT `canvasTitle` (canal prive : le cadenas du rail), prise
   * dans la liste FERMEE du socle, jamais un nom libre. Rendue par CE gabarit
   * (taille et couleur du texte du titre, `currentColor`), avant le texte.
   * `null` ou absent : aucune icone, le texte seul (le cas d'aujourd'hui).
   */
  canvasTitleIcon?: 'cadenas' | null;
  /**
   * LE PREFIXE DU TITRE D'UN CANVAS (« Archives : »), le pendant du prefixe
   * d'un mode d'une porte (`titreDuMode`), dans la meme couleur : une surface
   * qui montre ses archives le dit en 2a comme une porte native. Liste FERMEE.
   */
  canvasTitlePrefix?: 'Archives' | null;

  /* ---- Zone 3b + 4 : outils, tags, favoris (gabarit v2) ---- */
  /**
   * L'etat du moteur, passe tel quel : `tools={screen}`. Ce qui l'allume :
   * le groupe d'outils (filtre, tri, etoile) en 3b et le tiroir (tags 4a,
   * garder 4b, favoris 4c) en zone 4. Absent : ni outils ni tiroir, l'ecran
   * V1 garde exactement sa rangee d'avant.
   */
  tools?: ScreenToolset;
  /**
   * Le CATALOGUE de tri de l'ecran (les cles proposees par le popover de
   * l'outil tri). C'est une declaration d'ecran, pas un etat : le moteur ne le
   * connait pas. Sans lui, l'outil tri ne se rend pas. Ignore sans `tools`.
   */
  sortOptions?: ScreenSortOption[];
  /**
   * LECTURE V1 : les pastilles de filtre a choix unique, rendues dans la
   * rangee 3 (l'ancienne zone 4). Remplacees par `tools` des qu'un ecran
   * migre : un ecran ne passe jamais les deux.
   */
  filters?: ScreenFilter[];
  /** Id de la pastille active (V1, encre pleine). */
  activeFilterId?: string;
  /** Callback quand une pastille est cliquee (V1). */
  onFilterSelect?: (id: string) => void;

  /* ---- Zone 3c : le selecteur de vues, epingle ---- */
  /**
   * UNE SEULE REGLE, aux deux niveaux (`ViewSelector`) : moins de deux
   * entrees, pas de selecteur, il n'y a rien a choisir. Une entree grisee
   * compte, on ne cache jamais une vue.
   *
   * Les vues proposees par l'ecran (`useScreen().views`), rendues en segmented
   * control epingle a droite de la rangee 3 : une entree non satisfaite est
   * GRISEE avec sa raison au survol, jamais absente. Une vue n'est JAMAIS une
   * entree de `menuItems` : le menu designe un JEU de donnees, le selecteur
   * choisit le RENDU. Sans cette prop, la rangee 3 est celle d'un ecran sans
   * vues (aucun ecran existant ne bouge).
   *
   * PASSER LA LISTE COMPLETE, entrees `record` comprises : c'est elle qui
   * DEDUIT le niveau. Le selecteur, lui, n'affiche que le niveau `collection`
   * (le filtrage est fait ici, au rendu, jamais a la source).
   */
  views?: ViewOption[];
  /** Id de la vue active. */
  activeViewId?: ViewId;
  /** Callback quand une vue est choisie (`useScreen().setView`). */
  onViewSelect?: (id: ViewId) => void;

  /* ---- Rangee 3, partie gauche au niveau `record` ---- */
  /*
   * Ces quatre props ne se rendent QUE si la vue active est de niveau `record`
   * (niveau deduit de `views` + `activeViewId`, jamais demande). Elles sont
   * donc inertes sur un ecran sans vues, meme si une app les passe par erreur,
   * et elles n'entrent JAMAIS dans le calcul de rendu de la rangee 3 :
   * l'indicateur d'enregistrement VIT DANS la rangee, il n'existe que la ou
   * elle existe.
   */
  /**
   * Ferme la FICHE et ramene a la derniere vue collection
   * (`useScreen().openRecord(null)`). Absente : pas de puce, le selecteur
   * reste la seule sortie.
   *
   * Ce n'est PAS la sortie de l'app : celle-la vient du shell par
   * `ScreenNav.onBack` et vit sur la puce d'icone de la zone 1a.
   */
  onRecordExit?: () => void;
  /**
   * Le libelle de la puce de sortie : le nom de la vue vers laquelle on
   * revient ("Tableau", "Liste"). Cote ecran :
   * `screen.views.find((v) => v.id === screen.lastCollectionViewId)?.label`.
   * Defaut "Retour" : une puce muette n'existe pas.
   */
  recordExitLabel?: string;
  /** L'etat d'enregistrement de l'ecran (`useScreen().saveState`). */
  saveState?: SaveState;
  /** Rejoue la derniere ecriture echouee (`useScreen().retrySave`). */
  onRetrySave?: () => void;

  /* ---- Rangee 3, groupe EPINGLE au niveau `record` (3c) ---- */
  /*
   * Au niveau `record`, le groupe epingle porte le SELECTEUR DES VUES DE FICHE
   * (`ViewSelector`, nourri des entrees de niveau `record` de `views`, qui
   * ne se rend qu'a partir de deux) puis le menu « ... » des gestes SUR la fiche. Le
   * curseur « <- 1 / 5 -> » et le partage en sont partis (atelier du 26/10) ;
   * les raccourcis Alt + fleche haut et bas, qui passent d'une fiche a
   * l'autre, restent ecoutes par le moteur (`useScreen`).
   */
  /**
   * LES GESTES RARES SUR LA FICHE : le contenu du menu "..." de la rangee 3c
   * (`useScreen().recordMenuActions`). Archiver, Desarchiver, Supprimer. Ces
   * gestes sont RARES, un menu est leur place ; la rangee 3a porte ce qu'on
   * fait souvent.
   *
   * SURCHARGE, PAS CHEMIN PRINCIPAL : le chemin normal est `tools`
   * (`ScreenToolset.recordMenuActions`), qui les apporte a tout ecran deja
   * migre sans une ligne de cablage. Cette prop-ci ne sert qu'a un ecran qui
   * veut fournir SES gestes a la place de ceux du moteur ; fournie, elle gagne.
   *
   * Le nom dit ou ils ATTERRISSENT (le menu "..." de la 3c) et non ce qu'ils
   * sont : a cote de `quickActions` et `viewActions`, qui vivent tous deux dans
   * la rangee 3a, un troisieme `*Actions` sans autre precision se lirait comme
   * un troisieme groupe de boutons de cette rangee.
   *
   * Une action `disabled` reste VISIBLE avec sa raison en `title` (G16, G18) :
   * un bouton qui apparait et disparait selon l'app fabrique exactement le
   * "pourquoi je ne peux pas supprimer ici" qu'on veut eviter. "Supprimer" y
   * reste SECONDAIRE : aucun rouge dans le menu, le rouge ne vit que dans la
   * fenetre de confirmation. Une action qui porte un `confirm` voit sa fenetre
   * ouverte par le CHASSIS.
   *
   * Vide ou absente des deux cotes : le menu s'ouvre quand meme et DIT pourquoi
   * il est vide.
   */
  recordMenuActions?: ScreenAction[];
  /**
   * LES GESTES VISIBLES D'UNE FICHE, a cote de la puce de sortie.
   *
   * POURQUOI ILS EXISTENT, alors que `recordMenuActions` range deja les gestes
   * d'une fiche : ce menu est celui des gestes RARES, et il se merite (il faut
   * penser a l'ouvrir). Une porte dont le geste PRINCIPAL porte sur la fiche
   * n'avait aucun endroit pour le montrer, et le fondateur l'a dit en cherchant
   * deux fois de suite comment ouvrir une organisation depuis sa fiche
   * (16/09/2026). Un geste qu'on cherche n'est pas au bon endroit.
   *
   * CE N'EST PAS LA RANGEE 3a : elle appartient au niveau COLLECTION et ne se
   * rend pas ici. C'est la partie gauche de la rangee au niveau `record`, celle
   * qui porte deja la sortie de fiche et l'indicateur d'enregistrement.
   *
   * SANS PLAFOND (decision du fondateur, 28/10/2026 : « on n'a pas de limite
   * pour le moment »). Tous les gestes passes ici se rendent, a gauche, apres
   * la sortie ; le gabarit ne bascule plus les suivants dans le menu. Ce qui
   * vaut d'etre RANGE (un geste rare) reste a `recordMenuActions`, ou l'on
   * demande a le mettre. Quand la rangee ne tient pas dans la largeur, elle
   * passe a la ligne (voir le rendu) : aucun geste ne se cache.
   *
   * `variant: 'primary'` EST PERMIS, et il est fait pour le geste qui est la
   * raison d'etre de la porte (ouvrir l'espace d'une organisation, par
   * exemple). G14 tient quand meme : le chassis le redescend en secondaire des
   * qu'un brouillon est ouvert, puisque « Creer » porte alors le seul lime de
   * l'ecran.
   */
  recordActions?: ScreenAction[];

  /* ---- Zone 5 : la ref du conteneur qui defile ---- */
  /**
   * Ref posee sur la zone 5 (le seul conteneur de contenu qui defile). Le
   * gabarit la PRETE : le `ViewHost` s'en sert pour la memoire de defilement,
   * et les vues la recoivent dans `ViewProps.scrollRef` (une vue en
   * `scroll: 'self'` en a besoin pour se mesurer). A brancher sur
   * `useScreen().scrollRef`.
   */
  scrollRef?: Ref<HTMLElement>;

  /* ---- Zone 5 : contenu + etats standard ---- */
  /**
   * Etat chargement : remplace le contenu par un skeleton. Temporisation
   * integree (useLoadingState) : le skeleton n'apparait qu'apres un court
   * delai (donnee rapide = aucun flash), reste affiche une duree minimale
   * (pas de clignotement), et un chargement trop long (10 s) bascule vers
   * l'etat erreur standard (avec bouton reessayer si `onRetry`).
   */
  loading?: boolean;
  /**
   * @deprecated Ignore : l'etat chargement est un skeleton (prop `skeleton`),
   * plus un bloc de texte. Conserve pour ne pas casser les ecrans existants.
   */
  loadingText?: string;
  /**
   * Skeleton de zone 5 sur mesure, compose depuis les primitives Skeleton*
   * de @taki/ui, fidele au contenu reel (zero saut de mise en page).
   * Defaut : <SkeletonListRows /> (le pattern de liste de reference).
   */
  skeleton?: ReactNode;
  /** Message d'erreur : remplace le contenu (prioritaire sur tout). */
  error?: string | null;
  /**
   * Titre de l'etat erreur. Defaut "Something went wrong" ; pour l'erreur de
   * chargement trop long, defaut "Still loading, something may be wrong".
   */
  errorTitle?: string;
  /** Si fourni, l'etat erreur affiche un bouton reessayer. */
  onRetry?: () => void;
  /** Libelle du bouton reessayer. Defaut "Retry". */
  retryLabel?: string;
  /** Etat vide : remplace le contenu (apres error et loading). */
  empty?: boolean;
  /** Titre de l'etat vide. Defaut "Nothing here yet". */
  emptyTitle?: string;
  /** Phrase d'aide sous le titre de l'etat vide (ex. quoi faire pour demarrer). */
  emptyHint?: string;
  /** Contenu de l'ecran (zone 5). Ignore quand un etat standard s'affiche. */
  children?: ReactNode;
}

/**
 * Entree du menu (zone 1b). Actif = aplat --nav-selected + encre --nav-ink +
 * aria-current ; le compteur reste en mono, dans l'encre attenuee du rail.
 * Le rail et le dock partagent une navigation neutre ; l'accent client reste
 * disponible pour les actions et les tags du contenu.
 */
function RailItem({
  icon,
  label,
  count,
  brouillon = false,
  active = false,
  onClick,
  niveau,
  deplie,
  onBasculer,
  onRenommer,
  hierarchy,
  lastChild = false,
}: {
  icon?: ReactNode;
  label: string;
  count?: number | string;
  brouillon?: boolean;
  active?: boolean;
  onClick?: () => void;
  niveau?: number;
  deplie?: boolean;
  onBasculer?: () => void;
  onRenommer?: (label: string) => void;
  hierarchy?: ScreenLayoutProps['menuHierarchyStyle'];
  lastChild?: boolean;
}): JSX.Element {
  const [edition, setEdition] = useState<string | null>(null);
  const decalage = Math.min(Math.max(0, Math.floor(niveau ?? 0)), NIVEAU_DE_RAIL_MAX);
  /* UN ARBRE (une entree qui declare `niveau`) reserve la gouttiere du chevron
     a toutes ses entrees, feuilles comprises, et decale d'un cran par niveau :
     les libelles d'un meme niveau restent alignes. Sans `niveau` : la porte
     d'une app native, au pixel pres. */
  const childStyle = decalage > 0 ? hierarchy : undefined;
  const extraDepth = Math.max(0, decalage - 1) * 0.875;
  const paddingLeft = niveau != null ? `${1.5 + decalage * 0.875}rem` : undefined;
  const itemStyle = childStyle === 'line'
    ? { marginLeft: `${2 + extraDepth}rem`, width: `calc(100% - ${2 + extraDepth}rem)`, paddingLeft: '0.625rem' }
    : paddingLeft != null ? { paddingLeft } : undefined;
  if (edition != null && onRenommer != null) {
    const valider = (): void => {
      const propre = edition.trim();
      setEdition(null);
      if (propre !== '' && propre !== label) onRenommer(propre);
    };
    return (
      <input
        autoFocus
        aria-label={`Renommer ${label}`}
        value={edition}
        onChange={(event) => setEdition(event.target.value)}
        onBlur={valider}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            valider();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setEdition(null);
          }
        }}
        style={itemStyle}
        className="h-9 w-full shrink-0 rounded-[0.625rem] border border-nav-focus bg-nav-rail px-2.5 font-sans text-sm font-medium text-nav-ink outline-none"
      />
    );
  }
  return (
    <div className="group/rail relative flex shrink-0 items-center max-sm:w-auto">
      {childStyle === 'line' && <>
        {!lastChild && <span aria-hidden className="pointer-events-none absolute z-[1] -top-1 -bottom-1 w-px bg-nav-border"
          style={{ left: `${1 + extraDepth}rem` }} />}
        <span aria-hidden className="pointer-events-none absolute z-[1] -top-1 bottom-1/2 w-4 rounded-bl-sm border-b border-l border-nav-border"
          style={{ left: `${1 + extraDepth}rem` }} />
      </>}
      {deplie != null && (
        <button
          type="button"
          aria-label={`${deplie ? 'Replier' : 'Déplier'} ${label}`}
          aria-expanded={deplie}
          onClick={onBasculer}
          style={{ left: `${0.25 + decalage * 0.875}rem` }}
          className="absolute top-1/2 z-[1] flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-[0.375rem] text-nav-subtle hover:bg-nav-hover hover:text-nav-ink active:bg-nav-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-focus"
        >
          {deplie ? <IconChevronDown size={11} /> : <IconChevronRight size={11} />}
        </button>
      )}
      <button
        type="button"
        onClick={onClick}
        onDoubleClick={onRenommer != null ? () => setEdition(label) : undefined}
        aria-current={active ? 'true' : undefined}
        title={onRenommer != null ? `${label} (double-clic : renommer)` : undefined}
        style={itemStyle}
        className={cn(
          'flex h-9 w-full shrink-0 items-center gap-2.5 rounded-[0.625rem] px-2.5 text-left font-sans text-sm font-medium transition-colors max-sm:w-auto',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-focus',
          'active:bg-nav-press active:text-nav-ink',
          active ? 'bg-nav-selected font-semibold text-nav-ink hover:bg-nav-selected-hover' : 'text-nav-text hover:bg-nav-hover hover:text-nav-ink',
          childStyle !== undefined && 'relative h-8 text-[0.8125rem] font-normal',
          childStyle !== undefined && active && 'font-semibold',
          childStyle === 'line' && 'bg-transparent hover:bg-nav-hover',
        )}
      >
        {childStyle === undefined && icon != null && (
          <span className="flex shrink-0 items-center justify-center">{icon}</span>
        )}
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {brouillon && !active && (
          <span role="img" aria-label="Brouillon" title="Brouillon" className="flex shrink-0 items-center text-nav-subtle"><IconPen1 size={11} /></span>
        )}
        {count != null && (
          <span className="shrink-0 font-mono text-[0.6875rem] text-nav-subtle">{count}</span>
        )}
      </button>
    </div>
  );
}

/** La profondeur au-dela de laquelle une sous-entree du rail ne se decale plus. */
export const NIVEAU_DE_RAIL_MAX = 4;

/**
 * L'EN-TETE D'UN GROUPE du rail (`ScreenMenuItem.groupe`) : le nom du groupe en
 * petites capitales, et le « + » qui cree une entree de ce groupe. Le « + »
 * est un carre sans mot, secondaire (meme famille que l'ajout en tete d'une
 * carte liee, G5) ; sans `onAjouter` il porte sa raison et reste inerte (G16).
 */
function RailGroupHeading({
  groupe,
  first,
}: {
  groupe: NonNullable<ScreenMenuItem['groupe']>;
  first: boolean;
}): JSX.Element {
  const libelle = groupe.ajouterLabel ?? `Ajouter : ${groupe.label}`;
  const inerte = groupe.onAjouter == null;
  return (
    <div className={cn('flex h-7 shrink-0 items-center gap-1 pl-2.5 pr-1 max-sm:hidden', !first && 'mt-2')}>
      <span className="min-w-0 flex-1 truncate text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-nav-subtle">
        {groupe.label}
      </span>
      {(groupe.onAjouter != null || groupe.ajouterRaison != null) && (
        <button
          type="button"
          aria-label={libelle}
          title={inerte ? groupe.ajouterRaison : libelle}
          aria-disabled={inerte || undefined}
          onClick={inerte ? undefined : groupe.onAjouter}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-[0.375rem] text-nav-subtle transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-focus',
            inerte ? 'cursor-not-allowed opacity-60' : 'hover:bg-nav-hover hover:text-nav-ink active:bg-nav-press',
          )}
        >
          <IconPlus size={12} />
        </button>
      )}
    </div>
  );
}

/**
 * L'INDICATEUR D'ENREGISTREMENT de la rangee 3 (niveau `record` uniquement).
 *
 * Quatre etats, et le premier ne rend RIEN : avant la premiere ecriture, la
 * rangee ne porte aucun encombrement. Le rond tournant est ici legitime au
 * regard de la doctrine des etats de chargement (tokens.css) : une ecriture est
 * une ACTION PONCTUELLE, pas un chargement de contenu (qui, lui, prend un
 * squelette). L'echec, lui, n'est pas qu'un texte : il porte sa relance, parce
 * qu'une saisie perdue sans explication est le pire des deux.
 */
/**
 * UNE PUCE DU CHEMIN (`tools.chemin`) : la coque de la puce de sortie, pour
 * qu'on lise les deux retours comme une meme famille de gestes. `retour` pose
 * la fleche de sortie devant le mot ; sans lui, c'est l'acces a la fiche
 * parente, marque de la fleche qui ouvre une fiche. Le mot tronque sur un
 * petit ecran, la puce reste entiere au toucher.
 */
function PuceDuChemin({
  libelle,
  onClick,
  retour = false,
  aria,
  className,
}: {
  libelle: string;
  onClick: () => void;
  retour?: boolean;
  aria?: string;
  className?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={retour ? `Retour à ${libelle}` : libelle}
      aria-label={aria ?? (retour ? `Revenir à ${libelle}` : libelle)}
      className={cn(
        'flex h-8 min-w-0 max-w-[18rem] shrink items-center gap-2 rounded-[0.625rem] px-3',
        'font-sans text-xs font-semibold transition-colors',
        retour
          ? 'border border-border bg-surface text-ink-700 hover:bg-surface-1 hover:text-ink-900'
          : 'text-ink-500 hover:bg-surface-1 hover:text-ink-900',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
        className,
      )}
    >
      {retour && <IconLeftBack size={12} className="shrink-0" />}
      <span className="truncate">{libelle}</span>
      {!retour && <IconOpenArrow size={12} className="shrink-0" />}
    </button>
  );
}

/**
 * L'ACCES A LA FICHE PARENTE (`tools.chemin.parent`) : un bouton SECONDAIRE,
 * l'icone de la vue Fiche devant « Ouvrir fiche » (retour du fondateur du
 * 13/10/2026, qui remplace le lien « Fiche : <projet> → »). Le mot ne nomme
 * pas le parent, le titre de la zone 2 le fait deja ; le survol le redit.
 */
function BoutonFicheParente({
  parent,
  className,
}: {
  parent: NonNullable<CheminDePorte['parent']>;
  className?: string;
}): JSX.Element {
  const dit = parent.titre === '' ? 'Ouvrir la fiche' : `Ouvrir la fiche ${parent.titre}`;
  return (
    <Button
      variant="secondary"
      size="sm"
      className={cn(BULK_BUTTON, className)}
      title={dit}
      aria-label={dit}
      onClick={parent.ouvrir}
    >
      <IconFileBookmark size={14} className="shrink-0" />
      Ouvrir fiche
    </Button>
  );
}

function SaveIndicator({
  state,
  onRetry,
  reason,
  className,
}: {
  state: SaveState;
  onRetry?: () => void;
  /** Pose sur la premiere boite rendue (le placement dans la rangee : `ml-auto`). */
  className?: string;
  /**
   * LA RAISON D'UN ECHEC QUI N'EN EST PAS UN : en lecture seule
   * (`tools.raisonAcces`), le moteur refuse l'ecriture lui-meme et passe ici.
   * Elle REMPLACE alors "Echec de l'enregistrement", qui laisserait croire a
   * une panne, et elle RETIRE "Reessayer", qui rejouerait le meme refus.
   * Absente : l'echec ordinaire, avec sa relance.
   */
  reason?: string | null;
}): JSX.Element | null {
  if (state.mode === 'idle') return null;

  if (state.mode === 'saving') {
    return (
      <span role="status" className={cn('flex h-8 shrink-0 items-center gap-2 text-xs text-ink-400', className)}>
        <span className="taki-spinner" />
        Enregistrement...
      </span>
    );
  }

  if (state.mode === 'saved') {
    // "jamais un trou, jamais undefined a l'ecran" : sans heure, on dit juste
    // que c'est enregistre.
    const time =
      state.at != null
        ? state.at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
        : null;
    return (
      <span role="status" className={cn('flex h-8 shrink-0 items-center gap-1.5 text-xs text-ink-400', className)}>
        <IconCheck size={13} />
        {time != null ? `Enregistré à ${time}` : 'Enregistré'}
      </span>
    );
  }

  /* UN REFUS D'ACCES, PAS UNE PANNE. La phrase du refus est une phrase entiere
     et non deux mots : la pastille se laisse alors RETRECIR et coupe son texte
     (l'infobulle le rend en entier), au lieu de pousser le reste de la rangee
     hors de l'ecran. L'echec ordinaire, lui, garde exactement sa boite. */
  const refuse = reason != null && reason !== '';
  return (
    <>
      <span
        role="alert"
        {...(refuse ? { title: reason } : {})}
        className={cn(
          'flex h-8 items-center rounded-pill border border-danger-border bg-danger-tint px-3.5 font-sans text-xs font-semibold text-danger',
          refuse ? 'min-w-0 shrink' : 'shrink-0',
          className,
        )}
      >
        <span className="truncate">{refuse ? reason : "Échec de l'enregistrement"}</span>
      </span>
      {onRetry != null && !refuse && (
        <Button variant="secondary" size="sm" className="shrink-0" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </>
  );
}

/**
 * Bloc d'etat standard centre dans la zone 5 (chargement, vide, erreur).
 *
 * Exporte pour le seul `ViewHost` (interne au socle), qui rend les MEMES etats
 * quand la zone 5 est pilotee par une vue : deux implementations divergeraient
 * au premier ajustement. Volontairement absent du barrel `@taki/ui` : une app
 * ne rend jamais un etat standard elle-meme, elle passe les props du gabarit.
 */
export function StateBlock({
  title,
  hint,
  action,
  role,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  /** `alert` pour l'erreur, `status` pour le chargement : annonce aux lecteurs d'ecran. */
  role?: 'alert' | 'status';
}): JSX.Element {
  return (
    <div
      role={role}
      className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center"
    >
      <p className="text-[0.9375rem] font-semibold text-ink-900">{title}</p>
      {hint != null && <p className="max-w-sm text-[0.8125rem] text-ink-500">{hint}</p>}
      {action != null && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * Un bouton du groupe d'outils (3b) : meme boite que les entrees du selecteur
 * de vues (h 1.75rem sur 2rem, actif encre + ombre de carte). Un outil dont le
 * reglage n'est pas au repos porte un POINT (0.3125rem) : un filtre replie et
 * muet est un filtre qu'on oublie.
 *
 * CE POINT EST EN LIME (`--accent-dot`), demande du fondateur du 07/08/2026, et
 * ce n'est PAS une entorse a G17 : `--accent-dot` est le jeton des PETITS
 * MARQUEURS, pas le lime du bouton d'ajout ; a 5px l'encre se colle au glyphe
 * qu'elle surmonte et les deux formes noires se lisent comme une bavure ; et
 * surtout un point n'est pas un etat ACTIF au sens de la regle, aucun geste ne
 * l'atteint, il ne se clique pas. Il RAPPELLE qu'un reglage est pose ailleurs,
 * dans le popover replie.
 *
 * A NE PAS "REPARER" : le point qui marque une entree ACTIVE a l'interieur d'un
 * popover (`ToolPopover.tsx`) reste a l'ENCRE et ne s'aligne pas sur celui-ci.
 * Celui-la designe un etat qu'on CHOISIT, exactement ce que G17 vise. Les deux
 * points ne disent pas la meme chose : l'un rappelle, l'autre designe.
 */
/**
 * LA BARRE DE RECHERCHE DU SOCLE (2b), EN COMPOSANT.
 *
 * ELLE A ETE SORTIE DU GABARIT POUR QU'UNE SECONDE SURFACE LA MONTE : la
 * FENETRE DE CHOIX d'une fiche a lier (`views/core/LinkedPicker.tsx`) cherche
 * dans la meme fenetre cliente, avec la meme boite, la meme etincelle, le meme
 * contour au focus. Recopier ces quinze classes la-bas aurait donne deux champs
 * de recherche qui se ressemblent jusqu'au jour ou l'un des deux est retouche.
 *
 * L'ICONE EST LA LOUPE : ce champ filtre les fiches localement.
 *
 * `horsService` PORTE LA RAISON, ET C'EST LA COQUE QUI LA PORTE, pas le champ :
 * le champ est `inert`, il ne recoit donc aucun evenement de souris et son
 * propre `title` ne s'afficherait jamais. Meme procede que les boutons eteints
 * de la barre de lot. `inert` plutot que `disabled` : un champ `disabled` se
 * grise par les styles du navigateur PAR-DESSUS la hachure, et deux zones
 * eteintes du meme ecran s'eteindraient alors de deux facons differentes.
 *
 * NON CONTROLEE NULLE PART : la valeur vient toujours de l'appelant, qui tient
 * deja l'etat de sa recherche (le gabarit a son repli interne, la fenetre de
 * choix son `useState`).
 */
export function SearchField({
  value,
  onChange,
  placeholder = DEFAULT_SEARCH_PLACEHOLDER,
  className,
  horsService,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Le mot de la porte, ou celui du socle. Il sert AUSSI de nom accessible. */
  placeholder?: string;
  /** Le placement, laisse a l'appelant : la grille de la zone 2, ou une rangee. */
  className?: string;
  /** Le champ est hors service, et voici POURQUOI (G16). Absent : il repond. */
  horsService?: string;
}): JSX.Element {
  const eteint = horsService !== undefined;
  // Les declarations d'instance plus anciennes peuvent garder l'ancien
  // suffixe dans Firestore : la recherche n'annonce plus un assistant ici.
  const visiblePlaceholder = placeholder.replace(/\s+ou demander [aà] Lia(?:\.{3}|…)?/iu, '...');
  return (
    <div
      title={horsService}
      className={cn(
        'flex h-8 w-full min-w-0 items-center rounded-sm border border-border bg-surface-1',
        'transition-colors',
        eteint
          ? // Le survol n'allume plus le contour : un champ hors service
            // qui repond au survol se relit comme un champ qui marche.
            cn('cursor-not-allowed', DRAWER_HATCH)
          : cn(
              'hover:border-border-strong',
              'focus-within:border-accent-border focus-within:shadow-none focus-within:ring-1 focus-within:ring-inset focus-within:ring-accent-border',
            ),
        className,
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none flex shrink-0 items-center pl-3 text-ink-300"
      >
        <IconSearch size={15} />
      </span>
      <input
        type="text"
        inert={eteint}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={visiblePlaceholder}
        aria-label={visiblePlaceholder}
        className={cn(
          'h-full w-full min-w-0 bg-transparent pl-2 pr-3.5 font-sans text-[0.8125rem] text-ink-900',
          'outline-none placeholder:text-ink-300 focus:outline-none focus-visible:outline-none',
        )}
      />
    </div>
  );
}

/**
 * LE BOUTON D'UN OUTIL DE LA RANGEE 3b.
 *
 * EXPORTE DEPUIS LE LOT « fenetre de choix » : la fenetre qui propose les
 * fiches a lier monte le MEME outil de filtre que l'ecran, donc le meme bouton
 * dans la meme coque. Un second bouton maison aurait fini par ne pas repondre
 * comme celui-ci a la meme touche, ni porter le meme point d'etat.
 */
export function ToolButton({
  icon,
  label,
  marked = false,
  expanded,
  controls,
  haspopup = false,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  marked?: boolean;
  expanded: boolean;
  controls?: string;
  haspopup?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={expanded}
      {...(haspopup ? { 'aria-haspopup': 'listbox' as const } : {})}
      {...(controls != null ? { 'aria-controls': controls } : {})}
      onClick={onClick}
      className={cn(
        'relative flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-border bg-surface transition-colors',
        'focus-visible:border-accent-border focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
        expanded ? 'border-border-strong bg-surface-1 text-ink-900' : 'text-ink-500 hover:border-border-strong hover:bg-surface-1 hover:text-ink-900',
      )}
    >
      <span aria-hidden className="pointer-events-none flex">{icon}</span>
      {marked && (
        <span
          aria-hidden
          className="absolute right-0.5 top-0.5 h-[0.3125rem] w-[0.3125rem] rounded-pill bg-accent-dot"
        />
      )}
    </button>
  );
}

/**
 * LE BOUTON « SUIVI » DE LA RANGEE 3 (prop `suivi`, HORS FICHE : une porte,
 * une App Canvas qui pose une fiche ; sur une fiche, le rail de suivi en tient
 * lieu). La boite du bouton « Filtres » (secondaire, h 2rem, icone + mot +
 * pastille de nombre), pour qu'un outil du socle sur l'objet ressemble aux
 * outils du socle sur le jeu. Les deux bulles sont le glyphe du centre de
 * communication (`IconCentre` du rail du shell) : ce bouton y mene, il en
 * porte le signe.
 *
 * ETEINT PAR `aria-disabled`, JAMAIS `disabled` : un bouton `disabled` ne
 * recoit aucun evenement de souris et son `title` ne s'affiche jamais ; la
 * raison doit se lire (G16).
 */
function BoutonSuivi({ suivi }: { suivi: SuiviDuGabarit }): JSX.Element {
  const eteint = suivi.raison != null;
  const title = eteint ? `${suivi.label} : ${suivi.raison}` : suivi.label;
  return (
    <Button
      variant="secondary"
      size="sm"
      title={title}
      aria-label={title}
      aria-disabled={eteint || undefined}
      data-testid="bouton-suivi"
      onClick={eteint ? undefined : suivi.onOuvrir}
      className={cn(
        'shrink-0 focus-visible:border-accent-border focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
        eteint && 'cursor-default text-ink-300 hover:bg-surface hover:text-ink-300',
      )}
    >
      <IconChatTwoBubblesOval size={14} className="pointer-events-none" />
      Suivi
      {suivi.compte != null && suivi.compte > 0 && (
        <span className="pointer-events-none flex h-4 min-w-4 items-center justify-center rounded-pill bg-surface-3 px-1 text-[0.625rem] font-semibold tabular-nums text-ink-700">
          {suivi.compte}
        </span>
      )}
    </Button>
  );
}

/**
 * LE TITRE DE L'ECRAN DANS UN MODE : « Archives : Journal », « Modeles :
 * Journal », et le titre nu hors de tout mode.
 *
 * C'EST L'ELEMENT VISUEL DU MODE (decision du fondateur du 28/09/2026). Un mode
 * ne montre pas une TRANCHE du jeu (le tiroir s'en charge), il montre un AUTRE
 * jeu : c'est la zone 2 qui dit QUEL JEU est a l'ecran, donc c'est elle qui le
 * nomme. Le mode n'a plus de tag en zone 4.
 *
 * `arch` GAGNE si les deux arrivaient vrais : meme filet que l'adresse
 * (`readModesParams`), les deux modes etant exclusifs par l'etat d'ecran.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est du TEXTE VU par l'utilisateur, un
 * test la tient sans React ni DOM.
 */
export function titreDuMode(
  titre: string,
  modes: { archivesMode: boolean; modelesMode: boolean },
): string {
  const prefixe = prefixeDuMode(modes);
  return prefixe == null ? titre : `${prefixe} : ${titre}`;
}

/** Le nom du mode allume (« Archives », « Modeles »), `null` hors mode. */
export function prefixeDuMode(modes: {
  archivesMode: boolean;
  modelesMode: boolean;
}): string | null {
  if (modes.archivesMode) return 'Archives';
  if (modes.modelesMode) return 'Modèles';
  return null;
}

/**
 * LE LIBELLE DU BOUTON QUI REMPLACE « AJOUTER » DANS UN MODE : « Quitter
 * Archives », « Quitter Modeles », `null` hors mode.
 *
 * Decision du fondateur du 28/09/2026 : dans un mode, le geste principal de
 * l'ecran n'est plus d'ajouter, c'est d'en SORTIR. Le bouton prend donc la
 * place d'« Ajouter », au meme endroit, et il nomme le mode qu'il quitte.
 */
export function libelleDeSortieDeMode(modes: {
  archivesMode: boolean;
  modelesMode: boolean;
}): string | null {
  const prefixe = prefixeDuMode(modes);
  return prefixe == null ? null : `Quitter ${prefixe}`;
}

/**
 * LE LIBELLE D'UNE ENTREE DE MODE du popover de filtre (`title` ET
 * `aria-label` : G16, un controle sans texte porte sa raison).
 *
 * LES DEUX ENTREES ONT LA MEME ANATOMIE, DONC LE MEME CALCUL. Trois etats, et
 * deux des trois phrases sont litteralement les memes d'un mode a l'autre :
 * « le mode n'est pas branche sur cet ecran » et « quitter le mode ». Les
 * recopier une seconde fois pour l'entree Modeles aurait donne deux formulations
 * du meme fait le jour ou l'une est retouchee.
 *
 * `branche` EST LA PRESENCE DU CALLBACK, ET C'EST LE SEUL GRISAGE. Un ecran qui
 * ne branche pas le mode ne fait pas disparaitre l'entree : elle reste, elle
 * DIT ce qui manque, et elle le dit a qui la cherche (G16). Ce que la porte
 * n'a pas CONFIGURE, en revanche, ne se rend pas du tout : cette decision-la
 * se prend au-dessus, pas ici (voir `FilterPopover`).
 *
 * FONCTION PURE ET EXPORTEE EXPRES, meme raison que `titreDuMode`.
 */
export function libelleEntreeDeMode(entree: {
  /** Le nom du mode, en tete de phrase : « Archives », « Modeles ». */
  nom: string;
  /** Ce que le mode montre, quand il est eteint et branche : « voir ... ». */
  cible: string;
  /** Le callback de bascule existe-t-il sur cet ecran. */
  branche: boolean;
  /** Le mode est-il allume. */
  actif: boolean;
}): string {
  if (!entree.branche) return `${entree.nom} : le mode n'est pas branché sur cet écran`;
  return `${entree.nom} : ${entree.actif ? 'quitter le mode' : entree.cible}`;
}

/**
 * LE POPOVER DE L'OUTIL FILTRE (3b) : le catalogue de la porte, et lui seul.
 *
 * LES MODES EN SONT SORTIS le 28/09/2026 (chantier `modes-menu-trois-points`).
 * Le fondateur : « la logique n'est pas la bonne ». Un mode ne tranche pas le
 * jeu, il en change : il n'avait rien a faire parmi les tranches, et une fois
 * dedans les filtres devenaient inertes. Les deux entrees vivent desormais dans
 * le menu « ... » tout a droite de la rangee 3b (`ModesMenu`), et dans un mode
 * ce catalogue marche exactement comme ailleurs.
 *
 * Il sert aussi a la FENETRE DE CHOIX d'une fiche a lier
 * (`views/core/LinkedPicker.tsx`), qui reprend ce meme catalogue pour trancher
 * ses candidates, avec des cases a cocher (`selectionIndicator="check"`).
 */
export function FilterPopover({
  id,
  items,
  onPick,
  clearDisabled,
  onClear,
  selectionIndicator = 'dot',
}: {
  id: string;
  items: ToolPopoverItem[];
  onPick: (id: string) => void;
  clearDisabled: boolean;
  onClear: () => void;
  /** Repere visuel des choix actifs. Le menu compact du gabarit garde son point. */
  selectionIndicator?: 'dot' | 'check';
}): JSX.Element {
  return (
    <div
      id={id}
      role="listbox"
      aria-label="Filtrer"
      aria-multiselectable="true"
      className={cn(
        'absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex w-72 max-w-[calc(100vw-2rem)] flex-col overflow-hidden',
        'rounded-md border border-border bg-surface shadow-pop',
      )}
    >
      <p className="shrink-0 border-b border-border-soft px-3 py-2 font-sans text-xs font-semibold text-ink-900">
        Filtres
      </p>
      <div className="max-h-[min(24rem,calc(100vh-8rem))] overflow-y-auto p-1">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="option"
          aria-selected={item.active}
          onClick={() => onPick(item.id)}
          className={cn(
            'flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left',
            'font-sans text-[0.8125rem] font-medium text-ink-700 transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
            item.active && 'font-semibold text-ink-900',
            'cursor-pointer hover:bg-surface-1 hover:text-ink-900',
          )}
        >
          {selectionIndicator === 'check' ? (
            <span
              aria-hidden
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-xs border',
                item.active
                  ? 'border-ink-900 bg-ink-900 text-surface'
                  : 'border-border-strong bg-surface text-transparent',
              )}
            >
              <IconCheck size={10} />
            </span>
          ) : (
            <span
              aria-hidden
              className={cn(
                'h-1.5 w-1.5 shrink-0 rounded-pill bg-ink-900',
                item.active ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.detail != null && (
            <span className="shrink-0 pl-3 font-mono text-[0.6875rem] font-medium text-ink-400">
              {item.detail}
            </span>
          )}
        </button>
      ))}
      </div>
      <div className="shrink-0 border-t border-border-soft p-1">
        <button
          type="button"
          aria-disabled={clearDisabled || undefined}
          onClick={clearDisabled ? undefined : onClear}
          className={cn(
            'flex h-8 w-full items-center rounded-sm px-2 text-left font-sans text-[0.8125rem] font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
            clearDisabled ? 'cursor-default text-ink-300' : 'cursor-pointer text-ink-500 hover:bg-surface-1 hover:text-ink-900',
          )}
        >
          Réinitialiser
        </button>
      </div>
    </div>
  );
}

/**
 * LE MENU « ... » DE LA RANGEE 3b : les deux modes du socle, Modeles puis
 * Archives (decision du fondateur du 28/09/2026).
 *
 * MEME ICONE, MEME BOUTON, MEME PANNEAU que le menu des gestes rares de la
 * fiche (`RecordNavGroup`) : le fondateur l'a demande « meme icone et style que
 * dans la fiche ». Le glyphe `IconFullSpaced` dit « et le reste » : ce qui ne
 * se fait pas tous les jours, et changer de jeu est de cet ordre.
 *
 * `role="menu"` et des `menuitemcheckbox` : on BASCULE un etat de l'ecran,
 * `aria-checked` dit lequel est allume. Allumee, une entree prend l'aplat encre
 * et la croix de sortie (anatomie reprise telle quelle de l'ancien popover) :
 * c'est la seconde sortie du mode, apres « Quitter » en 3a.
 *
 * « ARCHIVES » SE REND PARTOUT, « MODELES » SEULEMENT LA OU ILS SONT CONFIGURES,
 * et la frontiere n'a pas bouge : toute fiche s'archive, donc la question « ou
 * sont les archives d'ici ? » a une reponse sur toutes les portes (G20), quitte
 * a ce que l'entree soit grisee le temps qu'un ecran branche le mode (G16). Les
 * modeles, eux, sont un accord porte par porte dont le defaut est non : une
 * entree grise a vie se lirait comme une panne (voir `raisonDesModeles`).
 *
 * « NOUVEAU MODELE », EN MODE MODELES SEULEMENT. Dans ce mode, « Ajouter » est
 * devenu « Quitter Modeles » : sans cette entree, on ne saurait plus creer un
 * modele vierge (enregistrer une fiche comme modele, depuis son propre menu,
 * resterait le seul chemin). Elle ouvre la meme fiche vide qu'« Ajouter »
 * ouvrait (`onNewModele`, le geste resolu par le moteur).
 */
export function ModesMenu({
  id,
  archivesMode,
  onArchivesToggle,
  modelesMode,
  showModeles,
  onModelesToggle,
  onNewModele,
  newModeleReason,
}: {
  id: string;
  archivesMode: boolean;
  /** Absent : l'entree se rend ETEINTE avec sa raison, jamais absente (G16). */
  onArchivesToggle?: (on: boolean) => void;
  modelesMode: boolean;
  /** Les modeles sont-ils CONFIGURES sur cette porte. Faux : pas d'entree. */
  showModeles: boolean;
  onModelesToggle?: (on: boolean) => void;
  /** Creer un modele vierge (mode Modeles). Absent : pas d'entree. */
  onNewModele?: () => void;
  /** Raison qui eteint « Nouveau modele » (lecture seule...). */
  newModeleReason?: string | null;
}): JSX.Element {
  const archivesLabel = libelleEntreeDeMode({
    nom: 'Archives',
    cible: 'voir les fiches archivées',
    branche: onArchivesToggle != null,
    actif: archivesMode,
  });
  const modelesLabel = libelleEntreeDeMode({
    nom: 'Modèles',
    cible: 'voir les modèles de la porte',
    branche: onModelesToggle != null,
    actif: modelesMode,
  });
  const entree = (
    key: string,
    label: string,
    title: string,
    icon: JSX.Element,
    actif: boolean,
    toggle: ((on: boolean) => void) | undefined,
  ): JSX.Element => (
    <button
      key={key}
      type="button"
      role="menuitemcheckbox"
      aria-checked={actif}
      aria-disabled={toggle == null || undefined}
      title={title}
      aria-label={title}
      onClick={toggle == null ? undefined : () => toggle(!actif)}
      className={cn(
        'flex h-8 w-full shrink-0 items-center gap-2 rounded-sm px-2 text-left',
        'font-sans text-[0.8125rem] font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
        toggle == null
          ? 'cursor-default text-ink-300'
          : actif
            ? 'cursor-pointer bg-ink-900 font-semibold text-surface'
            : 'cursor-pointer text-ink-700 hover:bg-surface-1 hover:text-ink-900',
      )}
    >
      <span aria-hidden className="pointer-events-none flex shrink-0">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {actif && (
        /* LA CROIX DE SORTIE : sur un aplat encre, rien ne dit qu'un second
           clic ressort. Decorative, c'est l'entree entiere qui bascule. */
        <span aria-hidden className="flex shrink-0">
          <IconClose size={10} />
        </span>
      )}
    </button>
  );
  const newOff = newModeleReason != null;
  const newLabel = newOff ? `Nouveau modèle : ${newModeleReason}` : 'Nouveau modèle';
  return (
    <div
      id={id}
      role="menu"
      aria-label="Autres vues"
      className={cn(
        'absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex min-w-48 flex-col p-1',
        'rounded-md border border-border bg-surface shadow-pop',
      )}
    >
      {/* UNE PILE DE FEUILLES pour les modeles (set Streamline, le fondateur
          n'a pas dessine de glyphe pour eux), la caisse d'archives fermee du
          set du fondateur (`archive.svg`) pour les archives. */}
      {showModeles &&
        entree('modeles', 'Modèles', modelesLabel, <IconLayers1 size={14} />, modelesMode, onModelesToggle)}
      {entree('archives', 'Archives', archivesLabel, <IconArchive size={14} />, archivesMode, onArchivesToggle)}
      {modelesMode && onNewModele != null && (
        <>
          <span aria-hidden className="my-1 h-px shrink-0 bg-border-soft" />
          <button
            type="button"
            role="menuitem"
            aria-disabled={newOff || undefined}
            title={newLabel}
            aria-label={newLabel}
            onClick={newOff ? undefined : onNewModele}
            className={cn(
              'flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left font-sans text-[0.8125rem] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
              newOff
                ? 'cursor-default text-ink-300'
                : 'cursor-pointer text-ink-700 hover:bg-surface-1 hover:text-ink-900',
            )}
          >
            <span aria-hidden className="pointer-events-none flex shrink-0">
              <IconPlus size={14} />
            </span>
            <span className="min-w-0 flex-1 truncate">Nouveau modèle</span>
          </button>
        </>
      )}
    </div>
  );
}

/**
 * UN TAG de la zone 4a : l'etat courant rendu en objets retirables. Contour
 * --accent-border sur fond blanc, JAMAIS de remplissage lime : le remplissage
 * pale appartient aux pieces du tag (survol, rang), le lime plein au seul
 * bouton d'ajout. Anatomie : corps (kind + valeur [+ rang] [+ sens]) puis la
 * croix. Le corps d'un tag de TRI inverse son sens ; celui d'un tag de FILTRE
 * est inerte. Encres : valeur --ink-900, kind / rang / sens --ink-600 (jamais
 * de *-soft-ink en texte sous 14px).
 */
function DrawerTag({
  kind,
  value,
  rank,
  dir,
  onInvert,
  onRemove,
  removeLabel,
}: {
  /** Le genre, en petit : "Filtre" ou "Tri" (sans lui, Camera et Nom se lisent pareil). */
  kind: string;
  value: string;
  /** Le niveau de tri, rendu seulement quand la pile en a 2 ou plus. */
  rank?: number;
  /** Sens du tri : chevron du set, pivote pour l'ascendant (jamais un second glyphe). */
  dir?: 'asc' | 'desc';
  /** Present = le corps est cliquable et inverse le sens. */
  onInvert?: () => void;
  /** Present = la croix se rend et retire le tag. */
  onRemove?: () => void;
  /** L'aria-label ET le title de la croix. Defaut : "Retirer <valeur>". */
  removeLabel?: string;
}): JSX.Element {
  const interactive = onInvert != null;
  const removeText = removeLabel ?? `Retirer ${value}`;
  return (
    <span
      className={cn(
        'flex h-8 shrink-0 items-stretch overflow-hidden rounded-md border border-border bg-surface-1 text-ink-900',
      )}
    >
      <button
        type="button"
        disabled={!interactive}
        {...(interactive ? { onClick: onInvert, title: 'Inverser le sens' } : {})}
        className={cn(
          'flex items-center gap-1.5 border-none bg-transparent pl-3 pr-1.5 font-sans text-xs font-semibold text-inherit transition-colors',
          'focus-visible:outline-none',
          interactive
            ? 'cursor-pointer hover:bg-surface-3 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border'
            : 'cursor-default',
        )}
      >
        <span className="text-[0.6875rem] font-medium text-ink-600">{kind}</span>
        <span className="whitespace-nowrap">{value}</span>
        {rank != null && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-xs bg-surface px-[0.1875rem] font-sans text-[0.625rem] font-semibold tabular-nums text-ink-600">
            {rank}
          </span>
        )}
        {dir != null && (
          <span aria-hidden className="flex text-ink-600">
            <IconChevronDown size={11} className={cn(dir === 'asc' && 'rotate-180')} />
          </span>
        )}
      </button>
      {onRemove != null && (
        /* ETEINTE, LA CROIX NE RETIRE PLUS RIEN, et c'est la meme regle que le
           popover : retirer un filtre qui ne s'applique pas repondrait a un
           geste qui n'a aucun effet visible sur la liste. Elle garde son
           `aria-label` (le tag reste annonce) mais lache son `title`, sinon
           l'infobulle de la piece masquerait la RAISON portee par la coque. */
        <button
          type="button"
          onClick={onRemove}
          title={removeLabel ?? 'Retirer'}
          aria-label={removeText}
          className={cn(
            'flex w-6 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-ink-400 transition-colors',
            'hover:bg-surface-3 hover:text-ink-900',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
          )}
        >
          <IconClose size={10} />
        </button>
      )}
    </span>
  );
}

/** La boite commune des glyphes « ... » de la rangee 3 : carre de 2rem. */
const RECORD_NAV_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border-none bg-transparent text-ink-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border';

/**
 * LA FERMETURE D'UN MENU DE GESTES APRES UN CLIC, commune aux deux « ... »
 * (fiche et canvas), qui partagent leur panneau : tout de suite, ou apres
 * `garderLeMenuOuvert` pour une entree qui affiche son propre accuse. Le
 * minuteur tombe si le menu se ferme autrement entre-temps (clic dehors,
 * Echap) ou si le groupe se demonte : il ne refermera jamais un menu rouvert.
 */
function useFermetureApresGeste(
  menuOpen: boolean,
  setMenuOpen: (open: boolean) => void,
): (action: ScreenAction) => void {
  const minuteurRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const annuler = useCallback(() => {
    if (minuteurRef.current !== null) clearTimeout(minuteurRef.current);
    minuteurRef.current = null;
  }, []);
  useEffect(() => {
    if (!menuOpen) annuler();
  }, [menuOpen, annuler]);
  useEffect(() => annuler, [annuler]);
  return useCallback(
    (action: ScreenAction) => {
      annuler();
      const delai = action.garderLeMenuOuvert;
      if (delai === undefined || delai <= 0) {
        setMenuOpen(false);
        return;
      }
      minuteurRef.current = setTimeout(() => {
        minuteurRef.current = null;
        setMenuOpen(false);
      }, delai);
    },
    [annuler, setMenuOpen],
  );
}

/**
 * LE PANNEAU D'UN MENU DE GESTES : celui du « ... » d'une fiche
 * (`recordMenuActions`) et, depuis le 05/10/2026, celui du « ... » d'un canvas
 * qui garde 3b (`canvasMenuActions`). UN SEUL rendu pour les deux, parce que
 * c'est le meme objet a l'ecran : un panneau `role="menu"` d'entrees de 2rem,
 * chaque entree eteinte restant visible avec sa raison (G16, G18), aucun rouge
 * (G21), et un panneau VIDE qui dit pourquoi au lieu de ne pas s'ouvrir.
 *
 * Meme vocabulaire que le popover des outils (--shadow-pop, --radius-md, items
 * de 2rem), mais `role="menu"` et non `listbox` : ici on declenche des gestes,
 * on ne choisit pas un etat, et il n'y a pas d'entree de sortie. C'est ce qui
 * l'empeche de reutiliser `ToolPopover` tel quel.
 *
 * UNE ENTREE `checked` EST UN INTERRUPTEUR (`menuitemcheckbox`, `aria-checked`),
 * a l'encre pleine quand elle est active et avec la croix de sortie du menu
 * des modes : c'est l'entree « Archives » d'un canvas, et elle se lit comme
 * celle d'une porte (G17, G20).
 */
function ActionsMenuPanel({
  id,
  label,
  emptyText,
  actions,
  onRun,
}: {
  id: string;
  label: string;
  /** La phrase du panneau vide (G16 : un menu qui s'ouvre sur rien le dit). */
  emptyText: string;
  actions: ReadonlyArray<CanvasMenuAction>;
  onRun: (action: CanvasMenuAction) => void;
}): JSX.Element {
  return (
    <div
      id={id}
      role="menu"
      aria-label={label}
      className={cn(
        'absolute right-0 top-[calc(100%+0.4375rem)] z-20 flex min-w-48 flex-col p-1',
        'rounded-md border border-border bg-surface shadow-pop',
      )}
    >
      {actions.length === 0 ? (
        <button
          type="button"
          role="menuitem"
          aria-disabled
          title={emptyText}
          className="flex h-8 cursor-default items-center rounded-sm px-2 text-left font-sans text-[0.8125rem] font-medium text-ink-300"
        >
          Aucune action pour l&apos;instant
        </button>
      ) : (
        actions.map((action) => {
          /* UNE ACTION ETEINTE RESTE VISIBLE AVEC SA RAISON (G16, G18) :
             une porte qui ne sait pas supprimer rend l'entree grisee, pas
             absente. Un geste qui apparait et disparait selon l'app
             fabrique exactement le "pourquoi je ne peux pas supprimer
             ici" qu'on veut eviter.
             `aria-disabled` et NON `disabled`, comme partout dans ce
             fichier : un bouton `disabled` ne recoit aucun evenement de
             souris, son `title` ne s'affiche donc jamais, et il sort de
             l'ordre de tabulation. Sa raison serait inatteignable, ce qui
             est exactement ce que G16 interdit.
             AUCUN ROUGE ICI, "Supprimer" compris : le rouge est le
             DERNIER geste d'un chemin, il ne vit que dans la fenetre de
             confirmation. En surface, il attirerait le clic qu'il devrait
             retenir. */
          const off = action.disabled === true;
          const texte = off && action.disabledReason != null
            ? `${action.label} : ${action.disabledReason}`
            : action.label;
          const interrupteur = action.checked !== undefined;
          const actif = action.checked === true;
          return (
            <button
              key={action.id}
              type="button"
              role={interrupteur ? 'menuitemcheckbox' : 'menuitem'}
              aria-checked={interrupteur ? actif : undefined}
              aria-disabled={off || undefined}
              title={texte}
              aria-label={texte}
              onClick={off ? undefined : () => onRun(action)}
              className={cn(
                'flex h-8 items-center gap-2 rounded-sm px-2 text-left font-sans text-[0.8125rem] font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                off
                  ? 'cursor-default text-ink-300'
                  : actif
                    ? 'cursor-pointer bg-ink-900 font-semibold text-surface'
                    : 'cursor-pointer text-ink-700 hover:bg-surface-1 hover:text-ink-900',
              )}
            >
              {action.icon != null && (
                <span className="flex shrink-0 items-center justify-center">
                  {action.icon}
                </span>
              )}
              <span className="min-w-0 flex-1 truncate">{action.label}</span>
              {actif && (
                /* LA CROIX DE SORTIE, comme au menu des modes : sur un aplat
                   encre, rien ne dit qu'un second clic ressort. */
                <span aria-hidden className="flex shrink-0">
                  <IconClose size={10} />
                </span>
              )}
            </button>
          );
        })
      )}
    </div>
  );
}

/**
 * LE MENU « ... » D'UN CANVAS QUI GARDE 3b (regle G23, `canvasMenuActions`).
 * Le meme bouton (trois carres pleins, `IconFullSpaced`) et le meme panneau
 * que le « ... » d'une fiche, pose au meme endroit : tout a droite de la
 * rangee 3. Un clic dehors ou Echap le referme (capture sur `document`, meme
 * raison que `RecordNavGroup`). Le bouton reste a l'encre tant qu'un
 * interrupteur du menu est actif : c'est le rappel du mode, comme le menu des
 * modes le fait pour Archives.
 */
function CanvasMenuGroup({
  actions,
  onRunAction,
}: {
  actions: ReadonlyArray<CanvasMenuAction>;
  onRunAction: (action: ScreenAction) => void;
}): JSX.Element {
  const baseId = useId();
  const menuId = `${baseId}-canvas-menu`;
  const [menuOpen, setMenuOpen] = useState(false);
  const fermerApresGeste = useFermetureApresGeste(menuOpen, setMenuOpen);
  const hostRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      const host = hostRef.current;
      if (host != null && event.target instanceof Node && host.contains(event.target)) return;
      setMenuOpen(false);
    };
    const onDocKeyCapture = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeyCapture, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onDocKeyCapture, true);
    };
  }, [menuOpen]);
  const actif = actions.some((action) => action.checked === true);
  return (
    <>
      <span aria-hidden className="h-4 w-px shrink-0 bg-border" />
      <span ref={hostRef} className="relative flex shrink-0">
        <button
          type="button"
          title="Autres actions"
          aria-label="Autres actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => setMenuOpen((open) => !open)}
          className={cn(
            RECORD_NAV_BUTTON,
            'cursor-pointer hover:bg-surface-3 hover:text-ink-900',
            (menuOpen || actif) && 'bg-surface-3 text-ink-900',
          )}
        >
          <IconFullSpaced size={14} />
        </button>
        {menuOpen && (
          <ActionsMenuPanel
            id={menuId}
            label="Autres actions"
            emptyText="Cette surface ne déclare aucun geste ici."
            actions={actions}
            onRun={(action) => {
              fermerApresGeste(action);
              onRunAction(action);
            }}
          />
        )}
      </span>
    </>
  );
}

/** L'id de la vue fiche du socle : toujours la premiere des vues de fiche. */
const VUE_FICHE_DU_SOCLE = 'core.fiche';

/**
 * LES VUES DE FICHE DU SELECTEUR, dans l'ordre : les entrees de niveau
 * `record` de `views`, « Fiche » (`core.fiche`) en tete quoi qu'en dise
 * l'ordre recu, les autres dans l'ordre declare. Exportee pour les tests.
 */
export function vuesDeFicheEnOrdre(views: readonly ViewOption[]): ViewOption[] {
  const deFiche = views.filter((view) => view.level === 'record');
  return [
    ...deFiche.filter((view) => view.id === VUE_FICHE_DU_SOCLE),
    ...deFiche.filter((view) => view.id !== VUE_FICHE_DU_SOCLE),
  ];
}

/**
 * LE GROUPE EPINGLE AU NIVEAU `record` (3c) : le selecteur des vues de fiche,
 * puis le menu « ... » des gestes SUR la fiche, sans filet entre les deux
 * (atelier du fondateur du 26/10, `docs/maquettes/atelier-2610/README.md`,
 * section 1).
 *
 * IL PREND LA PLACE DU SELECTEUR DES VUES DE COLLECTION : meme bout de rangee,
 * meme hauteur (2rem), meme bord droit. Le groupe epingle ne bouge pas d'un
 * niveau a l'autre, seul son CONTENU change.
 *
 * LE SELECTEUR EST `ViewSelector` LUI-MEME, nourri des vues de niveau `record`
 * de l'ecran : memes boites, entree active encree sur fond blanc, jamais de
 * lime, libelle au survol. « Fiche » (`core.fiche`) est toujours la premiere.
 * Une seule vue de fiche : pas de selecteur (la regle est dans `ViewSelector`, la meme qu'au niveau collection), rien a choisir. Le choix remplace
 * le contenu de la zone 5 ; la rangee 3 et le rail de suivi ne bougent pas.
 *
 * LE « ... » SE CALE SUR L'AXE DU RAIL DE SUIVI quand la fiche en a un
 * (`surLeRail`) : le rail fait 3rem au bord droit, son axe tombe a 1.5rem du
 * bord ; la rangee a 1.25rem de retrait et le bouton 2rem, d'ou 0.75rem de
 * moins a droite. Son centre et celui du compteur du rail sont sur la meme
 * verticale.
 */
function RecordNavGroup({
  vuesDeFiche,
  activeViewId,
  onViewSelect,
  surLeRail,
  actions,
  onRunAction,
}: {
  /** Les vues de niveau `record` de l'ecran, « Fiche » en tete. Moins de deux : `ViewSelector` ne rend rien. */
  vuesDeFiche: ViewOption[];
  activeViewId?: ViewId;
  onViewSelect?: (id: ViewId) => void;
  /** La fiche a un rail de suivi : le « ... » se cale sur son axe. */
  surLeRail: boolean;
  /** Les gestes rares du menu "..." (`recordMenuActions`). Vide : le menu dit pourquoi. */
  actions: ScreenAction[];
  /** Declenche l'action : le chassis ouvre sa fenetre si elle en porte une. */
  onRunAction: (action: ScreenAction) => void;
}): JSX.Element {
  const baseId = useId();
  const menuId = `${baseId}-record-menu`;
  const [menuOpen, setMenuOpen] = useState(false);
  const fermerApresGeste = useFermetureApresGeste(menuOpen, setMenuOpen);
  const menuHostRef = useRef<HTMLSpanElement | null>(null);

  // Un clic dehors referme le menu. Meme regle que les popovers d'outils : le
  // clic est juge par appartenance a l'ancre, pas par cible exacte.
  //
  // ECHAP AUSSI, ET EN PHASE DE CAPTURE SUR `document`. `useScreen` ecoute le
  // meme Echap en BULLE pour fermer la FICHE : une coupure posee sur le groupe
  // ne joue que si le focus est reste dedans, ce qui n'est pas le cas des
  // qu'on a tabule hors du groupe, ni sous Safari macOS ou un clic sur un
  // bouton ne le focalise pas. Le premier Echap fermait alors la fiche en
  // laissant le panneau ouvert : une frappe, le mauvais effet. La capture sur
  // `document` passe AVANT toute ecoute en bulle et `stopPropagation` empeche
  // celle-ci de s'executer, quel que soit l'element focalise.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (event: MouseEvent) => {
      const host = menuHostRef.current;
      if (host != null && event.target instanceof Node && host.contains(event.target)) return;
      setMenuOpen(false);
    };
    const onDocKeyCapture = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeyCapture, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onDocKeyCapture, true);
    };
  }, [menuOpen]);

  return (
    <div
      role="group"
      aria-label="Fiche"
      className="flex h-8 shrink-0 items-center gap-2"
      onBlur={(event) => {
        // Un panneau ne survit pas au focus qui s'en va : tabuler hors du
        // groupe le referme. `relatedTarget` nul (clic dans le vide) ferme
        // aussi, le clic dehors s'en chargeait de toute facon.
        if (!menuOpen) return;
        const next = event.relatedTarget;
        if (next instanceof Node && event.currentTarget.contains(next)) return;
        setMenuOpen(false);
      }}
    >
      <ViewSelector
        views={vuesDeFiche}
        {...(activeViewId != null ? { activeViewId } : {})}
        {...(onViewSelect != null ? { onViewSelect } : {})}
      />
      <span ref={menuHostRef} className={cn('relative flex', surLeRail && '-mr-3')}>
        {/* LE MENU DES GESTES RARES, rempli par `recordMenuActions` au chantier 2_0
            (Archiver / Desarchiver et Supprimer, decision du fondateur du
            07/08/2026 : ces gestes sont rares, un menu est leur place, la
            rangee 3a porte ce qu'on fait souvent). Il S'OUVRE meme vide, et son
            panneau DIT alors pourquoi : un controle mort et muet est interdit
            (G16), un controle qui ne peut pas s'ouvrir ne se rend pas. */}
        <button
          type="button"
          title="Autres actions"
          aria-label="Autres actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => setMenuOpen((open) => !open)}
          className={cn(
            RECORD_NAV_BUTTON,
            'cursor-pointer hover:bg-surface-3 hover:text-ink-900',
            menuOpen && 'bg-surface-3 text-ink-900',
          )}
        >
          {/* Le "..." NU : trois carres PLEINS, largement espaces. C'est le
              glyphe que le prototype REND (`full_spaced.svg`, mesure dans son
              balisage), alors que sa prose annonce `menu.svg`. Les deux
              existent au set et ne se ressemblent pas : `menu.svg` porte des
              carres EVIDES (un carre creux dans chaque) et son vide est deux
              fois plus serre, il se lit "grille", pas "et le reste". Aucun des
              deux n'est jointif : c'est la LARGEUR DU VIDE qui les separe, pas
              sa presence. Le fichier qui REND fait foi ; ARBITRAGE OUVERT :
              une ligne a changer ici si le fondateur voulait bien `IconMenu`,
              en sachant qu'une grille dirait "changer de vue" a l'endroit
              exact ou le niveau collection change de vue. */}
          <IconFullSpaced size={14} />
        </button>
        {menuOpen && (
          /* Meme vocabulaire que le popover des outils (--shadow-pop,
             --radius-md, items de 2rem), mais `role="menu"` et non
             `listbox` : ici on declenche des gestes, on ne choisit pas un
             etat, et il n'y a pas d'entree de sortie. C'est ce qui l'empeche
             de reutiliser `ToolPopover` tel quel. */
          <ActionsMenuPanel
            id={menuId}
            label="Autres actions"
            emptyText="Cet écran ne déclare aucun geste sur la fiche."
            actions={actions}
            onRun={(action) => {
              fermerApresGeste(action);
              onRunAction(action);
            }}
          />
        )}
      </span>
    </div>
  );
}

/* ========================================================================== */
/* LA BARRE DE LOT : LE TROISIEME ETAT DE LA RANGEE 3 (chantier 3_0)         */
/* ========================================================================== */

/**
 * UN GESTE DE LOT EN COURS, GELE au moment du clic : le verbe, les ids exacts,
 * et la primitive A DOCUMENT UNIQUE sur laquelle `BulkRunner` va boucler.
 *
 * LA PRIMITIVE VOYAGE AVEC LE LOT, elle n'est pas relue au moment d'ecrire :
 * c'est ce qui garantit qu'on ecrit avec le verbe de la porte que
 * l'utilisateur avait sous les yeux quand il a confirme. Meme regle que
 * l'action gardee dans `confirming`.
 */
interface BulkJob {
  kind: 'archive' | 'remove';
  ids: string[];
  runOne(id: string): Promise<void>;
  /**
   * LE NOM DE CHAQUE FICHE DU LOT, LUI AUSSI GELE AU CLIC, et c'est le point.
   * Le compte rendu doit NOMMER les fiches tombees ; or a la fin de l'ecriture
   * les fiches ECRITES ont deja quitte la vue (elles sont archivees, ou elles
   * ne passent plus le filtre). Un nom relu apres coup retomberait sur l'id,
   * pile pour les lignes qu'il faut pouvoir rattraper.
   */
  labelOf(id: string): string;
}

/**
 * LE COMPTE S'ACCORDE. On ecrit ce qu'on dirait A VOIX HAUTE, jamais
 * "N element(s) selectionne(s)" : le "(s)" est une economie de developpeur que
 * personne ne prononce, et elle se lit dans un produit qui parle francais
 * partout ailleurs.
 */
function selectionLabel(count: number): string {
  return count > 1 ? `${count} éléments sélectionnés` : `${count} élément sélectionné`;
}

/**
 * LA SORTIE DE LA SELECTION, un seul texte pour les deux chemins (la croix et
 * la touche). `Echap` est NOMMEE ICI ET NULLE PART AILLEURS : le prototype
 * valide ne dessine aucune puce `kbd` dans cette barre, et il fait foi sur le
 * rendu. La touche reste donc annoncee la ou G16 la veut, sur le controle qui
 * fait le geste, sans ajouter un cinquieme objet a une barre que le fondateur a
 * validee a QUATRE controles.
 */
const CLEAR_SELECTION_LABEL = 'Annuler la sélection (Échap)';

/** `canvasTitleIcon` : la liste FERMEE, le meme cadenas que le rail. */
const ICONES_TITRE_CANVAS = {
  cadenas: IconPadlockSquare1,
} as const;

/**
 * LA BOITE DES BOUTONS DE LA RANGEE 3, DANS SES DEUX ETATS : la barre de lot
 * ici, et la rangee 3a dans le corps de `ScreenLayout` (`actionButtonClasses`,
 * qui vaut cette constante-ci et RIEN D'AUTRE).
 *
 * ELLES NE SONT PLUS JUMELLES, ELLES SONT LA MEME. Un commentaire ne tient pas
 * un invariant : que l'une des deux chaines bouge seule, et la rangee 3 change
 * de hauteur entre son etat collection et son etat lot, donc toute la zone 5
 * saute d'un cran au premier clic dans une case a cocher. Le partage rend
 * l'accident impossible, et il ne coute qu'une ligne.
 */
const BULK_BUTTON = 'h-8 shrink-0 rounded-sm px-3 text-[0.8125rem]';

/**
 * LA HACHURE DE CE QUI EST HORS SERVICE PENDANT UN LOT : le TIROIR (zone 4) et
 * la RECHERCHE (zone 2b). Le nom dit son premier usage, la regle vaut pour les
 * deux : ce qui changerait le JEU sous un lot en cours se hachure, il ne
 * disparait pas.
 *
 * UN CALQUE UNIQUE, EN `::after` : il GRISE, il RAYE et il BLOQUE LES CLICS.
 * Un seul objet pour trois roles ; trois objets se seraient desynchronises au
 * premier ajustement. Le contenu passe DESSOUS a 0.35 d'opacite : attenue,
 * encore lisible, parce qu'on doit pouvoir relire le filtre qui explique la
 * liste sur laquelle on est en train d'agir.
 *
 * AUCUNE COULEUR EN DUR : le trait est `--border-strong`, le vide est
 * `transparent`, -45 degres, 5px de vide (0.3125rem) pour 1px de trait
 * (jusqu'a 0.375rem). Le theme sombre suit tout seul.
 *
 * CES CLASSES SONT LUES PAR TAILWIND DANS CE FICHIER, telles quelles : les
 * couper en morceaux calcules les ferait disparaitre de la feuille de style
 * sans qu'aucun build ne rougisse, et la hachure ne se verrait plus.
 */
const DRAWER_HATCH =
  "relative [&>*]:opacity-35 after:absolute after:inset-0 after:z-[1] after:content-[''] " +
  'after:bg-[image:repeating-linear-gradient(-45deg,transparent_0_0.3125rem,var(--border-strong)_0.3125rem_0.375rem)]';

/**
 * CE QUE LA HACHURE NE DIT PAS TOUTE SEULE.
 *
 * Les rayures disent qu'il se passe QUELQUE CHOSE ; elles ne disent ni QUOI, ni
 * pour COMBIEN DE TEMPS, ni COMMENT revenir. Sans ces deux phrases, cinq clics
 * (un tag, sa croix, "Effacer filtres", une pastille de favori, le champ de
 * recherche) ne produisent strictement rien et aucun ne s'explique : on ne sait
 * pas si c'est hors service ou casse.
 *
 * ELLES NE NOMMENT PAS `Echap`, et c'est voulu : la touche est nommee a UN seul
 * endroit du produit, sur le controle qui fait le geste (voir
 * `CLEAR_SELECTION_LABEL`). Ici on nomme la SORTIE, pas la touche.
 *
 * OU ELLES SE POSENT : sur la coque EXTERIEURE, celle qui reste survolable,
 * jamais sur le contenu hachure. Le calque bloque la souris et `inert` bloque
 * le clavier : un `title` pose dessous ne serait jamais lu.
 */
const DRAWER_OFF_REASON =
  "Le tiroir est hors service tant qu'une sélection est en cours. " +
  'Annule la sélection pour filtrer à nouveau.';
/**
 * POURQUOI LES TROIS GESTES DE LA BARRE S'ETEIGNENT quand un lot est deja
 * ouvert (voir `bulkBarAvailability`). Elle nomme ce qui est A L'ECRAN, parce
 * que c'est la fenetre ouverte qui porte la suite du geste : deux boucles
 * lancees sur la meme selection ecriraient les memes documents deux fois, et le
 * second compte rendu effacerait le premier, sa liste d'echecs comprise.
 */
const BULK_BUSY_REASON =
  "Un geste de lot est déjà en cours. Réponds à la fenêtre ouverte, puis recommence.";
/** Le pendant pour la recherche (2b). Meme regle, meme forme, autre zone. */
const SEARCH_OFF_REASON =
  "La recherche est hors service tant qu'une sélection est en cours. " +
  'Annule la sélection pour chercher à nouveau.';

/**
 * UN GESTE DE LOT. Il est GRISE AVEC SA RAISON quand la porte ne sait pas le
 * faire, jamais absent (G16).
 *
 * LA RAISON EST PORTEE PAR UNE COQUE, PAS PAR LE BOUTON. Le `Button` du socle
 * porte `disabled:pointer-events-none` : desactive, il ne recoit aucun
 * evenement de souris, donc son propre `title` ne s'afficherait jamais. Meme
 * procede que le bouton "+ Ajouter" de la rangee 3a, quelques centaines de
 * lignes plus bas ; le bouton garde la raison en `aria-label` pour le clavier
 * et les lecteurs d'ecran.
 */
function BulkAction({
  label,
  variant,
  reason,
  onClick,
}: {
  label: string;
  variant: 'primary' | 'secondary' | 'danger';
  /** `null` = disponible. Une chaine = la raison, et le bouton s'eteint. */
  reason: string | null;
  onClick: () => void;
}): JSX.Element {
  const off = reason != null;
  return (
    <span title={reason ?? label} className="flex shrink-0">
      <Button
        variant={variant}
        size="sm"
        className={BULK_BUTTON}
        aria-label={reason ?? label}
        onClick={onClick}
        disabled={off}
      >
        {label}
      </Button>
    </span>
  );
}

/**
 * LA BARRE DE LOT, le TROISIEME etat de la rangee 3 (apres collection et
 * fiche). Des qu'une ligne est cochee, la rangee change de NATURE et se vide
 * ENTIEREMENT : plus d'actions de porte (3a), plus d'outils (3b), et plus de
 * selecteur de vues (3c).
 *
 * CE DERNIER POINT CONTREDIT G18 ("epingle, jamais conditionnel") ET C'EST
 * VOULU : arbitrage du fondateur du 07/08/2026, le selecteur s'efface avec le
 * reste. On n'est plus en train de REGARDER une collection, on est en train
 * d'AGIR sur un lot, et changer de vue au milieu du geste ferait tomber la
 * selection (elle n'existe qu'en tableau). La doctrine porte l'exception, ce
 * n'est pas un oubli a "reparer".
 *
 * ALIGNEE A GAUCHE, PAS CENTREE : un groupe centre aurait invente un troisieme
 * alignement dans un ecran qui n'en connait que deux.
 *
 * EXACTEMENT QUATRE CONTROLES, et le premier n'en est pas un :
 *
 *   [ 3 elements selectionnes  x ]  [ Modification groupee ]  [ Archiver ]  [ Supprimer ]
 *
 * - LE COMPTE EST UN TEXTE, en encre. Il ne se clique pas, il s'ANNONCE
 *   (`role="status"`). Le premier essai en faisait un bouton portant aussi la
 *   croix : trop lourd, et ca faisait du compte un controle.
 * - LA CROIX EST LE SEUL CONTROLE de ce groupe, et elle est NUE comme les
 *   autres commandes du gabarit ("Effacer filtres", "Garder") : les coques
 *   bordees appartiennent aux OBJETS, pas aux GESTES.
 * - "MODIFICATION GROUPEE" EST PRIMAIRE, EN APLAT LIME, et LA REGLE DU LIME
 *   UNIQUE TIENT QUAND MEME : cet etat est le seul ou le bouton "+ Ajouter" a
 *   QUITTE l'ecran, donc le lime n'est pas duplique, il est TRANSFERE au lot.
 *   A NE PAS "REPARER" en retrogradant ce bouton.
 *   Le compte, lui, a ete essaye en lime sous trois formes et repose : aucun
 *   lime du set ne porte du texte de facon lisible (1.3:1 pour `--accent`,
 *   1.9:1 pour `--accent-dot`, 2.6:1 pour `--accent-soft-ink`, tous sous le
 *   seuil), et le seul lisible est l'aplat, qui ferait du compte un objet.
 * - "SUPPRIMER" N'EST PAS ROUGE PLEIN ICI : il porte la variante `danger` du
 *   socle, qui est en CONTOUR (bordure `--danger-border`, encre `--danger`,
 *   fond `--danger-tint` au survol). Le rouge PLEIN est garde pour le bouton de
 *   confirmation de la fenetre, ou il est le DERNIER geste et non le premier :
 *   un aplat rouge dans une barre attire le clic qu'il devrait retenir.
 */
function BulkBar({
  count,
  availability,
  onClear,
  onEdit,
  onArchive,
  onRemove,
}: {
  count: number;
  availability: { archive: string | null; remove: string | null; edit: string | null };
  /** Absent : la croix s'eteint avec sa raison, elle ne disparait pas. */
  onClear?: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onRemove: () => void;
}): JSX.Element {
  // UN ETAT SANS SORTIE EST UNE NASSE. Si le moteur n'a pas branche
  // l'annulation, la croix reste VISIBLE et DIT pourquoi elle ne repond pas
  // (G16) : la faire disparaitre laisserait l'utilisateur enferme dans un etat
  // dont plus rien a l'ecran ne nomme la sortie.
  const clearOff = onClear == null;
  const clearLabel = clearOff
    ? "Cet écran ne sait pas annuler la sélection. Décoche les lignes une à une."
    : CLEAR_SELECTION_LABEL;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <span className="flex shrink-0 items-center gap-1">
        {/* LE COMPTE. `role="status"` : il s'annonce tout seul quand il change,
            personne n'a a aller le chercher. Chasse tabulaire pour que passer
            de 9 a 10 ne fasse pas glisser la croix d'un demi-caractere. */}
        <span
          role="status"
          className="whitespace-nowrap font-sans text-[0.8125rem] font-semibold tabular-nums text-ink-900"
        >
          {selectionLabel(count)}
        </span>
        {/* LA CROIX, NUE. Meme anatomie que les gestes du tiroir : ni contour
            ni fond, un survol, un anneau au focus. Elle S'ETEINT par
            `aria-disabled` et JAMAIS par `disabled` : un bouton `disabled` ne
            recoit aucun evenement de souris, son `title` ne s'afficherait donc
            jamais, et il sort de l'ordre de tabulation. Sa raison serait
            inatteignable, exactement ce que G16 interdit. */}
        <button
          type="button"
          onClick={clearOff ? undefined : onClear}
          aria-disabled={clearOff || undefined}
          title={clearLabel}
          aria-label={clearLabel}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-pill border-none bg-transparent p-0',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
            clearOff
              ? 'cursor-default text-ink-300'
              : 'cursor-pointer text-ink-400 hover:bg-surface-3 hover:text-ink-900',
          )}
        >
          <IconClose size={12} />
        </button>
      </span>
      <BulkAction
        label="Modification groupée"
        variant="primary"
        reason={availability.edit}
        onClick={onEdit}
      />
      <BulkAction
        label="Archiver"
        variant="secondary"
        reason={availability.archive}
        onClick={onArchive}
      />
      <BulkAction
        label="Supprimer"
        variant="danger"
        reason={availability.remove}
        onClick={onRemove}
      />
    </div>
  );
}

/**
 * Gabarit d'ecran standard MyTaki (gabarit v2, habillage plat).
 *
 * Squelette : rail gauche fixe (identite + portes, rien d'autre) + colonne
 * principale (en-tete 3 colonnes, rangee de commandes, tiroir, contenu).
 * Zones separees par des filets `border-soft` sur une surface blanche
 * continue. Seule la zone 5 defile.
 *
 * @example
 * <ScreenLayout
 *   appName="Projets"
 *   appIcon={<IconModule size={18} />}       // puce en tete du rail (1a)
 *   menuItems={[{ id: 'projets', label: 'Projets', count: 30 }]}
 *   activeMenuId={screen.section ?? 'projets'} onMenuSelect={screen.setSection}
 *   title="Projets"
 *   headerStats={screen.door.stats}
 *   onSearchChange={screen.setSearch} searchValue={screen.query.search}
 *   searchPlaceholder={screen.door.searchPlaceholder}
 *   quickActions={screen.door.actions}       // rangee 3a, regle "+ Ajouter"
 *   tools={screen}                           // outils 3b + tiroir 4, tels quels
 *   sortOptions={[{ key: 'nom', label: 'Nom' }]}
 *   views={screen.views} activeViewId={screen.viewId} onViewSelect={screen.setView}
 * >
 *   ...contenu (ViewHost)...
 * </ScreenLayout>
 */
export function ScreenLayout({
  canvas,
  appName,
  appIcon,
  menuItems = [],
  menuHierarchyStyle,
  activeMenuId,
  onMenuSelect,
  title,
  titleContent,
  headerStats = [],
  onSearchChange,
  searchValue,
  searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
  quickActions: quickActionsProp = [],
  viewActions: viewActionsProp = [],
  canvasMenuActions,
  canvasActionGroups = [],
  canvasActionOrder,
  canvasMenuReplacesModes = false,
  canvasTitle,
  suivi = null,
  etapes = null,
  canvasTitleIcon = null,
  canvasTitlePrefix = null,
  tools,
  sortOptions = [],
  filters = [],
  activeFilterId,
  onFilterSelect,
  views = [],
  activeViewId,
  onViewSelect,
  // Sortie de la FICHE (rangee 3). A ne pas confondre avec `onBack` du
  // ScreenNavContext, lu quelques lignes plus bas, qui sort de l'APP entiere
  // depuis la puce d'icone de la zone 1a.
  onRecordExit,
  recordExitLabel = 'Retour',
  saveState,
  onRetrySave,
  recordMenuActions,
  recordActions = [],
  scrollRef,
  loading = false,
  skeleton,
  error = null,
  errorTitle,
  onRetry,
  retryLabel = 'Retry',
  empty = false,
  emptyTitle = 'Nothing here yet',
  emptyHint,
  children,
}: ScreenLayoutProps): JSX.Element {
  /*
   * LES CANVAS (G23), lus UNE fois et appliques par SOUSTRACTION : chaque zone
   * garde son rendu exact, on ne fait que decider si elle existe. C'est ce qui
   * garantit qu'un ecran NATIVE (canvas absente, la quasi-totalite du depot) ne
   * traverse pas une seule ligne de code neuve.
   */
  const isAppCanvas = canvas?.level === 'app';
  const elementsCadre = resoudreElementsCadre(canvas);
  /* Libre = aucun element natif APRES resolution : une porte retrouve toujours
     sa partie 1, donc son titre, meme declaree avec `elements: []`. */
  const cadreLibre = canvas != null && elementsCadre.length === 0;
  const garde = (element: string): boolean => elementsCadre.includes(element as typeof elementsCadre[number]);
  useFondCadre(garde('fond'));

  // La rangee conserve son balisage natif ; seules ses contributions changent.
  const rowActionsOff = canvas != null && !garde('actions');
  const quickActions = rowActionsOff ? [] : quickActionsProp;
  const viewActions = rowActionsOff ? [] : viewActionsProp;
  const showCanvasMenu = canvas != null && garde('menu') && canvasMenuActions != null;

  // Le rail et son identite se choisissent independamment des autres elements.
  const railDeCanvas = isAppCanvas && garde('rail');
  const showRail = garde('rail');
  const hasAppIdentity = appName != null && showRail && garde('identite');
  const hasSearch = onSearchChange != null && garde('recherche');

  // Le NIVEAU se DEDUIT, il ne se demande pas : `views` porte le level de chaque
  // entree et `activeViewId` dit laquelle est active. Une prop "niveau" serait
  // une seconde source de verite, donc une occasion de mentir, et chaque app
  // aurait a la calculer (exactement le bug du bouton "Modifier" re-code
  // partout). La garde explicite documente le cas "ecran sans vues".
  const isRecordLevel =
    activeViewId != null &&
    views.some((view) => view.id === activeViewId && view.level === 'record');

  // LE SELECTEUR (3c) PORTE LE NIVEAU OU L'ON EST, JAMAIS LES DEUX. Au niveau
  // collection, les vues de collection : on n'entre pas dans une fiche en
  // CHOISISSANT une vue, on entre en OUVRANT une ligne, et un selecteur qui
  // proposerait "Fiche" sans dire LAQUELLE promettrait un geste qu'il ne peut
  // pas tenir. Au niveau fiche, les vues DE FICHE (chantier `fiche-suivi`) :
  // la meme fiche montree autrement, « Fiche » (`core.fiche`) toujours en tete.
  // LE FILTRAGE EST AU RENDU, PAS A LA SOURCE : `views` garde ses deux niveaux
  // (c'est `isRecordLevel` juste au-dessus, et `lastCollectionViewId` cote
  // moteur, qui les lisent).
  const collectionViews = views.filter((view) => view.level === 'collection');
  const vuesDeFiche = vuesDeFicheEnOrdre(views);

  // LE RAIL DE SUIVI : au niveau fiche, des que l'ecran fournit le suivi.
  // Ailleurs, le meme objet est le bouton « Suivi » de la rangee 3.
  const railDeSuivi = isRecordLevel && suivi != null;

  // Titre (zone 2a) : le label de l'entree de menu active prime, `title` sert
  // de repli (ecran sans menu, ou id actif inconnu).
  const activeMenuItem = menuItems.find((item) => item.id === activeMenuId);
  // 2a. En App Canvas il n'y a ni rail ni porte : le h1 porte le NOM DE L'APP,
  // et l'en-tete ne disparait jamais entierement (doctrine section 9).
  /* LE TITRE POSE PAR UNE APP CANVAS (`canvasTitle`) : rendu apres le nom de
     l'app, jamais a sa place. Une chaine vide vaut l'absence. */
  const titrePoseParLaSurface =
    canvas != null && canvasTitle != null && canvasTitle.trim() !== '' ? canvasTitle.trim() : null;
  /* AVEC LE RAIL GARDE, le nom de l'app vit deja en 1a : 2a porte le titre de
     ce qui est ouvert, seul, comme le label de la porte active d'une app
     native. Sans rail, il suit le nom de l'app (« Canaux / general »). */
  const titreDeSurface = (railDeCanvas || canvas?.level === 'space') ? null : titrePoseParLaSurface;
  const headerTitle = railDeCanvas
    ? (titrePoseParLaSurface ?? activeMenuItem?.label ?? appName ?? title)
    : isAppCanvas
      ? (appName ?? title)
      : (titrePoseParLaSurface ?? tools?.chemin?.titre ?? activeMenuItem?.label ?? title);
  const chemin = !cadreLibre && !isAppCanvas ? (tools?.chemin ?? null) : null;

  // Zone 2c : plafond de 3 stats, tenu par le gabarit (doctrine de la porte).
  const stats = headerStats.slice(0, 3);
  const showStats = stats.length > 0 && garde('statistiques');

  // Champ de recherche : controle par l'app si `searchValue` est fourni, sinon
  // le gabarit tient un etat interne de repli (le champ reste fonctionnel meme
  // si l'app oublie `searchValue`), tout en remontant chaque frappe a l'app.
  const searchControlled = searchValue !== undefined;
  const [internalSearch, setInternalSearch] = useState('');
  const searchFieldValue = searchControlled ? searchValue : internalSearch;

  /* ---- Zones 3b + 4 : l'etat des outils, lu depuis le moteur ---- */

  // `sections: false` = la porte ne se tranche pas : le CATALOGUE de filtres,
  // l'etoile, l'outil FILTRE et le tiroir sont masques. Le menu « ... » des
  // modes reste : toute porte offre ses archives (G20).
  const doorSections = canvas != null ? (garde('filtres') || garde('tiroir')) : tools?.door?.sections !== false;
  const toolFilters = tools?.filters ?? [];
  const activeFilterIds = tools?.activeFilterIds ?? [];
  const sortStack = tools?.sortStack ?? [];
  const sortKeys = tools?.sortKeys;
  const allSortOptions = [...new Map([...sortOptions, ...(tools?.fieldSortOptions ?? [])]
    .map((option) => [option.key, option])).values()];
  const availableSortOptions = sortKeys == null ? allSortOptions
    : allSortOptions.filter((option) => sortKeys.includes(option.key));
  const favorites = tools?.favorites ?? [];
  const drawerOpen = tools?.drawerOpen ?? false;
  const canKeep = tools?.canKeepFavorite ?? false;
  const archivesMode = tools?.archivesMode ?? false;
  // LE MODE MODELES, LU A COTE DE SON JUMEAU. Meme repli `false` : un ecran qui
  // ne passe pas `tools` n'est dans aucun mode, et surtout pas dans celui-la.
  const modelesMode = tools?.modelesMode ?? false;
  /* LE MODE ALLUME SE LIT DANS LE TITRE (« Archives : Journal ») et dans le
     bouton qui remplace « Ajouter » (« Quitter Archives ») : decision du
     fondateur du 28/09/2026. Pas en App Canvas : sans rangee 3, aucun mode ne
     s'y allume, et le titre y porte le nom de l'app. */
  const prefixeMode = isAppCanvas ? null : prefixeDuMode({ archivesMode, modelesMode });
  const prefixeTitre =
    prefixeMode ?? (isAppCanvas ? canvasTitlePrefix : activeMenuItem?.prefixe?.trim() || null);
  const sortieDeMode = isAppCanvas ? null : libelleDeSortieDeMode({ archivesMode, modelesMode });

  /* ---- La selection de lignes et la barre de lot (chantier 3_0) ---- */

  const selection = tools?.selection ?? [];
  const writeCell = tools?.writeCell;
  const archiveOne = tools?.archiveOne;
  const removeOne = tools?.removeOne;

  /*
   * `bulkActive` : LA RANGEE 3 EST-ELLE DANS SON TROISIEME ETAT.
   *
   * `selectionLevel` SERT A REFUSER, JAMAIS A ALLUMER, et la nuance vaut la
   * peine d'etre lue. La selection n'existe QU'EN TABLEAU (arbitrage du
   * fondateur du 07/08/2026) et quitter le tableau l'ANNULE : c'est le moteur
   * qui le fait, comme il l'annule deja au changement de porte, de recherche,
   * de filtre ou de mode. Le chassis pourrait s'en assurer une seconde fois en
   * exigeant `selectionLevel === 'table'` ; il ne le fait PAS, parce qu'un
   * champ ABSENT eteindrait alors la barre entiere en silence, sans qu'aucun
   * build ne rougisse. C'est exactement la panne du chantier 2_0, ou le mode
   * Archives est parti en relecture avec une adresse entierement inerte.
   * On lit donc `!== null` : le moteur qui dit EXPLICITEMENT "aucune surface ne
   * peut choisir" est cru, celui qui ne dit rien ne bloque personne.
   *
   * `!isRecordLevel` : une fiche ne porte pas de selection. Si le moteur ouvre
   * une fiche avec des lignes encore cochees, la rangee 3 est celle de la
   * fiche, pas celle du lot.
   */
  const bulkActive =
    !isRecordLevel && selection.length > 0 && tools?.selectionLevel !== null;

  /*
   * CE QUE LA PORTE SAIT FAIRE, avec un FILET DE SECURITE. `bulkAvailability`
   * appartient au moteur ; mais un geste dont la primitive n'existe pas ne doit
   * pas se proposer et ne rien faire au clic. Un bouton muet est pire qu'un
   * bouton eteint : on croit avoir agi. La raison de repli est ecrite POUR UN
   * UTILISATEUR, comme celles du moteur.
   */
  const declaredAvailability = tools?.bulkAvailability;
  /*
   * LE PLAFOND SE DIT AVANT LA CONFIRMATION, JAMAIS APRES.
   *
   * `BulkRunner` refuse deja un lot trop grand, mais il le refuse dans une
   * fenetre qui ne s'ouvre qu'APRES la confirmation destructrice : on lisait
   * "Supprimer definitivement 350 fiches ? C'est definitif", on cliquait le
   * seul bouton rouge plein du produit, et la fenetre suivante repondait "Lot
   * trop grand, rien n'a ete ecrit". Le dernier geste irreversible du produit
   * franchi pour rien.
   *
   * ET LE PIEGE S'ATTEINT EN UN CLIC : la case d'en-tete du tableau coche tout
   * le jeu AFFICHE, et la fenetre cliente en tient jusqu'a 500, soit plus du
   * double du plafond. Les TROIS gestes s'eteignent donc AVEC LEUR RAISON
   * (G16), avant toute fenetre ; le refus cote fenetre reste, en dernier filet.
   *
   * LA MEME RAISON SUR LES TROIS, meme quand la porte a deja la sienne : tant
   * que le lot est trop grand, c'est CA qu'il faut corriger, et le reste se
   * dira tout seul une fois la selection reduite.
   */
  const overBulkMax = selection.length > BULK_MAX;
  const bulkMaxReason = `Un lot ne dépasse pas ${BULK_MAX} fiches. Tu en as choisi ${selection.length} : réduis la sélection.`;
  const bulkAvailability = overBulkMax
    ? { edit: bulkMaxReason, archive: bulkMaxReason, remove: bulkMaxReason }
    : {
        edit:
          declaredAvailability?.edit ??
          (writeCell == null ? "Cet écran ne sait pas modifier des fiches en lot." : null),
        archive:
          declaredAvailability?.archive ??
          (archiveOne == null ? "Cet écran ne sait pas archiver une fiche." : null),
        remove:
          declaredAvailability?.remove ??
          (removeOne == null ? "Cet écran ne sait pas supprimer une fiche." : null),
      };

  /*
   * LES GESTES DE FICHE : `tools` EST LE DEFAUT, LA PROP EST LA SURCHARGE.
   *
   * C'est ce qui tient la promesse du chantier 2_0, "dans toutes les apps, sur
   * toutes les portes, sans qu'aucune app n'ecrive une ligne". Tant qu'ils
   * n'arrivaient que par la prop, un ecran qui passait deja `tools={screen}` et
   * `recordCursor` sans y penser ouvrait un menu "..." vide la ou l'ecran d'a
   * cote proposait Archiver et Supprimer : meme menu, meme geste, deux
   * vocabulaires selon l'app, exactement le bouton qui apparait et disparait
   * que le brief interdit.
   *
   * `??` et non `||` : un ecran qui fournit DELIBEREMENT un tableau vide veut
   * un menu vide (et son panneau dira pourquoi), il ne veut pas retomber en
   * silence sur les gestes du moteur.
   */
  const menuActions = recordMenuActions ?? tools?.recordMenuActions ?? [];

  // L'OUTIL FILTRE NE SE REND QU'AVEC UN CATALOGUE. Depuis le 28/09/2026 les
  // modes vivent dans le menu « ... » (`showModesTool`) : un popover de filtre
  // sans une seule tranche serait vide, donc il n'est plus rendu sur une porte
  // `sections: false` ni sur une porte sans filtres.
  const showFilterTool = tools != null && doorSections && (toolFilters.length > 0 || (tools.filterFields?.length ?? 0) > 0) && garde('filtres');
  // LE MENU « ... » DES MODES SE REND PARTOUT OU LA RANGEE 3b EXISTE : toute
  // porte offre ses archives (G20), porte sans tranches comprise.
  // UNE APP CANVAS N'A PAS DE MODES DE PORTE : son « ... » est celui que la
  // surface pose (`canvasMenuActions`), « Archives » compris. Ses `tools` ne
  // portent que le catalogue de filtres qu'elle pose (`poserFiltres`).
  const showModesTool = tools != null && !isAppCanvas && !canvasMenuReplacesModes;
  const showSortTool = tools != null && availableSortOptions.length > 0 && garde('tri');
  // L'etoile n'est disponible que lorsque son tiroir natif est garde et alimente.
  const showStarTool =
    tools != null && garde('tiroir') && doorSections && tools.setDrawerOpen != null;
  // 3b. Un canvas ne garde le groupe d'outils que s'il le declare ; sinon la
  // rangee entiere disparait avec lui (voir `hasCommandRow`).
  const hasToolGroup =
    (showFilterTool || showSortTool || showStarTool);
  // Le menu « ... » suit la meme regle de canvas que le groupe 3b dont il
  // prolonge les outils, mais il se pose TOUT A DROITE de la rangee, apres le
  // selecteur de vues : la place exacte du « ... » d'une fiche.
  const showModesMenu =
    showModesTool && !isRecordLevel && (canvas == null || garde('menu'));
  // LE TIERS DE DROITE DU TIROIR (4b garder + 4c favoris) SUIT `sections`,
  // EXACTEMENT COMME L'ETOILE DE LA RANGEE 3b. Une porte `sections: false` dit
  // par contrat qu'elle n'a AUCUN favori : le bouton "garder en favori" y
  // proposait de fabriquer un objet que la porte ne saura jamais rendre. Il
  // n'etait garde que par la presence du callback, qui vient du moteur et existe
  // donc toujours ; l'etoile de 3b, elle, avait deja ete masquee pour cette
  // raison. Le trou ne se voyait pas tant que le tiroir ne se rendait pas sur
  // ces portes : le mode archives l'y fait paraitre.
  const showShortcuts = doorSections;
  // Le tiroir peut entourer un Canvas ; ses donnees viennent du fournisseur.
  const showDrawer = tools != null && garde('tiroir') && !isRecordLevel && doorSections;
  const drawerDeployed = drawerOpen;

  // UN SEUL popover ouvert a la fois. Etat du CHASSIS, pas du moteur : un menu
  // deroulant n'est pas un etat d'ecran, il ne survit ni au changement de
  // porte ni au passage en fiche (l'effet plus bas les referme).
  const [openPop, setOpenPop] = useState<'filter' | 'sort' | 'modes' | null>(null);

  /*
   * LES DEUX FENETRES DU CHASSIS. Elles vivent A LA RACINE de l'arbre rendu,
   * jamais dans la rangee 3 : celle-ci est `relative z-10` et ouvre donc un
   * contexte d'empilement dont un overlay ne sortirait plus.
   *
   * 1. LA FENETRE D'ENTREE DU MODE. Entrer dans un mode n'est pas cocher un
   *    filtre : ca change ENTIEREMENT ce que l'ecran montre, et rien dans un
   *    menu ne peut le faire comprendre en une ligne. Elle ne parait qu'a
   *    l'ENTREE ; SORTIR ne redemande RIEN, c'est reversible et sans
   *    consequence, redemander serait de la ceremonie.
   * 2. LA FENETRE D'UNE ACTION qui porte un `confirm` : c'est le CHASSIS qui
   *    l'ouvre et n'appelle `onClick` qu'apres confirmation.
   *
   * DEUX ETATS PAR FENETRE, ET C'EST VOULU : le Modal s'anime a la FERMETURE,
   * donc l'action confirmee doit rester en place le temps du fondu. On bascule
   * `confirmOpen` et on garde `confirming` : sans ca, la fenetre se viderait de
   * son texte avant de disparaitre.
   */
  const [askArchivesMode, setAskArchivesMode] = useState(false);
  /* LA MEME FENETRE, POUR L'AUTRE MODE. Deux etats et non un seul drapeau
     "quel mode demande-t-on" : les deux fenetres n'ont ni le meme titre, ni le
     meme corps, ni le meme bouton, et un etat partage n'aurait fait qu'ajouter
     un ternaire dans chacun des trois. */
  const [askModelesMode, setAskModelesMode] = useState(false);
  /* L'ACTION **ET** SA FENETRE DEJA RESOLUE. Les deux ensemble, parce qu'un
     `confirm` peut etre une FABRIQUE que le chassis attend au clic
     (`ScreenAction.confirm`) : relire `confirming.confirm` au rendu rappellerait
     la fabrique a chaque image, donc une lecture serveur par image. Ce qui est
     garde est le RESULTAT, celui que l'utilisateur a sous les yeux. */
  const [confirming, setConfirming] = useState<{
    action: ScreenAction;
    confirm: ScreenConfirm;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /* LE DOUBLE CLIC PENDANT L'ATTENTE D'UNE FABRIQUE. Entre le clic et la
     fenetre, il y a desormais un aller-retour serveur : le bouton reste vivant,
     et deux clics lanceraient deux comptes puis ouvriraient deux fois la meme
     fenetre. Une REF et non un etat : la garde doit valoir DANS le meme tick,
     avant tout rendu. */
  const confirmPendingRef = useRef(false);
  /*
   * 3. LA FENETRE A CHAMP D'UNE ACTION (`prompt`), meme chemin et memes DEUX
   *    etats que la confirmation, pour la meme raison : le Modal s'anime a la
   *    fermeture, donc la question doit rester lisible le temps du fondu.
   */
  const [prompting, setPrompting] = useState<ScreenAction | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const runRecordAction = (action: ScreenAction): void => {
    // LA SAISIE PASSE AVANT LA CONFIRMATION : un geste qui demande un mot ne
    // demande pas aussi un oui, et `onClick` n'est alors jamais appele (il n'a
    // pas la valeur). Voir `ScreenAction.prompt`.
    if (action.prompt != null) {
      setPrompting(action);
      setPromptOpen(true);
      return;
    }
    if (action.confirm == null) {
      action.onClick();
      return;
    }
    /* LA FENETRE CONNUE D'AVANCE : rien ne change, le chemin reste synchrone. */
    if (typeof action.confirm !== 'function') {
      setConfirming({ action, confirm: action.confirm });
      setConfirmOpen(true);
      return;
    }
    /*
     * LA FABRIQUE : le geste a besoin d'un fait que seul le serveur connait
     * (« combien de fiches pointent celle-ci ? »), et on ne le demande qu'au
     * CLIC. Trois issues, et une seule ouvre quelque chose :
     *  - un `ScreenConfirm` : la fenetre s'ouvre, comme d'habitude ;
     *  - `null` : le geste est REFUSE, on n'ouvre RIEN et on n'appelle pas
     *    `onClick`. La fabrique a deja pose la raison a l'ecran (la pastille de
     *    la rangee 3, `SaveState.reason`) : le chassis n'en fabrique aucune, il
     *    ne saurait pas laquelle ;
     *  - un rejet : la fabrique est censee decider elle-meme quoi en faire
     *    (refuser, c'est son travail). S'il remonte quand meme jusqu'ici, on
     *    n'ouvre rien plutot que de faire agir un geste sur une question restee
     *    sans reponse, et la trace part en console.
     */
    if (confirmPendingRef.current) return;
    confirmPendingRef.current = true;
    const fabrique = action.confirm;
    void (async (): Promise<void> => {
      try {
        const fenetre = await fabrique();
        if (fenetre == null) return;
        setConfirming({ action, confirm: fenetre });
        setConfirmOpen(true);
      } catch (cause) {
        console.error("[taki/ui] La fenetre de confirmation n'a pas pu etre composee :", cause);
      } finally {
        confirmPendingRef.current = false;
      }
    })();
  };

  /*
   * LES TROIS FENETRES DU LOT, elles aussi A LA RACINE de l'arbre (plus bas).
   *
   * LE LOT EST GELE AU CLIC, ids compris. On agit sur ce que l'utilisateur a
   * VU et CONFIRME, jamais sur un lot recompose entre-temps : la fenetre reste
   * ouverte pendant que l'ecran vit derriere elle (un rechargement, un voisin
   * qui archive), et une confirmation qui porterait "3 fiches" pour en ecrire
   * 5 serait le pire defaut possible de ce chantier. C'est la meme regle que
   * `confirming` juste au-dessus, poussee jusqu'aux ids.
   *
   * DEUX ETATS PAR FENETRE (le lot et l'ouverture), pour la meme raison que
   * `confirming` / `confirmOpen` : le Modal s'anime a la FERMETURE, donc le
   * contenu doit rester en place le temps du fondu, sinon la fenetre se vide
   * de son texte avant de disparaitre.
   */
  const [bulkAsk, setBulkAsk] = useState<BulkJob | null>(null);
  const [bulkAskOpen, setBulkAskOpen] = useState(false);
  const [bulkRun, setBulkRun] = useState<BulkJob | null>(null);
  const [bulkRunOpen, setBulkRunOpen] = useState(false);
  const [bulkEdit, setBulkEdit] = useState<Pick<BulkJob, 'ids' | 'labelOf'> | null>(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  /*
   * UN GESTE DE LOT NE S'EMPILE PAS SUR UN AUTRE.
   *
   * RIEN NE L'INTERDISAIT, et le chemin est court : le `Modal` du socle pose
   * bien un voile qui avale les CLICS, mais il ne piege pas le FOCUS et ne rend
   * pas le fond inerte. Un `Maj + Tab` depuis la fenetre revient donc sur la
   * barre de lot restee dessous, et `Entree` y ouvre un SECOND geste pendant
   * que le premier ecrit. La boucle en cours changeait alors de verbe et
   * supprimait ce qu'elle devait archiver.
   *
   * DEUX GARDES, ET LES DEUX SONT VOULUES : celle-ci ferme la porte, celle du
   * moteur de lot gele ce qui passerait quand meme.
   *
   * ON LIT LES TROIS DRAPEAUX D'OUVERTURE (`*Open`) ET JAMAIS LES ETATS GARDES
   * (`bulkAsk`, `bulkRun`, `bulkEdit`) : ceux-la survivent au fondu de
   * fermeture, et s'y fier interdirait un second geste parfaitement legitime
   * pendant la demi-seconde qui suit le premier.
   */
  const bulkBusy = bulkAskOpen || bulkRunOpen || bulkEditOpen;

  /*
   * ET LA GARDE SE VOIT, elle n'est pas seulement muette (G16).
   *
   * Le `if (bulkBusy) return;` des gestes ci-dessous suffit a EMPECHER le
   * second lot ; il ne suffit pas a l'EXPLIQUER. Trois boutons qui ne
   * repondent plus a rien sont exactement le "controle mort et muet" que G16
   * interdit, et le voile du `Modal` etant translucide, on les VOIT rester
   * pleins pendant qu'ils ne font plus rien.
   *
   * LES ETEINDRE FERME AUSSI LE CHEMIN, au lieu de le boucher a l'arrivee :
   * `BulkAction` rend un `Button` reellement `disabled`, donc les trois
   * quittent l'ordre de tabulation, et le `Maj + Tab` qui ouvrait ce trou ne
   * tombe plus dessus. La raison, elle, vit sur la COQUE (un element desactive
   * ne recoit aucun evenement de souris, son propre `title` ne s'afficherait
   * jamais).
   *
   * ELLE PASSE DEVANT TOUTES LES AUTRES, plafond compris : tant qu'une fenetre
   * de lot est a l'ecran, c'est elle qu'il faut traiter, et le reste se dira
   * tout seul apres.
   */
  const bulkBarAvailability = bulkBusy
    ? { edit: BULK_BUSY_REASON, archive: BULK_BUSY_REASON, remove: BULK_BUSY_REASON }
    : bulkAvailability;

  /**
   * RELEVE LES NOMS DES LIGNES AFFICHEES, une fois pour toutes, au moment du
   * clic. Les trois gestes de lot s'en servent : leur compte rendu doit NOMMER
   * les fiches tombees, et a la fin de l'ecriture les fiches ECRITES ont quitte
   * la vue (archivees, supprimees, ou sorties du filtre parce que le lot vient
   * de changer le champ sur lequel l'ecran trie). Relire un nom a ce
   * moment-la ne rendrait que des identifiants, pile pour les lignes qu'il faut
   * pouvoir rattraper. `ProjectedRow.title` n'est jamais vide : il retombe deja
   * sur l'id.
   */
  const freezeLabels = (): ((id: string) => string) => {
    const names = new Map((tools?.rows ?? []).map((row) => [row.id, row.title]));
    return (id: string) => names.get(id) ?? id;
  };

  /**
   * Ouvre la CONFIRMATION d'un geste de lot. Les deux fenetres sont celles du
   * chantier 2_0, avec le NOMBRE ("Archiver 12 fiches ?") : meme boite, meme
   * ton, meme ordre de boutons, meme focus par defaut sur Annuler. Deux
   * fenetres differentes pour un meme geste feraient deux vocabulaires, et le
   * lecteur chercherait ce qui change entre les deux au lieu de lire le nombre.
   */
  const askBulk = (kind: BulkJob['kind'], runOne: (id: string) => Promise<void>): void => {
    // UN LOT A LA FOIS (voir `bulkBusy`). Le bouton est deja eteint AVEC SA
    // RAISON quand on passe ici (`bulkBarAvailability`) : cette garde est la
    // ceinture, pas le message. Elle sort donc en silence, il n'y a rien a dire
    // de plus que ce que le bouton dit deja.
    if (bulkBusy) return;
    setBulkAsk({ kind, ids: [...selection], runOne, labelOf: freezeLabels() });
    setBulkAskOpen(true);
  };

  const toolsHostRef = useRef<HTMLDivElement | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const sortButtonRef = useRef<HTMLButtonElement | null>(null);
  const modesButtonRef = useRef<HTMLButtonElement | null>(null);
  const modesHostRef = useRef<HTMLSpanElement | null>(null);
  const baseId = useId();
  const modesMenuId = `${baseId}-modes-menu`;
  const filterPopId = `${baseId}-filter-pop`;
  const sortPopId = `${baseId}-sort-pop`;
  const drawerId = `${baseId}-pills-wrap`;

  useEffect(() => {
    if (openPop == null) return;
    // Deux hotes : le groupe 3b, et le menu « ... » pose au bout de la rangee.
    const dansUnHote = (event: Event): boolean => {
      const path = event.composedPath();
      return [toolsHostRef.current, modesHostRef.current].some(
        (host) => host != null && path.includes(host),
      );
    };
    const onPointerDown = (event: PointerEvent) => {
      if (dansUnHote(event)) return;
      setOpenPop(null);
    };
    const onDocKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (!dansUnHote(event)) return;
      event.preventDefault();
      event.stopPropagation();
      const trigger =
        openPop === 'filter'
          ? filterButtonRef.current
          : openPop === 'modes'
            ? modesButtonRef.current
            : sortButtonRef.current;
      setOpenPop(null);
      trigger?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onDocKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onDocKey, true);
    };
  }, [openPop]);

  // Changer de porte ou passer en fiche referme les popovers : un menu ouvert
  // sur un catalogue qui vient de changer montrerait des entrees perimees.
  useEffect(() => {
    setOpenPop(null);
  }, [activeMenuId, isRecordLevel]);

  // ENTRER DANS LE LOT REFERME LES POPOVERS, pour la meme raison qu'un
  // changement de porte : la rangee 3 se vide ENTIEREMENT, donc l'outil qui
  // ancrait le panneau n'est plus rendu. Sans ca, `openPop` resterait pose sur
  // un ancrage disparu et le panneau reviendrait, ouvert, a l'annulation de la
  // selection : un menu qu'on n'a pas rouvert.
  useEffect(() => {
    if (bulkActive) setOpenPop(null);
  }, [bulkActive]);

  /*
   * `ECHAP` ANNULE LA SELECTION.
   *
   * OU CETTE ECOUTE SE POSE, ET POURQUOI LA. Trois autres ecoutes d'`Echap`
   * existent deja et l'ordre entre elles a deja coute une correction au
   * chantier 2_0 :
   *
   * 1. `useScreen` ferme la FICHE, en BULLE sur `document`, et son effet n'est
   *    monte QU'AU NIVEAU `record`. La barre de lot, elle, n'existe qu'au
   *    niveau collection : les deux ne peuvent pas se declencher sur la meme
   *    frappe. Rien a arbitrer, mais il fallait le verifier avant d'ecrire.
   * 2. Le menu "..." de la rangee 3c ecoute en CAPTURE avec `stopPropagation`,
   *    justement pour passer AVANT le point 1. Il ne vit lui aussi qu'au niveau
   *    fiche : hors de portee d'ici.
   * 3. Les popovers d'outils (plus haut dans ce fichier) ecoutent en BULLE.
   *    En lot ils sont deja refermes par l'effet ci-dessus, mais l'ordre entre
   *    deux ecoutes de bulle posees sur `document` depend de l'ordre de
   *    MONTAGE : on ne s'y fie pas, on garde explicitement `openPop`.
   *
   * CETTE ECOUTE EST DONC EN BULLE, ET SURTOUT PAS EN CAPTURE. En capture elle
   * passerait AVANT le `Modal`, qui ecoute lui aussi `Escape` sur `document`
   * sans stopper la propagation : une seule frappe fermerait la fenetre de lot
   * ET jetterait la selection dessous. C'est la premiere garde ci-dessous.
   */
  const clearSelectionRef = useRef<(() => void) | undefined>(undefined);
  clearSelectionRef.current = tools?.clearSelection;
  useEffect(() => {
    if (!bulkActive) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      // UN OVERLAY EST OUVERT (une fenetre de lot, la fenetre du mode, un
      // Modal d'app). Il ferme par `Escape` tout seul ; le lot reste, sinon on
      // sortirait d'une fenetre et on perdrait le travail qu'elle preparait.
      // `role="dialog"` est le seul signal commun a tous les overlays du socle,
      // et c'est deja la garde que pose `useScreen`.
      if (document.querySelector('[role="dialog"]') != null) return;
      // ON TAPE DANS UN CHAMP. La recherche 2b, elle, est desormais HORS
      // SERVICE pendant le lot (hachuree et `inert`, zone 2b) : ce qui reste
      // atteignable, c'est un champ de la zone 5 ou d'une app. La, `Echap`
      // annule la saisie ; il ne jette pas le lot par-dessus.
      const active = document.activeElement;
      const tag = active?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      if (active instanceof HTMLElement && active.isContentEditable) return;
      // Un panneau d'outil est ouvert : il a la touche (voir le point 3).
      if (openPop != null) return;
      event.preventDefault();
      clearSelectionRef.current?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [bulkActive, openPop]);

  // LE FAVORI QU'ON VIENT DE FABRIQUER DOIT SE VOIR. `keepFavorite` ajoute la
  // pastille EN BOUT de liste, et le tiers de droite du tiroir est borne : sur
  // une porte qui declare deja plusieurs presets, la nouvelle pastille nait
  // hors du cadre visible, et comme la barre de defilement macOS ne se montre
  // qu'au geste, rien ne dit qu'il faut faire defiler. Le bouton se grise, et
  // c'est le seul retour du geste. On ramene donc le defilement en bout quand
  // la liste GRANDIT, et seulement dans ce cas : en retirer un ne doit pas
  // deplacer ce qu'on regarde.
  const favoritesScrollRef = useRef<HTMLDivElement | null>(null);
  const favoritesCountRef = useRef(favorites.length);
  useEffect(() => {
    const grew = favorites.length > favoritesCountRef.current;
    favoritesCountRef.current = favorites.length;
    if (!grew) return;
    const node = favoritesScrollRef.current;
    if (node != null) node.scrollLeft = node.scrollWidth;
  }, [favorites.length]);

  /* ---- Les entrees des popovers ---- */

  const filterItems: ToolPopoverItem[] = toolFilters.map((filter) => ({
    id: filter.id,
    label: filter.label,
    active: activeFilterIds.includes(filter.id),
    ...(filter.count != null ? { detail: String(filter.count) } : {}),
  }));

  /* ---- Les tags du tiroir (4a) ---- */

  const filterLabelOf = (id: string): string =>
    toolFilters.find((filter) => filter.id === id)?.label ?? id;
  const sortLabelOf = (key: string): string =>
    allSortOptions.find((option) => option.key === key)?.label ?? key;
  // Les modes ne posent plus de tag (28/09/2026) : seuls filtres et tri.
  const hasTags = activeFilterIds.length > 0 || sortStack.length > 0;

  /*
   * LE LIBELLE DU BOUTON 4b, unique explication d'un controle sans texte : il
   * sert de `title` ET d'`aria-label` (G16, tout controle desactive porte sa
   * raison). DEUX raisons de desactivation, deux phrases : `canKeepFavorite`
   * seul ne les distingue pas, il est faux dans les deux cas. Elles se
   * DEDUISENT de ce que le moteur donne deja, aucun champ de plus a declarer :
   * "aucun ecart a l'etat de base" = aucun filtre actif ET pile de tri vide ;
   * "deja garde" = il y a un ecart et le moteur refuse quand meme.
   *
   * Un favori retient des filtres et un tri, jamais un mode : il s'applique
   * donc aussi DANS un mode, sur le jeu du mode.
   */
  const hasFilterOrSortTags = activeFilterIds.length > 0 || sortStack.length > 0;
  const keepLabel = canKeep
    ? 'Garder cet état en favori'
    : (tools?.fieldConditions?.length ?? 0) > 0
      ? 'Ces filtres ne peuvent pas encore être enregistrés'
    : hasFilterOrSortTags
      ? 'Cet état est déjà un favori'
      : "Rien à garder : l'écran est à son état de base";

  /* ---- Rangee 3a : la regle "+ Ajouter" et le lime unique ---- */

  // L'action `kind: 'add'` est rendue en premier, en lime, libelle universel.
  // S'il n'y en a pas, la premiere action declaree `primary` garde sa
  // promotion (decision motivee d'ecran) ; s'il y en a une, toute autre
  // `primary` est RETROGRADEE : jamais deux limes sur un ecran.
  const addAction = quickActions.find((action) => action.kind === 'add');
  const otherActions =
    addAction == null ? quickActions : quickActions.filter((action) => action !== addAction);
  const otherEntries: ReadonlyArray<ScreenAction | NavigationDeSurface | ChoixDeSurface> = canvasActionOrder === undefined
    ? otherActions
    : [...otherActions, ...(garde('actions') ? canvasActionGroups : [])].sort((a, b) => canvasActionOrder.indexOf(a.id) - canvasActionOrder.indexOf(b.id));
  // Les actions `cote: 'droite'` passent au bout, la premiere pousse le reste (`ml-auto`).
  const aDroite = (a: ScreenAction | NavigationDeSurface | ChoixDeSurface): boolean => a.kind !== 'navigation' && a.kind !== 'choix' && (a as ScreenAction).cote === 'droite';
  const entreesRangees = [...otherEntries.filter((a) => !aDroite(a)), ...otherEntries.filter(aDroite)];
  const premiereADroite = entreesRangees.find(aDroite)?.id;
  // DANS UN MODE, « Quitter » (secondaire) prend la place d'« Ajouter » : aucune
  // autre action n'est promue en lime a sa place, l'ecran n'en a donc aucun.
  const promotedId =
    addAction == null && sortieDeMode == null
      ? quickActions.find((action) => action.variant === 'primary')?.id
      : undefined;
  const hasRowActions = quickActions.length > 0 || viewActions.length > 0;

  /*
   * DANS UN MODE, « + AJOUTER » DEVIENT « QUITTER ARCHIVES » / « QUITTER
   * MODELES » (decision du fondateur du 28/09/2026, `libelleDeSortieDeMode`).
   * Meme place, et c'est la place du geste principal : dans un mode, le geste
   * qu'on cherche est d'en sortir. Le bouton scinde (et sa fleche des modeles)
   * n'est donc PAS rendu dans un mode ; creer un modele vierge passe par
   * « Nouveau modele », dans le menu « ... » (`ModesMenu`).
   *
   * Hors mode, un seul fait eteint « Ajouter » : la lecture seule, un fait sur
   * l'UTILISATEUR qu'il ne changera pas lui-meme.
   *
   * LA PHRASE VIENT DU MOTEUR (`tools.raisonAcces`), jamais d'ici : elle est
   * ecrite une fois dans `RAISONS_ACCES` (`lib/acces.tsx`).
   */
  const raisonAcces = tools?.raisonAcces ?? null;
  /*
   * LA PHRASE DE LA PASTILLE D'ENREGISTREMENT : deux refus, une seule surface.
   *
   * `raisonAcces` decrit une SITUATION (cet ecran ne s'ecrit pas) et vaut pour
   * tout ce qu'on y ferait. `saveState.reason` repond au GESTE qu'on vient de
   * faire, avec son compte (« Suppression refusee : 3 fiches liees ... »), et
   * le moteur ne le pose qu'a ce moment-la. Le second gagne donc : il est le
   * plus precis et le plus recent, et l'autre reste vrai une seconde plus tard.
   * Dans les deux cas, la pastille remplace « Echec de l'enregistrement » et
   * retire « Reessayer » : un refus rejoue reste un refus.
   */
  const raisonDeSauvegarde = saveState?.reason ?? raisonAcces;
  /*
   * `raisonAcces` BLOQUE TOUJOURS : creer est une ECRITURE. Mieux vaut un bouton
   * eteint qui dit pourquoi qu'un aller-retour que les rules refuseraient.
   *
   * LA RAISON EST PORTEE PAR UNE COQUE, PAS PAR LE BOUTON. Le `Button` du socle
   * porte `disabled:pointer-events-none` : desactive, il ne recoit aucun
   * evenement de souris, donc son propre `title` ne s'afficherait jamais. Le
   * `title` est pose sur l'enveloppe, qui elle reste survolable.
   */
  const addBlocked = raisonAcces != null;
  /*
   * LA RAISON DE L'ACTION ELLE-MEME, quand c'est elle qui s'eteint et non
   * l'acces : une surface canvas en lecture seule passe `disabled` et sa
   * `disabledReason` (il n'y a pas de `tools.raisonAcces` sans porte), et un
   * « Ajouter » grise sans un mot violerait G16. L'acces prime, comme pour les
   * autres actions de la rangee ; la composition « libelle : raison » est la
   * meme que la leur.
   */
  const addLabel =
    raisonAcces ??
    (addAction?.disabled === true && addAction.disabledReason != null
      ? `Ajouter : ${addAction.disabledReason}`
      : 'Ajouter');

  /* ---- Rangee 3a : le BOUTON SCINDE « Ajouter » et son menu de modeles ---- */

  /*
   * LE SECOND CHEMIN DE CREATION EST LA MOITIE DROITE DU MEME BOUTON.
   * Decision du fondateur du 07/09/2026 : un bouton scinde comme le « Nouveau »
   * de Notion. A gauche l'ajout d'aujourd'hui, inchange ; a droite une fleche
   * ETROITE qui ouvre le menu des modeles de la porte. Ce n'est plus un bouton
   * secondaire pose a cote : deux boutons cote a cote se lisaient comme deux
   * gestes concurrents, alors qu'il n'y en a qu'un (creer une fiche) avec deux
   * points de depart.
   *
   * LE LIME RESTE UNIQUE parce que les deux segments sont UN SEUL bouton :
   * meme fond, un filet entre eux, les coins arrondis seulement a l'exterieur.
   * La regle du lime unique (G14), arbitree quelques lignes plus haut, n'est
   * donc pas entamee.
   *
   * LA FLECHE NE SE CACHE JAMAIS SELON LES DONNEES, et cette moitie-la de la
   * doctrine du 07/09/2026 tient toujours : l'ancienne exception (« cache tant
   * qu'aucun modele n'existe ») reste ABANDONNEE. Modeles allumes et liste
   * VIDE, la fleche est VIVANTE et le menu s'ouvre pour DIRE qu'il n'y en a
   * pas, avec le chemin pour en fabriquer un (G16 : on grise ou on explique, on
   * ne cache pas).
   *
   * CE QUI CHANGE, decision du fondateur du 08/09/2026 : LA CONFIGURATION FAIT
   * DISPARAITRE LA FLECHE AU LIEU DE LA GRISER. Trois frontieres, et une seule
   * question les separe : un geste a l'ecran leve-t-il le manque ?
   *  - CONFIGURATION (pas de port des modeles, pas de `jeu` sur la porte, pas
   *    de `modeles: true`) : ABSENCE. Aucun clic, aucun role, aucun mode ne
   *    fera apparaitre les modeles sur cette porte ; il faut editer
   *    `taki.screens.json` ou monter un `ModelesProvider`. Un demi-bouton
   *    grise A VIE n'enseigne rien a qui le regarde, il fait seulement croire a
   *    une panne du produit. Le bouton redevient alors un « + Ajouter » plein
   *    et ordinaire, sans moignon colle a droite.
   *  - ETAT (mode archives, lecture seule, ecran qui ne sait pas ouvrir la
   *    fiche creee) : GRISAGE avec sa raison (G16, forme 2). Ce sont des faits
   *    qu'on quitte d'un clic, ou qui tiennent a l'utilisateur du moment : la
   *    phrase sert, elle nomme ce qu'il y a a lever.
   *  - DONNEES : JAMAIS, ni absence ni grisage. Voir juste au-dessus.
   *
   * L'ASYMETRIE AVEC LE MENU « ... » DE LA FICHE EST ASSUMEE, pas oubliee :
   * `record.saveAsTemplate` y reste GRISE avec la phrase qui NOMME la clef
   * `modeles: true` (`views/useScreen.ts`). Les deux surfaces ne s'adressent
   * pas au meme lecteur au meme instant : le menu d'une fiche est une LISTE de
   * verbes qu'on parcourt, une entree grisee y apprend la clef manquante ; la
   * rangee 3a est un bouton UNIQUE qu'on vise, et une moitie inerte collee a
   * lui n'apprend rien a personne. Le menu explique, le bouton scinde se tait.
   *
   * LA LISTE EST LUE UNE FOIS PAR PORTE, ET A CHAQUE MODELE ENREGISTRE depuis
   * le menu de la fiche (`tools.modelesRevision`), jamais a chaque rendu : le
   * menu n'ouvre pas d'appel, et changer de porte annule proprement celui
   * d'avant (le drapeau `abandonne`), sinon une reponse lente ecraserait la
   * liste de la porte ou l'on vient d'arriver. Sans le compteur, un premier
   * modele n'apparaissait qu'apres un rechargement de la page.
   */
  const modelesPort = useModelesPort();
  const jeuDeLaPorte = tools?.door?.jeu ?? null;
  const familleDuJeu = jeuDeLaPorte?.family ?? '';
  const typeDuJeu = jeuDeLaPorte?.type ?? '';
  const modelesRevision = tools?.modelesRevision ?? 0;
  const [modeles, setModeles] = useState<ModeleDeFiche[]>([]);
  const [modelesOpen, setModelesOpen] = useState(false);
  /* DEUX ETATS POUR LA CONFIRMATION DE RETRAIT (le modele garde, l'ouverture) :
     le `Modal` s'anime a la fermeture, la question doit rester lisible jusqu'au
     bout du fondu. Meme regle que les autres fenetres du chassis. */
  const [modeleARetirer, setModeleARetirer] = useState<ModeleDeFiche | null>(null);
  const [retraitOpen, setRetraitOpen] = useState(false);
  const modelesMenuId = `${baseId}-modeles-menu`;
  const modelesHostRef = useRef<HTMLSpanElement | null>(null);
  /*
   * CE QUI MANQUE AUX MODELES SUR CETTE PORTE, calcule ICI parce que DEUX choses
   * en dependent : l'effet de lecture juste dessous, et la fleche plus bas. Le
   * calcul et ses phrases vivent dans le PORT (`lib/templates.tsx`), partages
   * avec le moteur qui grise « Enregistrer comme modele » a l'autre bout du meme
   * geste.
   */
  const raisonModeles = raisonDesModeles({
    port: modelesPort,
    jeu: jeuDeLaPorte,
    modeles: tools?.door?.modeles === true,
  });

  useEffect(() => {
    /* ON NE DEMANDE RIEN QUAND LE GESTE EST ETEINT : une porte sans port, sans
       jeu, sans `modeles: true`, ou un utilisateur qui n'ecrit pas ici,
       n'ouvrira jamais ce menu, et l'appel serait un aller-retour reseau pour
       une liste que personne ne verra (et que les rules refuseraient a qui n'a
       aucun acces sur la porte). */
    if (raisonModeles != null || raisonAcces != null || modelesPort == null) {
      setModeles([]);
      return;
    }
    let abandonne = false;
    void (async (): Promise<void> => {
      try {
        const trouves = await modelesPort.list(familleDuJeu, typeDuJeu);
        if (!abandonne) setModeles(trouves);
      } catch (cause) {
        // UNE LISTE QU'ON NE PEUT PAS LIRE EST UNE LISTE VIDE, pas une erreur a
        // l'ecran : le bouton ne se rend pas, l'ecran reste entier, et la
        // console porte la raison pour qui code.
        console.error('[taki/ui] La lecture des modeles a echoue :', cause);
        if (!abandonne) setModeles([]);
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [modelesPort, raisonModeles, raisonAcces, familleDuJeu, typeDuJeu, modelesRevision]);

  /*
   * LA FLECHE SE REND QUAND LES MODELES SONT CONFIGURES ICI, jamais selon les
   * donnees. Trois faits la font disparaitre, et aucun n'est « il n'y a pas
   * encore de modele » :
   *
   * 1. CE QUI MANQUE AUX MODELES EUX-MEMES (`raisonModeles`) : le port, le jeu
   *    de la porte, ou son activation. C'est de la CONFIGURATION, donc une
   *    absence et pas un grisage (la frontiere est ecrite plus haut). CE TEST
   *    ABSORBE L'ANCIEN `modelesPort != null` : le port est le PREMIER echelon
   *    de `raisonDesModeles` (`lib/templates.tsx`), un port absent rend donc
   *    deja une raison, et le retester ici serait une seconde copie de la meme
   *    regle, libre de diverger.
   * 2. UNE PORTE QUI A ABSORBE 3a et n'en recoit donc aucune action (G23).
   * 3. L'ABSENCE DE « + Ajouter », puisque la fleche est la moitie droite de CE
   *    bouton et de rien d'autre.
   * 4. UN « + Ajouter » QUE LE SOCLE NE TIENT PAS (`brouillon` absent : l'app
   *    garde sa propre fenetre d'ajout), ou un ecran qui ne relaie pas
   *    `ouvrirBrouillon`. Depuis le 23/09/2026, choisir un modele OUVRE LE
   *    BROUILLON du socle, pre-rempli : sans lui, le clic ne ferait rien, et un
   *    bouton mort ment (G16). C'est de la CONFIGURATION, donc une absence.
   */
  const chevronRendu =
    raisonModeles == null &&
    !rowActionsOff &&
    addAction != null &&
    addAction.brouillon === true &&
    tools?.ouvrirBrouillon != null;

  /*
   * LES RAISONS QUI L'ETEIGNENT SANS LA RETIRER, DANS CET ORDRE, chacune avec
   * sa phrase (G16, forme 2 : coque survolable, bouton `disabled`). Les trois
   * sont des ETATS, et c'est ce qui leur vaut de rester a l'ecran : un geste
   * les leve, la phrase dit lequel.
   *
   * 1. LE MODE ARCHIVES, avec la MEME phrase que "+ Ajouter" : c'est le meme
   *    fait ("on n'ajoute pas dans les archives") a deux centimetres du meme
   *    endroit, et les deux moities d'un meme bouton ne peuvent pas raconter
   *    deux histoires au meme moment. Il passe donc EN PREMIER, et il PRIME.
   * 2. LE MODE MODELES, et c'est ICI que les deux moities du bouton cessent
   *    pour la premiere fois de dire la meme chose : a gauche « + Ajouter »
   *    reste VIVANT et cree un MODELE ; a droite la fleche ouvrirait une FICHE a
   *    creer depuis un modele, et ca, dans ce mode, n'a pas de sens. La fiche
   *    naitrait dans un jeu que l'ecran ne montre pas, et le geste se solderait
   *    par une liste qui n'a pas bouge. La phrase nomme donc la SORTIE, comme
   *    celle des archives.
   *    C'EST UN ETAT, DONC CA GRISE : un clic sur la croix du tag le leve, et
   *    la phrase dit lequel. Rien a voir avec les trois manques de
   *    configuration, qui font disparaitre la fleche plus haut.
   * 3. LA LECTURE SEULE, avec la MEME phrase que "+ Ajouter" pour exactement la
   *    meme raison : creer depuis un modele CREE une fiche, c'est le meme geste
   *    par un autre chemin, et le meme bouton.
   *
   * « RIEN POUR OUVRIR CE QU'ON CREE » A QUITTE CETTE CASCADE LE 23/09/2026 :
   * la fleche ne cree plus rien, elle ouvre le brouillon du moteur, qui sait
   * s'ouvrir lui-meme. Ce qui la privait de ce brouillon (une action d'ajout que
   * le socle ne tient pas) est de la CONFIGURATION, et la RETIRE plus haut.
   *
   * CE QUI MANQUE AUX MODELES EUX-MEMES N'EST PLUS DANS CETTE CASCADE (decision
   * du fondateur du 08/09/2026) : le port, le jeu et l'activation sont de la
   * CONFIGURATION, ils font DISPARAITRE la fleche quelques lignes plus haut au
   * lieu de la griser. Leurs phrases ne sont pas perdues pour autant : le
   * moteur en grise « Enregistrer comme modele » a l'autre bout du meme geste
   * (`views/useScreen.ts`), la ou une liste de verbes peut les enseigner.
   *
   * LES MODES N'Y SONT PLUS (28/09/2026) : dans un mode, le bouton scinde
   * entier cede la place a « Quitter », fleche comprise. Reste l'acces.
   */
  const chevronReason = raisonAcces;
  const chevronOff = chevronReason != null;
  const chevronLabel = chevronReason ?? 'Créer depuis un modèle';

  /*
   * UN MENU NE SURVIT PAS A LA DISPARITION DE SON BOUTON, NI A SON EXTINCTION.
   * Les trois termes couvrent les deux sorties, et il en faut bien trois :
   * changer pour une porte sans modeles RETIRE la fleche (`chevronRendu`),
   * entrer dans un mode ou perdre l'ecriture l'ETEINT sans la retirer
   * (`chevronOff`), ouvrir une fiche remplace la rangee 3a (`isRecordLevel`).
   * Sans cette remise a zero, l'etat resterait vrai et le panneau se rouvrirait
   * tout seul au retour, sans que personne ne l'ait demande.
   */
  useEffect(() => {
    if (!chevronRendu || chevronOff || isRecordLevel) setModelesOpen(false);
  }, [chevronRendu, chevronOff, isRecordLevel]);

  /*
   * OUVERTURE ET FERMETURE DU MENU : LE MECANISME DU MENU « ... » DE LA FICHE,
   * repris a l'identique (`RecordNavGroup`, plus haut dans ce fichier), et pas
   * un second mecanisme. Un clic dehors ferme, juge par APPARTENANCE a l'ancre
   * et non par cible exacte ; Echap ferme aussi, en phase de CAPTURE sur
   * `document`, parce que `useScreen` ecoute le meme Echap en bulle pour fermer
   * la fiche : sans la capture et le `stopPropagation`, une seule frappe
   * produirait le mauvais effet.
   */
  useEffect(() => {
    if (!modelesOpen) return;
    const onDocClick = (event: MouseEvent) => {
      const host = modelesHostRef.current;
      if (host != null && event.target instanceof Node && host.contains(event.target)) return;
      setModelesOpen(false);
    };
    const onDocKeyCapture = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setModelesOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onDocKeyCapture, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onDocKeyCapture, true);
    };
  }, [modelesOpen]);

  /*
   * CHOISIR UN MODELE OUVRE LA FICHE EN CREATION, PRE-REMPLIE (fondateur,
   * 23/09/2026) : le MEME brouillon que « + Ajouter » (regle G6), et rien n'est
   * ecrit avant « Creer ». La version d'avant creait la fiche EN BASE au clic,
   * fiches liees comprises, sans rien a relire ni a annuler.
   *
   * LE CHASSIS NE CREE PLUS RIEN : il passe le modele au moteur et referme le
   * menu. La sequence d'apres creation (lue, rechargee, ouverte, gel differe)
   * vit une seule fois, dans `creerBrouillon`. Le moteur refuse, et le dit en
   * console, dans les cas que la fleche a deja grises ou retires.
   */
  const ouvrirDepuisModele = (modele: ModeleDeFiche): void => {
    setModelesOpen(false);
    tools?.ouvrirBrouillon?.({ depuisModele: modele });
  };

  /* RETIRER un modele : le port archive, et la liste locale suit tout de suite.
     Relire le port entier pour une ligne en moins ferait clignoter le menu.
     UN MODELE CITE PAR UN PLAN NE PART PAS (chantier `plan-sur-modele`) : la
     croix du menu est deja eteinte, cette garde tient le cas ou la fenetre
     aurait ete ouverte avant que la liste ne le sache. Le SDK refuserait de
     toute facon ; on n'envoie pas une ecriture qu'on sait perdue. */
  const retirerModele = async (templateId: string): Promise<void> => {
    if (modelesPort == null) return;
    const vise = modeles.find((modele) => modele.id === templateId);
    if (vise != null && raisonDeRetraitDuModele(vise) != null) return;
    await modelesPort.archive(templateId);
    setModeles((avant) => avant.filter((modele) => modele.id !== templateId));
  };

  /*
   * UN SEUL PANNEAU A LA FOIS : demander un retrait FERME le menu et ouvre la
   * confirmation. Deux panneaux empiles rendraient la sortie ambigue (Echap
   * ferme lequel ?), et le menu, laisse ouvert, se refermerait de toute facon au
   * premier clic dans la fenetre, qui lui est exterieure. On le ferme donc
   * expres plutot que par accident.
   */
  const demanderRetrait = (modele: ModeleDeFiche): void => {
    setModelesOpen(false);
    setModeleARetirer(modele);
    setRetraitOpen(true);
  };

  // « Reinitialiser » vide la tranche, jamais le jeu : un mode reste allume.
  const clearAllLabel = 'Réinitialiser les filtres et le tri';

  // La rangee 3 se rend des qu'une de ses parties existe, et reste affichee
  // dans TOUS les etats de la zone 5 ET aux deux niveaux : la masquer ferait
  // de la fiche une nasse, sans selecteur ni puce de sortie (3c jamais
  // conditionnel). Les props de fiche (`onRecordExit`, `saveState`) n'entrent
  // pas dans ce calcul : un ecran sans actions, sans outils et sans vues ne
  // gagne aucune rangee.
  // `hasToolGroup` et non `tools != null` : les deux sont equivalents sur un
  // ecran NATIVE (l'outil filtre se rend des que `tools` existe, G20), et c'est
  // ce qui fait tomber la rangee entiere d'un canvas qui ne garde pas 3b.
  // Pastilles V1 et selecteur de vues n'ont, eux, aucun sens sur un canvas.
  // LA FLECHE DES MODELES N'ENTRE PAS DANS CE CALCUL, et elle n'a plus a y
  // entrer : elle est la moitie droite de « + Ajouter », donc elle ne se rend
  // que la ou une action de la rangee se rend deja (`hasRowActions`). Du temps
  // ou c'etait un bouton autonome, il fallait le compter ici pour qu'un ecran
  // qui n'avait que lui garde sa rangee.
  const hasCommandRow = !cadreLibre && (
    hasRowActions || (garde('actions') && canvasActionGroups.length > 0) ||
    // UN MODE A TOUJOURS SA SORTIE, meme sur une porte sans « Ajouter ».
    (sortieDeMode != null && !isRecordLevel) ||
    // LE CHEMIN A TOUJOURS SON RETOUR AU DEBUT.
    chemin != null ||
    showModesMenu ||
    showCanvasMenu ||
    hasToolGroup ||
    // LE SUIVI TIENT LA RANGEE A LUI SEUL, App Canvas compris : c'est un
    // outil du socle sur l'objet affiche, pas une zone que l'app garde (G23).
    suivi != null ||
    ((canvas == null && filters.length > 0) || (garde('vues') && (isRecordLevel || collectionViews.length > 1))));

  // Boite commune des boutons de la rangee 3a : h 2rem, rayon sm, libelle
  // 0.8125rem. Le libelle ne se coupe jamais (G13 : plafond 2rem, title
  // complet) ; la rangee defile au-dela.
  // C'EST LA CONSTANTE DE LA BARRE DE LOT, PAS UNE COPIE (voir `BULK_BUTTON`) :
  // la rangee 3 change de nature sans changer de hauteur. Deux chaines jumelles
  // tenues par un commentaire finissent par diverger, et toute la zone 5
  // sauterait alors d'un cran au premier clic dans une case a cocher.
  const actionButtonClasses = BULK_BUTTON;

  // Chargement temporise (useLoadingState) : skeleton apres un court delai,
  // duree minimale d'affichage, timeout vers l'etat erreur standard.
  const { showLoading, timedOut } = useLoadingState(loading);

  const retryAction =
    onRetry != null ? (
      <Button variant="secondary" size="sm" onClick={onRetry}>
        {retryLabel}
      </Button>
    ) : undefined;

  // Etats standard de la zone 5, par priorite :
  // error > loading > empty > children.
  let zone5: ReactNode;
  if (error != null && error !== '') {
    zone5 = (
      <StateBlock
        role="alert"
        title={errorTitle ?? 'Something went wrong'}
        hint={error}
        action={retryAction}
      />
    );
  } else if (loading && timedOut) {
    // Chargement anormalement long : etat erreur standard, avec relance si
    // l'ecran fournit `onRetry`.
    zone5 = (
      <StateBlock
        role="alert"
        title={errorTitle ?? 'Still loading, something may be wrong'}
        action={retryAction}
      />
    );
  } else if (loading && showLoading) {
    // Skeleton (decoratif : aria-hidden ; l'etat est porte par aria-busy sur
    // la zone 5). Cle dediee : le swap vers le contenu remonte un nouveau
    // conteneur et rejoue le fondu.
    zone5 = (
      <div key="skeleton" aria-hidden className="taki-anim-fade">
        {skeleton ?? <SkeletonListRows />}
      </div>
    );
  } else if (empty && !loading) {
    // L'ETAT VIDE A SON PROPRE TEXTE EN MODE ARCHIVES. Le message standard de
    // l'ecran dirait qu'il n'y a rien du tout, ce qui est faux et inquietant :
    // ici, il n'y a rien D'ARCHIVE, et la sortie est nommee dans la phrase.
    //
    // CETTE BRANCHE NE SERT QU'AUX ECRANS QUI PASSENT ENCORE `empty` (les V1).
    // Un ecran a portes delegue sa zone 5 au `ViewHost`, qui tient la priorite
    // des etats et rend le MEME bloc : c'est LA que ce texte sort en pratique.
    // Les deux doivent rester jumeaux, toute retouche de l'un se reporte sur
    // l'autre.
    zone5 = archivesMode ? (
      <StateBlock
        title="Aucune fiche archivée"
        hint="« Quitter Archives » ramène au travail courant."
      />
    ) : modelesMode ? (
      /* LE MEME TEXTE, AU CARACTERE PRES, QUE CELUI DU `ViewHost` : les deux
         etats vides sont des JUMEAUX OBLIGATOIRES, et c'est celui du ViewHost
         qui sort en pratique (les ecrans a portes lui deleguent la zone 5, ce
         gabarit-ci ne sert plus qu'aux ecrans V1 qui passent encore `empty`).
         L'INDICE NOMME LES DEUX CHEMINS parce qu'il y en a deux et qu'ils ne se
         ressemblent pas : creer un modele nu depuis « + Ajouter », ou partir
         d'une fiche existante et l'enregistrer comme modele depuis son menu. */
      <StateBlock
        title="Aucun modèle pour cette porte"
        hint="Crée un modèle depuis le menu …, ou enregistre une fiche comme modèle."
      />
    ) : (
      <StateBlock title={emptyTitle} hint={emptyHint} />
    );
  } else {
    // Contenu reel (ou fenetre de delai avant skeleton : on garde le contenu
    // affiche, la donnee rapide arrive sans aucun flash). PAS de fondu ici :
    // le contenu monte a chaque changement d'app, un fondu depuis opacity 0 se
    // lirait comme un flash. Le contenu apparait d'un coup ; seul le skeleton
    // (chargement lent avere) fait un fondu d'entree. h-full : conserve le
    // comportement d'un contenu pose directement dans la zone qui defile.
    zone5 = (
      <div key="content" className="h-full">
        {children}
      </div>
    );
  }

  return (
    <div className={garde('fond') ? "flex h-full min-h-0 w-full bg-surface font-sans text-ink-900 max-sm:flex-col" : "flex h-full min-h-0 w-full font-sans text-ink-900 max-sm:flex-col"}>
      {/* ------------------------------------------------------------------
          Zone 1 : le rail. Identite (1a) + menu des portes (1b), RIEN d'autre :
          le rail s'arrete au menu. Les actions vivent en 3a, la zone 7 est
          supprimee.

          EN APP CANVAS, IL N'EST PAS RENDU DU TOUT (G23) : c'est le seul
          discriminant entre une App Canvas et une app native dont des portes
          sont des Space Canvas. Garder le rail, c'est etre native.
          ------------------------------------------------------------------ */}
      {showRail && (
      <aside className="flex w-[13.5rem] shrink-0 flex-col border-r border-nav-border bg-nav-rail max-sm:w-full max-sm:border-b max-sm:border-r-0">
        <nav
          aria-label={appName != null ? `Menu ${appName}` : 'Menu'}
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3 pt-4 max-sm:flex-row max-sm:items-center max-sm:overflow-x-auto max-sm:overflow-y-hidden max-sm:px-2 max-sm:py-1"
        >
          {hasAppIdentity && (
            <div className="mb-3 flex h-9 items-center gap-2.5 px-2 max-sm:mb-0 max-sm:shrink-0">
              {/* LA PUCE EST PARTAGEE AVEC LE STUDIO (`AppIconChip`, plus
                  haut) : c'est elle qui lit `ScreenNav` et qui devient le
                  bouton retour. Le gabarit ne decide plus rien ici. */}
              <AppIconChip icon={appIcon} />
              <span className="min-w-0 truncate text-sm font-semibold text-nav-ink">{appName}</span>
            </div>
          )}
          {menuItems
            .filter((item) => item.placement !== 'bottom')
            .map((item, index, items) => (
              <Fragment key={item.id}>
                {item.groupe != null && items[index - 1]?.groupe?.id !== item.groupe.id && (
                  <RailGroupHeading groupe={item.groupe} first={index === 0} />
                )}
                <RailItem
                  icon={item.icon}
                  label={item.label}
                  count={item.count}
                  brouillon={item.brouillon === true}
                  active={item.id === activeMenuId}
                  onClick={() => onMenuSelect?.(item.id)}
                  {...(item.niveau != null ? { niveau: item.niveau } : {})}
                  {...(menuHierarchyStyle !== undefined && (item.niveau ?? 0) > 0 ? {
                    hierarchy: menuHierarchyStyle,
                    lastChild: (items[index + 1]?.niveau ?? 0) < (item.niveau ?? 0),
                  } : {})}
                  {...(item.deplie != null ? { deplie: item.deplie } : {})}
                  {...(item.onBasculer != null ? { onBasculer: item.onBasculer } : {})}
                  {...(item.onRenommer != null ? { onRenommer: item.onRenommer } : {})}
                />
              </Fragment>
            ))}
          {/* PIED DE RAIL : seulement s'il y a quelque chose a y poser. L'espace
              flexible (basis 0) pousse le groupe en bas quand la liste tient
              dans la hauteur, et s'ecrase a zero quand elle deborde : le pied
              suit alors les portes dans le defilement, il ne les recouvre pas.
              Le filet dit que ce qui suit n'est pas une porte de plus. */}
          {menuItems.some((item) => item.placement === 'bottom') && (
            <>
              <div aria-hidden className="min-h-[1rem] flex-1 max-sm:hidden" />
              <div aria-hidden className="mx-2.5 mb-1 shrink-0 border-t border-nav-border max-sm:hidden" />
              {menuItems
                .filter((item) => item.placement === 'bottom')
                .map((item) => (
                  <RailItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    count={item.count}
                    active={item.id === activeMenuId}
                    onClick={() => onMenuSelect?.(item.id)}
                  />
                ))}
            </>
          )}
        </nav>
      </aside>
      )}

      {/* ------------------------------------------------------------------
          Colonne principale : en-tete (2), rangee de commandes (3), tiroir (4),
          contenu (5).
          ------------------------------------------------------------------ */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Zone 2 : titre (2a), recherche centree sur l'ecran (2b), stats (2c).
            L'ORDRE DE CE QUI CEDE est une regle de chassis : 2c ne cede jamais,
            la recherche cede jusqu'a 9rem, le titre tronque en dernier. Les
            colonnes sont posees sur le conteneur et chaque cellule est placee
            explicitement : sans stat comme sans recherche, la grille reste la
            meme et le titre ne bouge pas (l'equivalent React du [hidden] du
            proto). */}
        {!cadreLibre && <header className="grid shrink-0 grid-cols-[minmax(0,1fr)_minmax(9rem,22rem)_minmax(0,1fr)] items-center gap-5 border-b border-border-soft px-5 py-2 max-sm:grid-cols-1 max-sm:gap-2 max-sm:px-3">
          {/* LE TITRE NOMME LE MODE ALLUME (« Archives : Journal »,
              decision du fondateur du 28/09/2026) : c'est la zone 2 qui dit
              QUEL JEU est a l'ecran. Le prefixe prend la couleur de l'ORG
              (`--accent-dot`, decision du fondateur du 28/09/2026) : l'accent
              lui-meme quand il est fonce, sa version assombrie quand il est
              clair (le lime Taki), donc toujours lisible sur blanc.
              Le texte lu reste le titre entier (`titreDuMode`) : le span ne
              change que la couleur. */}
          <h1
            className={cn("col-start-1 min-w-0 truncate text-[1.3125rem] font-bold tracking-[-0.02em] text-ink-900",
              titleContent != null && !hasSearch && !showStats && 'col-span-3 max-sm:col-span-1')}
            {...(prefixeMode != null
              ? { title: titreDuMode(headerTitle, { archivesMode, modelesMode }) }
              : titreDeSurface != null
                ? { title: `${headerTitle} / ${titreDeSurface}` }
                : chemin?.titre != null
                  ? { title: chemin.titre }
                  : {})}
          >
            {prefixeTitre != null && (
              <span className="text-accent-dot">{`${prefixeTitre} : `}</span>
            )}
            {railDeCanvas && canvasTitleIcon != null && titrePoseParLaSurface != null && (() => {
              const IconeTitre = ICONES_TITRE_CANVAS[canvasTitleIcon];
              return <IconeTitre aria-hidden size={16} className="mr-1.5 inline-block align-text-bottom" />;
            })()}
            {titleContent ?? headerTitle}
            {/* LE TITRE DE LA SURFACE suit le nom de l'app (App Canvas, G23) :
                le separateur est attenue et muet pour un lecteur d'ecran, le
                h1 se lit « Canaux, general ». */}
            {titreDeSurface != null && (
              <>
                <span aria-hidden className="px-2 font-normal text-ink-300">/</span>
                <span className="sr-only">, </span>
                {canvasTitleIcon != null && (() => {
                  const IconeTitre = ICONES_TITRE_CANVAS[canvasTitleIcon];
                  return <IconeTitre aria-hidden size={16} className="mr-1.5 inline-block align-text-bottom" />;
                })()}
                <span>{titreDeSurface}</span>
              </>
            )}
          </h1>
          {hasSearch && (
            /* 2b : rendue aux deux niveaux, la requete survit a la fiche et
               revient telle quelle en collection.

               PENDANT UN LOT, ELLE EST HORS SERVICE, exactement comme le
               tiroir : hachuree, inerte, et elle DIT pourquoi. C'etait le seul
               geste de l'ecran qui detruisait du travail SANS UN MOT, et le
               paradoxe etait net : la rangee 3 a ete videe et le tiroir hachure
               precisement pour empecher qu'un filtre change le jeu sous un lot
               en cours, pendant que la recherche, elle, faisait la meme chose EN
               PIRE. Elle vide la selection ENTIERE des le premier caractere,
               avant meme d'avoir filtre quoi que ce soit : vingt et un clics
               perdus par le reflexe le plus naturel du monde, cliquer dans la
               recherche pour trouver une vingt-deuxieme ligne a ajouter.
               Chercher fait de toute facon tomber le lot ; mieux vaut REFUSER
               le geste que le laisser detruire en silence. */
            <SearchField
              className="col-start-2 max-sm:col-start-1"
              value={searchFieldValue}
              onChange={(value) => {
                if (!searchControlled) setInternalSearch(value);
                onSearchChange(value);
              }}
              placeholder={searchPlaceholder}
              {...(bulkActive ? { horsService: SEARCH_OFF_REASON } : {})}
            />
          )}
          {showStats && (
            /* 2c : memes boites que SkeletonStats (calque au rem pres) : toute
               retouche ici se reporte la-bas, sinon le squelette saute. */
            <div className="col-start-3 flex shrink-0 items-stretch gap-4 justify-self-end max-sm:col-start-1 max-sm:justify-self-start">
              {stats.map((stat, index) => (
                <Fragment key={`${stat.label}-${index}`}>
                  {index > 0 && <span aria-hidden className="w-px bg-border-soft" />}
                  <div className="text-right">
                    <span className="block text-base font-semibold leading-tight text-ink-900">
                      {stat.value}
                    </span>
                    <span className="block text-[0.6875rem] text-ink-400">{stat.label}</span>
                  </div>
                </Fragment>
              ))}
            </div>
          )}
          {/* LE CHEMIN SUR UN TELEPHONE : ses deux puces ont leur ligne, sous
              le titre, avec leur mot court ; le debut prend la place de son
              mot, l'etape d'avant le reste. Dans la rangee 3,
              elles se seraient reduites a leur fleche, a cote des outils qui
              ne cedent pas. Dernier enfant de l'en-tete, pour la meme raison
              que les puces de la rangee 3. */}
          {hasCommandRow && !bulkActive && chemin != null && (
          <div className="col-span-full flex items-center gap-2 sm:hidden">
            <PuceDuChemin
              libelle={chemin.debut.court}
              aria={`Revenir à ${chemin.debut.libelle}`}
              onClick={chemin.debut.aller}
              retour
              className="max-w-[40%] shrink-0"
            />
            {isRecordLevel
              ? onRecordExit != null && (
                  <PuceDuChemin
                    libelle={
                      chemin.etape != null && tools?.sortie?.libelle === chemin.etape.libelle
                        ? chemin.etape.court
                        : (tools?.sortie?.libelle ?? recordExitLabel)
                    }
                    aria={`Revenir à ${tools?.sortie?.libelle ?? recordExitLabel}`}
                    onClick={tools?.sortie?.retour ?? onRecordExit}
                    retour
                    className="max-w-none flex-1"
                  />
                )
              : chemin.parent != null && (
                  <BoutonFicheParente parent={chemin.parent} className="shrink-0" />
                )}
          </div>
        )}
        </header>}

        {/* RANGEE 3, TROISIEME ETAT : LA BARRE DE LOT. Elle REMPLACE la rangee
            entiere (3a, 3b et 3c) des qu'une ligne est cochee.

            C'EST UN BLOC FRERE, PAS UNE BRANCHE DANS LA RANGEE NORMALE, et
            c'est volontaire : la rangee normale garde ainsi son balisage EXACT
            (memes conditions, meme imbrication, meme indentation), donc le
            troisieme etat n'a rien pu casser des deux premiers. Les deux
            portent la MEME coque (`relative z-10`, memes filets, meme retrait,
            meme hauteur de boutons) : la rangee change de nature sans que rien
            ne saute a l'ecran.

            `hasCommandRow` N'ENTRE PAS DANS CETTE CONDITION : une selection
            n'arrive que par `tools`, donc `hasCommandRow` est vrai de toute
            facon ; et si un jour ce n'etait plus le cas, un lot sans barre
            serait un lot sans sortie. */}
        {bulkActive && (
          <div className="relative z-10 flex shrink-0 items-center gap-2 border-b border-border-soft px-5 py-2">
            <BulkBar
              count={selection.length}
              availability={bulkBarAvailability}
              {...(tools?.clearSelection != null
                ? { onClear: () => tools.clearSelection?.() }
                : {})}
              onEdit={() => {
                // UN LOT A LA FOIS (voir `bulkBusy`), la meme ceinture que dans
                // `askBulk`. Le bouton est deja eteint avec sa raison, et un
                // bouton eteint sort de l'ordre de tabulation : le `Maj + Tab`
                // depuis une fenetre ouverte ne tombe donc plus dessus. Cette
                // ligne reste parce qu'une garde qui depend d'un rendu n'est pas
                // une garde.
                if (bulkBusy) return;
                // LE LOT EST GELE ICI AUSSI, noms compris : la fenetre travaille
                // sur les ids qu'on vient de voir, pas sur ceux qu'elle relirait
                // plus tard.
                setBulkEdit({ ids: [...selection], labelOf: freezeLabels() });
                setBulkEditOpen(true);
              }}
              onArchive={() => {
                if (archiveOne != null) askBulk('archive', archiveOne);
              }}
              onRemove={() => {
                if (removeOne != null) askBulk('remove', removeOne);
              }}
            />
          </div>
        )}
        {/* Rangee 3 : 3a actions a gauche, vide au milieu, 3b outils puis le
            groupe epingle 3c a droite. `relative z-10` : les popovers des
            outils se posent PAR-DESSUS la zone 5, jamais rognes par elle.
            Au niveau fiche, la partie gauche devient puce de sortie +
            indicateur d'enregistrement et le groupe epingle passe du selecteur
            de vues au curseur de fiche ; il garde son x (le groupe de gauche
            garde flex-1 aux deux niveaux). */}
        {hasCommandRow && !bulkActive && (
          <div className="relative z-10 flex shrink-0 items-center gap-2 border-b border-border-soft px-5 py-2">
            {/* LE BOUTON SCINDE « Ajouter » EST POSE HORS DU GROUPE DEFILANT,
                et il le faut : son menu est un panneau `absolute`, et un
                conteneur `overflow` tranche tout ce qui depasse (c'est deja ce
                qui a tue l'ancien relief du survol, voir le commentaire du
                groupe plus bas). Pose dans le defilement, le menu serait rogne
                net sous la rangee. Il y gagne au passage d'etre TOUJOURS
                visible : le geste principal ne part plus a gauche quand la
                rangee deborde.
                IL PORTE DONC SA PROPRE GARDE DE NIVEAU (`!isRecordLevel`) : il
                est sorti du ternaire qui distingue collection et fiche, et une
                fiche ouverte n'ajoute rien. */}
            {/* DANS UN MODE, « QUITTER ARCHIVES » PREND LA PLACE D'« AJOUTER »
                (decision du fondateur du 28/09/2026) : meme place, en
                SECONDAIRE, avec la fleche de retour de la puce de sortie d'une
                fiche (`left_back.svg`) : quitter un mode, c'est revenir a la
                vue d'avant, le meme geste. SORTIR ne redemande rien. */}
            {!isRecordLevel && sortieDeMode != null && (
              <Button
                variant="secondary"
                size="sm"
                className={actionButtonClasses}
                title={`${sortieDeMode} : revenir au travail courant`}
                aria-label={`${sortieDeMode} : revenir au travail courant`}
                onClick={() => {
                  if (archivesMode) tools?.setArchivesMode?.(false);
                  else tools?.setModelesMode?.(false);
                }}
              >
                <IconLeftBack size={12} className="shrink-0" />
                {sortieDeMode}
              </Button>
            )}
            {!isRecordLevel && sortieDeMode == null && addAction != null && (
              <span
                key={addAction.id}
                ref={modelesHostRef}
                className="relative flex shrink-0"
              >
                {/* La COQUE porte le title : desactive, le bouton ne recoit
                    plus la souris et n'afficherait jamais le sien (voir
                    `ADD_BLOCKED_REASON` plus haut). Elle est en `flex` et
                    `shrink-0` pour ne rien changer a la geometrie de la
                    rangee : la coque a exactement la boite du bouton.
                    L'AJOUT REFERME LE MENU DES MODELES. Le clic tombe DANS
                    l'ancre, donc la fermeture sur clic exterieur ne le voit
                    pas : sans cette ligne, le formulaire de l'app s'ouvrirait
                    par-dessus un menu reste ouvert derriere lui. */}
                <span title={addLabel} className="flex shrink-0">
                  <Button
                    variant="primary"
                    size="sm"
                    className={cn(actionButtonClasses, chevronRendu && 'rounded-r-none')}
                    aria-label={addLabel}
                    onClick={() => {
                      setModelesOpen(false);
                      /* EN MODE MODELES, LE MEME BOUTON OUVRE LA MEME FICHE
                         VIDE (pivot du 10/09/2026, regle G6). Le chassis n'a
                         plus de branche a lui : `addAction.onClick` est celui
                         que le MOTEUR a resolu (`ouvrirBrouillon`), et c'est le
                         moteur qui sait, au clic sur « Creer », s'il ecrit une
                         fiche ou un modele. La version d'avant ouvrait ici une
                         fenetre « Nouveau modele » a champ unique : elle ne
                         savait demander qu'un nom, alors qu'un modele a des
                         champs, et il fallait ensuite les remplir un a un dans
                         la liste. */
                      addAction.onClick();
                    }}
                    disabled={addAction.disabled === true || addBlocked}
                  >
                    <IconPlus size={15} className="shrink-0" />
                    Ajouter
                  </Button>
                </span>
                {/* LA MOITIE DROITE : la fleche des modeles. ETROITE et
                    COLLEE, meme fond lime, coins arrondis seulement a
                    l'exterieur, et `-ml-px` pour que les deux bordures
                    `--accent-border` se superposent en UN filet de 1px au
                    lieu de deux. Les deux segments forment UN bouton
                    primaire : la regle du lime unique (G14) n'est pas
                    entamee, elle est respectee a la lettre.
                    ETEINTE, ELLE PORTE SA RAISON PAR LA COQUE (G16, forme
                    2), exactement comme "+ Ajouter" a un centimetre de la :
                    un `Button` `disabled` ne recoit aucun evenement de
                    souris, son propre `title` ne s'afficherait jamais. */}
                {chevronRendu &&
                  (chevronOff ? (
                    <span title={chevronLabel} className="flex shrink-0 cursor-not-allowed">
                      <Button
                        variant="primary"
                        size="sm"
                        className={cn(actionButtonClasses, '-ml-px w-7 rounded-l-none px-0')}
                        aria-label={chevronLabel}
                        disabled
                      >
                        <IconChevronDown size={14} className="shrink-0" />
                      </Button>
                    </span>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      className={cn(actionButtonClasses, '-ml-px w-7 rounded-l-none px-0')}
                      title={chevronLabel}
                      aria-label={chevronLabel}
                      aria-haspopup="menu"
                      aria-expanded={modelesOpen}
                      aria-controls={modelesMenuId}
                      onClick={() => setModelesOpen((open) => !open)}
                    >
                      <IconChevronDown size={14} className="shrink-0" />
                    </Button>
                  ))}
                {chevronRendu && !chevronOff && modelesOpen && (
                  <TemplateMenu
                    id={modelesMenuId}
                    {...(tools?.door?.label != null ? { doorLabel: tools.door.label } : {})}
                    modeles={modeles}
                    onChoose={ouvrirDepuisModele}
                    onRemove={demanderRetrait}
                  />
                )}
              </span>
            )}
            {isRecordLevel ? (
              /* LE GROUPE DE GAUCHE PASSE A LA LIGNE (`flex-wrap`) : les gestes
                 visibles n'ont plus de plafond, donc la rangee peut depasser
                 la largeur de l'ecran. Un defilement cacherait des gestes que
                 le fondateur veut voir tous (28/10/2026) ; le passage a la
                 ligne les garde visibles, sans chevauchement ni debordement. */
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-2">
                {onRecordExit != null && (
                  /* La puce de SORTIE de fiche : h 2rem, la ligne de base de
                     toute la rangee. La sortie au doigt et la sortie au
                     clavier sont le MEME geste, mais la touche n'est PAS
                     ANNONCEE (decision du fondateur du 07/08/2026) : Echap sert
                     deja a fermer un editeur et a annuler une saisie sur cet
                     ecran, l'afficher promettait une touche a sens unique. Le
                     geste reste entierement cable (`useScreen`), il n'est plus
                     montre NULLE PART, `title` compris : une infobulle qui
                     souffle « Echap » une seconde apres le survol refait
                     exactement la promesse qu'on vient de retirer, et elle la
                     refait a la personne qui hesite. Le `title` nomme donc la
                     DESTINATION, comme dans le prototype ("Retour a la Liste").
                     La puce GARDE sa coque la ou le groupe epingle est nu :
                     elle porte un MOT et non un glyphe, et un mot pose au bord
                     d'une rangee sans coque se lit comme un titre, pas comme un
                     bouton.

                     UNE FICHE ARRIVEE PAR UN LIEN REECRIT CETTE PUCE, et ne lui
                     en ajoute jamais une seconde (`tools.sortie`) : elle porte
                     alors le NOM de la fiche d'ou l'on vient (« Sony FX9 n°1 »)
                     et y ramene, au lieu de nommer la vue de retour et de
                     ramener a la collection. C'est la seule exception ecrite a
                     la regle G13 (« un libelle ne se construit jamais depuis
                     une donnee ») : ici le libelle DIT ou l'on retombe, et
                     « Liste » mentirait. Regle du fondateur, 09/09/2026. La
                     PRESENCE de la puce, elle, ne bouge pas d'un cheveu : c'est
                     toujours `onRecordExit` qui la decide, donc un ecran qui a
                     choisi de ne pas en avoir n'en gagne pas une. */
                  <button
                    type="button"
                    onClick={tools?.sortie?.retour ?? onRecordExit}
                    title={`Retour à ${tools?.sortie?.libelle ?? recordExitLabel}`}
                    aria-label={
                      tools?.sortie != null
                        ? `Revenir à ${tools.sortie.libelle}`
                        : `Fermer la fiche et revenir à ${recordExitLabel}`
                    }
                    className={cn(
                      chemin != null
                        ? 'flex h-8 min-w-0 shrink items-center gap-2 rounded-[0.625rem] border border-border bg-surface px-3 max-sm:hidden'
                        : 'flex h-8 shrink-0 items-center gap-2 rounded-[0.625rem] border border-border bg-surface px-3',
                      'font-sans text-xs font-semibold text-ink-700 transition-colors',
                      'hover:bg-surface-1 hover:text-ink-900',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                    )}
                  >
                    {/* Le chevron du fondateur (`left_back.svg`), pas un
                        Streamline : c'est le glyphe que le prototype REND ici
                        (`inventaire-fiche.html`, viewBox 131.04 x 226.83 a
                        0.75rem), et la puce etant desormais le SEUL signal
                        visible de la sortie, elle ne peut pas garder le plus
                        maigre des deux dessins. Meme taille que le prototype :
                        12 px de boite, soit 0.75rem. */}
                    <IconLeftBack size={12} {...(chemin != null ? { className: 'shrink-0' } : {})} />
                    <span className="truncate">{tools?.sortie?.libelle ?? recordExitLabel}</span>
                  </button>
                )}
                {/* LES GESTES VISIBLES DE LA FICHE, juste apres la sortie : le
                    geste qui justifie la porte se montre, il ne se merite pas
                    dans le menu « ... » (voir `recordActions`). Memes classes
                    que la rangee 3a, pour que la rangee 3 change de nature sans
                    changer de hauteur. La COQUE porte la raison quand le geste
                    est grise (G16, forme 2) : un bouton desactive ne recoit pas
                    la souris et n'afficherait jamais son propre `title`. */}
                {recordActions.map((action, rang) => (
                  <span
                    key={action.id}
                    title={action.disabled === true ? action.disabledReason : undefined}
                    className="flex shrink-0"
                  >
                    <Button
                      /*
                       * LE LIME EST PERMIS ICI, MAIS JAMAIS DEUX FOIS (G14).
                       * Sur une fiche, l'autre aplat lime possible est
                       * « Creer », qui ne se rend QUE sur un brouillon : tant
                       * qu'il est la, une action de porte redescend en
                       * secondaire, quoi qu'elle demande. Le chassis le garantit
                       * plutot que de le recommander : un commentaire ne tient
                       * pas une regle que deux ecrans peuvent enfreindre.
                       *
                       * ET PARMI LES GESTES DE LA RANGEE, le PREMIER qui le demande
                       * le garde : la vue de fiche et la declaration de la porte
                       * posent leurs gestes chacune de leur cote (28/10/2026), et
                       * deux « primary » feraient deux aplats lime.
                       */
                      variant={
                        action.variant === 'primary' &&
                        tools?.brouillon == null &&
                        recordActions.findIndex((candidate) => candidate.variant === 'primary') === rang
                          ? 'primary'
                          : 'secondary'
                      }
                      size="sm"
                      className={actionButtonClasses}
                      disabled={action.disabled === true}
                      /* `onClick` d'une `ScreenAction` prend des options, pas
                         un evenement de souris : on l'appelle sans rien. */
                      onClick={() => action.onClick()}
                    >
                      {action.label}
                    </Button>
                  </span>
                ))}
                {/* LES EN-TETES (prop `etapes`), cote a cote, alignes a
                    gauche apres la sortie et les gestes visibles, dans la
                    place libre. Jamais sur un brouillon : la rangee garde la
                    place de « Creer » et « Annuler ». */}
                {tools?.brouillon == null && etapes != null && etapes.length > 0 && <EnTetesDeLaFiche etapes={etapes} />}
                {tools?.brouillon != null && (
                  /*
                   * LES DEUX BOUTONS DE LA CREATION EN FICHE, et ils ne se
                   * rendent QUE sur un brouillon : une fiche ordinaire s'edite
                   * en place, sans rien a valider (G6).
                   *
                   * « CREER » EST LE SEUL LIME DE L'ECRAN A CE NIVEAU (G14), et
                   * c'est ce qui rend la regle tenable : « + Ajouter », l'autre
                   * lime du produit, appartient au niveau COLLECTION et n'est
                   * pas rendu sur une fiche. Un seul aplat lime a l'ecran, donc
                   * un seul geste qui se propose, celui qui fait exister la
                   * fiche.
                   *
                   * MEMES CLASSES QUE LA RANGEE 3a (`actionButtonClasses`) :
                   * la rangee 3 change de nature sans changer de hauteur, ici
                   * comme pour la barre de lot. Deux boites jumelles ecrites a
                   * la main auraient fait sauter toute la zone 5 d'un cran au
                   * moment ou l'on ouvre un brouillon.
                   */
                  <span className="flex shrink-0 items-center gap-2">
                    {/* LA COQUE PORTE LA RAISON (G16, forme 2), comme
                        « + Ajouter » : un `Button` eteint ne recoit aucun
                        evenement de souris, son propre `title` ne s'afficherait
                        jamais. Vivant, le `title` nomme le geste et rien
                        d'autre. */}
                    <span
                      title={tools.brouillon.raison ?? 'Créer la fiche'}
                      className={cn(
                        'flex shrink-0',
                        tools.brouillon.raison != null && 'cursor-not-allowed',
                      )}
                    >
                      <Button
                        variant="primary"
                        size="sm"
                        className={actionButtonClasses}
                        aria-label={tools.brouillon.raison ?? 'Créer la fiche'}
                        /* L'ATTENTE VIT SUR LE BOUTON, pas seulement dans la
                           pastille : c'est lui qu'on vient de cliquer, et le
                           `loading` du socle garde sa largeur (pas de saut). */
                        loading={tools.brouillon.enCreation}
                        disabled={tools.brouillon.raison != null}
                        onClick={() => {
                          void tools.creerBrouillon?.();
                        }}
                      >
                        {/* PAS DE GLYPHE : « + Ajouter » OUVRE quelque chose,
                            « Créer » le CONCLUT, et le plus des deux se lisait
                            comme un second ajout. Le lime suffit a designer le
                            geste (decision du fondateur, 09/09/2026). */}
                        Créer
                      </Button>
                    </span>
                    {/* « ANNULER » EST SECONDAIRE, et il ne ferme pas toujours :
                        sur une fiche ou l'on a saisi quelque chose, le moteur
                        pose la fenetre « Fiche non creee » et c'est elle qui
                        repond. Le chassis ne decide pas de ca, il appelle le
                        verbe (meme partage que partout : le moteur decide, le
                        gabarit rend et demande). */}
                    <Button
                      variant="secondary"
                      size="sm"
                      className={actionButtonClasses}
                      title="Annuler la création"
                      aria-label="Annuler la création"
                      onClick={() => tools.annulerBrouillon?.()}
                    >
                      Annuler
                    </Button>
                  </span>
                )}
                {saveState != null && (
                  /* LA RAISON DE L'ETAT GAGNE SUR CELLE DE L'ECRAN
                     (`raisonDeSauvegarde`, plus haut) : deux refus passent par
                     la meme pastille, et le plus precis parle.
                     TOUJOURS A DROITE de la rangee, juste avant le selecteur de
                     vues et le « ... » (`ml-auto`), qu'il y ait des en-tetes ou
                     non (retour du fondateur du 27/10) : il passait a droite
                     avec une piste et restait a gauche sans, la meme
                     information a deux places selon la porte. */
                  <SaveIndicator
                    className="ml-auto"
                    state={saveState}
                    {...(onRetrySave != null ? { onRetry: onRetrySave } : {})}
                    {...(raisonDeSauvegarde != null ? { reason: raisonDeSauvegarde } : {})}
                  />
                )}
                {/* LA FICHE OUVERTE DEPUIS UNE PORTE DANS UNE FICHE a DEUX
                    retours : le debut (ici), puis l'etape d'avant (la puce de
                    sortie, que `tools.sortie` reecrit). Rendue en DERNIER et
                    placee en tete par `order-first` : un enfant insere avant
                    les autres decalerait les identifiants (`useId`) de toute
                    la rangee, figes par `cadre-legacy-golden.json`. */}
                {onRecordExit != null && chemin != null && (
                  <PuceDuChemin
                    libelle={chemin.debut.libelle}
                    onClick={chemin.debut.aller}
                    retour
                    className="order-first max-sm:hidden"
                  />
                )}
              </div>
            ) : (
              /* 3a + pastilles V1 : le groupe DEFILANT. Le survol du primaire
                 est SOBRE (fond eclairci, rien ne bouge, aucune ombre, choix
                 fondateur du 04/08) : aucun degagement d'ombre a menager. Si un
                 relief revenait un jour, se souvenir qu'un conteneur overflow
                 tranche tout ce qui depasse (halo compris) : c'est ce qui a
                 tue l'ancien survol. Le bouton scinde « Ajouter », lui, est
                 pose AVANT ce groupe, hors du defilement : son menu est un
                 panneau `absolute` que ce conteneur trancherait. */
              <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
                {/* LA COQUE PORTE LA RAISON, LE BOUTON NE LE PEUT PAS (G16,
                    forme 2). Le `Button` du socle porte
                    `disabled:pointer-events-none` : eteint, il ne recoit plus
                    aucun evenement de souris, donc le `title` qu'il portait
                    lui-meme ne s'affichait JAMAIS et l'action restait grisee et
                    muette. C'est le `span` parent qui recoit le survol, donc
                    c'est lui qui parle, et qui porte le curseur `not-allowed`.
                    POURQUOI LA FORME 2 ET PAS LA FORME 1 (`aria-disabled`
                    vivant, celle du menu "..." de la rangee fiche, plus haut
                    dans ce fichier) : le bouton "+ Ajouter" juste au-dessus et
                    les gestes de la barre de lot (`BulkAction`) sont deja des
                    coques, et deux formes cote a cote dans la MEME rangee se
                    survoleraient differemment ; et la forme 1 obligerait a
                    neutraliser le clic a la main alors qu'un `disabled` natif
                    le garantit. La raison passe AUSSI en `aria-label` : la
                    coque parle a la souris, l'`aria-label` parle au clavier et
                    aux lecteurs d'ecran. La composition du texte est celle deja
                    en place pour le menu "..." ("libelle : raison") : le
                    libelle survit dans l'infobulle au lieu d'etre remplace par
                    la raison. */}
                {entreesRangees.map((action) => {
                  if (action.kind === 'navigation' || action.kind === 'choix') {
                    const group = action;
                    return group.kind === 'navigation' ? (
                  <div key={group.id} className="flex items-center gap-1">
                    <Button variant="secondary" size="sm" aria-label="Précédent" onClick={group.precedent}><IconLeft size={16} /></Button>
                    <Button variant="secondary" size="sm" onClick={group.aujourdHui}>Aujourd’hui</Button>
                    <Button variant="secondary" size="sm" aria-label="Suivant" onClick={group.suivant}><IconRight size={16} /></Button>
                    <span className="px-2 text-sm text-ink-700">{group.label}</span>
                  </div>
                ) : (
                  <label key={group.id} title={group.options.length === 0 ? "Le catalogue ne propose aucun choix pour le moment." : undefined} className="flex items-center gap-2 text-sm text-ink-700">{group.label}
                    <select className="h-8 rounded-sm border border-border bg-surface px-2 text-ink-900" disabled={group.options.length === 0} value={group.value} onChange={(event) => group.onChange(event.target.value)}>
                      {!group.options.some((option) => option.id === group.value) && <option value={group.value} disabled>{group.valueLabel ?? (group.value === '' ? 'Aucun choix' : 'Indisponible')}</option>}
                      {group.options.map((option) => <option key={option.id} value={option.id}>{option.label}{option.detail ? ` (${option.detail})` : ''}</option>)}
                    </select>
                  </label>
                );
                  }

                  /* LA LECTURE SEULE N'ETEINT QUE CE QUI ECRIT. Une action de
                     porte peut parfaitement LIRE (exporter, imprimer, ouvrir
                     un rapport) : les griser toutes ferait passer un acces en
                     lecture pour une app en panne. Seul `kind: 'add'` tombe,
                     parce que c'est le meme geste que le bouton « Ajouter » a
                     quelques centimetres de la, et qu'un ecran peut en declarer
                     un second. L'ACCES PRIME sur la raison locale : il est plus
                     general (meme ordre que partout ailleurs). */
                  const accesDeLAction =
                    action.kind === 'add' || action.ecrit === true ? raisonAcces : null;
                  const raisonDeLAction = accesDeLAction ?? action.disabledReason ?? null;
                  const off = action.disabled === true || accesDeLAction != null;
                  const label =
                    off && raisonDeLAction != null
                      ? `${action.label} : ${raisonDeLAction}`
                      : action.label;
                  return (
                    <span
                      key={action.id}
                      title={label}
                      className={cn('flex shrink-0', off && 'cursor-not-allowed', action.id === premiereADroite && 'ml-auto')}
                    >
                      <Button
                        variant={action.id === promotedId && action.cote !== 'droite' ? 'primary' : 'secondary'}
                        size="sm"
                        className={actionButtonClasses}
                        aria-label={label}
                        /* LA CONFIRMATION DECLAREE PASSE AVANT L'EFFET, comme au menu
                           « ... » : une action de porte qui declare `confirmation`
                           s'executait sans demander, et le premier bouton qui
                           l'a declaree (« Générer maintenant ») creait des fiches
                           au premier clic. */
                        onClick={() => runRecordAction(action)}
                        disabled={off}
                      >
                        {action.icon != null && (
                          <span className="flex shrink-0 items-center justify-center">
                            {action.icon}
                          </span>
                        )}
                        <span className="whitespace-nowrap">{action.label}</span>
                        {action.detail != null && <span className="whitespace-nowrap tabular-nums text-ink-500">· {action.detail}</span>}
                      </Button>
                    </span>
                  );
                })}
                {/* Les actions de la vue : TOUJOURS secondaires, quoi
                    qu'elles declarent. Clef prefixee : une vue et un ecran
                    peuvent porter le meme id d'action sans se marcher
                    dessus. */}
                {viewActions.map((action) => {
                  // MEME COQUE QUE JUSTE AU-DESSUS (G16, forme 2) : une action
                  // de vue est une `ScreenAction` comme une autre, elle est
                  // rendue dans la meme rangee 3a et se serait eteinte muette
                  // exactement de la meme facon.
                  const off = action.disabled === true;
                  const label =
                    off && action.disabledReason != null
                      ? `${action.label} : ${action.disabledReason}`
                      : action.label;
                  return (
                    <span
                      key={`view:${action.id}`}
                      title={label}
                      className={cn('flex shrink-0', off && 'cursor-not-allowed')}
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        className={actionButtonClasses}
                        aria-label={label}
                        onClick={() => action.onClick()}
                        disabled={action.disabled}
                      >
                        {action.icon != null && (
                          <span className="flex shrink-0 items-center justify-center">
                            {action.icon}
                          </span>
                        )}
                        <span className="whitespace-nowrap">{action.label}</span>
                      </Button>
                    </span>
                  );
                })}
                {/* LECTURE V1 : les pastilles a choix unique, dans la meme
                    rangee (l'ancienne zone 4 a fusionne ici). Elles vivent
                    jusqu'a la migration de l'ecran vers `tools`.

                    ABSORBEES PAR UN CANVAS, comme les outils et le tiroir : un
                    Space Canvas qui garde 3a sur un ecran reste aux pastilles
                    (il en reste trois dans le depot) aurait vu revenir, dans la
                    rangee ouverte pour son action, les pastilles qu'il venait
                    d'absorber. */}
                {(canvas == null ? filters : []).map((filter) => {
                  const active = filter.id === activeFilterId;
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={
                        onFilterSelect != null ? () => onFilterSelect(filter.id) : undefined
                      }
                      aria-pressed={active}
                      className={cn(
                        'h-8 shrink-0 rounded-pill border px-3.5 font-sans text-xs font-semibold transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                        active
                          ? 'border-ink-900 bg-ink-900 text-surface'
                          : 'border-border bg-surface text-ink-500 hover:bg-surface-1 hover:text-ink-900',
                      )}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </div>
            )}
            {/* 3b : le groupe d'outils, meme shell que le selecteur de vues.
                Porte sans tranches (`sections: false`) : il ne garde que le
                tri. Absent au niveau fiche (la fiche ne se filtre pas). */}
            {!isRecordLevel && hasToolGroup && (
              <div
                ref={toolsHostRef}
                role="group"
                aria-label="Outils"
                className="flex h-8 shrink-0 items-center gap-1"
              >
                {showFilterTool && (
                  <span className="relative flex">
                    <Button
                      ref={filterButtonRef}
                      variant="secondary"
                      size="sm"
                      aria-expanded={openPop === 'filter'}
                      aria-controls={filterPopId}
                      aria-haspopup={(tools?.filterFields?.length ?? 0) > 0 ? undefined : 'listbox'}
                      className={cn(
                        'focus-visible:border-accent-border focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
                        openPop === 'filter' && 'border-border-strong bg-surface-1',
                      )}
                      onClick={() => setOpenPop((open) => (open === 'filter' ? null : 'filter'))}
                    >
                      <IconFilter2 size={14} className="pointer-events-none" />
                      Filtres
                      {activeFilterIds.length > 0 && (
                        <span className="pointer-events-none flex h-4 min-w-4 items-center justify-center rounded-pill bg-surface-3 px-1 text-[0.625rem] font-semibold tabular-nums text-ink-700">
                          {activeFilterIds.length}
                        </span>
                      )}
                    </Button>
                    {openPop === 'filter' && ((tools?.filterFields?.length ?? 0) > 0 ? (
                      <FieldFilterPopover
                        id={filterPopId}
                        fields={tools?.filterFields ?? []}
                        conditions={tools?.fieldConditions ?? []}
                        loadedOnly={tools?.pagination === 'server'}
                        hasLegacyFilters={filterItems.some((item) => !item.id.startsWith('~field:') && item.active)}
                        onSave={(condition) => tools?.saveFieldCondition?.(condition)}
                        onRemove={(id) => tools?.removeFilter?.(id)}
                        onClear={() => tools?.clearFilters?.()}
                      />
                    ) : (
                      <FilterPopover
                        id={filterPopId}
                        items={filterItems}
                        onPick={(id) => tools?.toggleFilter?.(id)}
                        clearDisabled={activeFilterIds.length === 0}
                        onClear={() => tools?.clearFilters?.()}
                        selectionIndicator="check"
                      />
                    ))}
                  </span>
                )}
                {showSortTool && (
                  <span className="relative flex">
                    <Button
                      ref={sortButtonRef}
                      variant="secondary"
                      size="sm"
                      aria-expanded={openPop === 'sort'}
                      aria-controls={sortPopId}
                      className={cn(
                        'focus-visible:border-accent-border focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
                        openPop === 'sort' && 'border-border-strong bg-surface-1',
                      )}
                      onClick={() => setOpenPop((open) => (open === 'sort' ? null : 'sort'))}
                    >
                      <IconLineArrowTransferHorizontal1 size={14} className="pointer-events-none rotate-90" />
                      Tri
                      {sortStack.length > 0 && (
                        <span className="pointer-events-none flex h-4 min-w-4 items-center justify-center rounded-pill bg-surface-3 px-1 text-[0.625rem] font-semibold tabular-nums text-ink-700">
                          {sortStack.length}
                        </span>
                      )}
                    </Button>
                    {openPop === 'sort' && (
                      <SortPopover
                        id={sortPopId}
                        options={availableSortOptions}
                        stack={sortStack}
                        onToggle={(key) => tools?.toggleSort?.(key)}
                        onInvert={(key) => tools?.invertSort?.(key)}
                        onRemove={(key) => tools?.removeSort?.(key)}
                        onClear={() => tools?.clearSort?.()}
                      />
                    )}
                  </span>
                )}
                {showStarTool && (
                  /* L'etoile commande le TIROIR (jamais fermee par Escape ni
                     par un clic dehors : seul son bouton, ou une porte).
                     L'etoile SIMPLE en contour (`fav.svg`) : elle ouvre la
                     barre, elle ne fabrique rien. Celle du tiroir (4b) porte le
                     PLUS, sinon les deux boutons sont indiscernables. */
                  <ToolButton
                    icon={<IconFav size={15} />}
                    label="Favoris"
                    expanded={drawerOpen}
                    controls={drawerId}
                    onClick={() => {
                      setOpenPop(null);
                      tools?.setDrawerOpen?.(!drawerOpen);
                    }}
                  />
                )}
              </div>
            )}
            {/* LE SUIVI DE L'OBJET AFFICHE, HORS FICHE : cote outils (3b),
                avant le groupe epingle, sur une porte et sur un App Canvas.
                Sur une fiche, le rail de suivi en tient lieu. Voir la prop
                `suivi`. */}
            {suivi != null && !isRecordLevel && <BoutonSuivi suivi={suivi} />}
            {/* 3c : LE GROUPE EPINGLE, toujours le dernier de la rangee. Sa
                place ne change JAMAIS, son CONTENU change avec le niveau : au
                niveau collection le selecteur de vues, au niveau fiche le
                selecteur des vues de fiche puis les gestes sur la fiche. C'est ce qui fait que la
                rangee ne saute pas quand on ouvre une ligne. Aucun filet avant
                lui : deux groupes cote a cote se separent deja tout seuls. */}
            {isRecordLevel ? (
              <RecordNavGroup
                vuesDeFiche={garde('vues') ? vuesDeFiche : []}
                surLeRail={railDeSuivi}
                actions={menuActions}
                onRunAction={runRecordAction}
                {...(activeViewId != null ? { activeViewId } : {})}
                {...(onViewSelect != null ? { onViewSelect } : {})}
              />
            ) : (
              <ViewSelector
                views={garde('vues') ? collectionViews : []}
                {...(activeViewId != null ? { activeViewId } : {})}
                {...(onViewSelect != null ? { onViewSelect } : {})}
              />
            )}
            {/* LE MENU « ... » DES MODES, TOUT A DROITE (28/09/2026) : Modeles
                puis Archives. Meme glyphe et meme bouton que le « ... » de la
                fiche, a la meme place, derriere le meme filet. ENTRER passe
                par la fenetre du mode, SORTIR non. */}
            {/* 3b D'UN CANVAS : le menu « ... » declare par la surface, a la
                place exacte du menu des modes d'une porte (jamais les deux :
                un canvas n'a pas de `tools`, donc pas de menu des modes). */}
            {showCanvasMenu && canvasMenuActions != null && (
              <CanvasMenuGroup actions={canvasMenuActions} onRunAction={runRecordAction} />
            )}
            {showModesMenu && (
              <>
                <span aria-hidden className="h-4 w-px shrink-0 bg-border" />
                <span ref={modesHostRef} className="relative flex shrink-0">
                  <button
                    ref={modesButtonRef}
                    type="button"
                    title="Modèles et archives"
                    aria-label="Modèles et archives"
                    aria-haspopup="menu"
                    aria-expanded={openPop === 'modes'}
                    aria-controls={modesMenuId}
                    onClick={() => setOpenPop((open) => (open === 'modes' ? null : 'modes'))}
                    className={cn(
                      RECORD_NAV_BUTTON,
                      'cursor-pointer hover:bg-surface-3 hover:text-ink-900',
                      (openPop === 'modes' || archivesMode || modelesMode) &&
                        'bg-surface-3 text-ink-900',
                    )}
                  >
                    <IconFullSpaced size={14} />
                  </button>
                  {openPop === 'modes' && (
                    <ModesMenu
                      id={modesMenuId}
                      archivesMode={archivesMode}
                      {...(tools?.setArchivesMode != null
                        ? {
                            onArchivesToggle: (on: boolean) => {
                              setOpenPop(null);
                              if (!on) {
                                tools.setArchivesMode?.(false);
                                return;
                              }
                              setAskArchivesMode(true);
                            },
                          }
                        : {})}
                      modelesMode={modelesMode}
                      /* L'ENTREE « MODELES » NE SE REND QUE SI LA PORTE LES A
                         CONFIGURES : meme verdict du port (`raisonDesModeles`)
                         que la fleche de « Ajouter ». */
                      showModeles={raisonModeles == null}
                      {...(tools?.setModelesMode != null
                        ? {
                            onModelesToggle: (on: boolean) => {
                              setOpenPop(null);
                              if (!on) {
                                tools.setModelesMode?.(false);
                                return;
                              }
                              setAskModelesMode(true);
                            },
                          }
                        : {})}
                      {...(addAction != null
                        ? {
                            onNewModele: () => {
                              setOpenPop(null);
                              addAction.onClick();
                            },
                            newModeleReason:
                              raisonAcces ??
                              (addAction.disabled === true
                                ? "Cet écran ne sait pas ajouter ici."
                                : null),
                          }
                        : {})}
                    />
                  )}
                </span>
              </>
            )}
            {/* LA PORTE OUVERTE DANS UNE FICHE : le retour au debut, puis
                l'acces discret a la fiche parente, en tete de la rangee
                (`order-first`, rendus en dernier : voir la fiche, plus haut). */}
            {!isRecordLevel && chemin != null && (
              <>
                <PuceDuChemin
                  libelle={chemin.debut.libelle}
                  onClick={chemin.debut.aller}
                  retour
                  className="order-first max-sm:hidden"
                />
                {chemin.parent != null && (
                  <BoutonFicheParente parent={chemin.parent} className="order-first max-sm:hidden" />
                )}
              </>
            )}
          </div>
        )}

        {/* Zone 4 : le TIROIR (tags 4a, garder 4b, favoris 4c). Replie par
            defaut (max-height 0), il s'ouvre au premier tag (regle du moteur)
            et a la demande par l'etoile. Jamais rendu au niveau fiche : l'etat
            est garde par le moteur et revient au retour en collection. La
            barre est a peine teintee (--surface-1), a plat : ce n'est pas une
            Card. `inert` quand il est replie : rien de repliable n'est
            atteignable au clavier. */}
        {/* PENDANT UNE SELECTION, LE TIROIR NE DISPARAIT PAS : IL SE HACHURE.
            Le faire disparaitre ferait sauter la rangee entiere et ferait
            oublier l'etat DEPUIS LEQUEL la selection a ete faite. Hachure, il
            reste lisible et dit qu'il est HORS SERVICE tant que dure le lot :
            filtrer pendant qu'on agit sur un lot serait de toute facon un geste
            qui se contredit (le filtre ferait sortir des lignes du jeu, donc de
            la selection).
            SON ETAT EST GARDE ET REVIENT EXACTEMENT COMME IL ETAIT, sans une
            ligne de code pour ca : rien n'est demonte, rien n'est remis a zero.
            `drawerOpen` appartient au moteur et le chassis n'y touche pas.
            `inert` S'AJOUTE A LA HACHURE. Le calque bloque la SOURIS ; sans
            `inert`, la tabulation garderait une porte derobee vers des
            controles que le doigt ne peut plus atteindre, et "Effacer filtres"
            au clavier changerait le jeu sous un lot en cours. C'est deja
            l'idiome du tiroir replie, quelques caracteres plus loin.
            MAIS `inert` DESCEND D'UN CRAN, SUR LA GRILLE INTERIEURE, et la
            coque exterieure reste survolable : c'est elle qui porte la RAISON
            (`title`) et le curseur barre. Pose sur la coque, `inert` la rendait
            muette : le calque bloque la souris, `inert` bloque le reste, donc
            aucun `title` de l'interieur n'aurait jamais pu s'afficher, et cinq
            clics sans effet (un tag, sa croix, "Effacer filtres", un favori) ne
            s'expliquaient nulle part. La grille etant le seul enfant de la
            coque, ce que `inert` couvre n'a pas change d'un element. */}
        {showDrawer && (
          <div
            id={drawerId}
            title={bulkActive ? DRAWER_OFF_REASON : undefined}
            className={cn(
              'shrink-0 overflow-hidden transition-[max-height]',
              drawerDeployed ? 'max-h-24' : 'max-h-0',
              bulkActive && 'cursor-not-allowed',
            )}
            style={{
              transitionDuration: 'var(--dur-base)',
              transitionTimingFunction: 'var(--ease-standard)',
            }}
          >
            {/* TROIS TIERS, colonnes FIXES : deux tiers pour l'ETAT COURANT
                (les tags defilants, puis le mot qui vide tout), un filet
                STRUCTUREL, un tiers pour les RACCOURCIS (le bouton Garder puis
                les favoris, alignes a GAUCHE dans leur tiers).
                Correction fondateur du 04/08 apres essai : quand les favoris
                partageaient la ligne avec les tags, chaque bascule de favori
                changeait la longueur des tags et faisait sauter les pastilles
                qu'on venait de cliquer. La colonne de droite ne depend donc NI
                des tags NI de ce que la gauche porte.
                Le passage aux trois tiers (07/08) etend cette raison au tiers
                de droite lui-meme : le bouton Garder y est TOUJOURS rendu,
                seulement desactive quand il n'y a rien a garder. Un bouton qui
                apparait et disparait ferait sauter les pastilles pour
                exactement la meme raison. */}
            {/* PENDANT UN LOT, LE TIROIR SE HACHURE (voir `DRAWER_HATCH`), et
                c'est ICI que vit `inert` : replie (rien de repliable n'est
                atteignable au clavier) comme pendant un lot. La coque
                exterieure, elle, reste survolable pour porter la raison. */}
            <div
              inert={!drawerDeployed || bulkActive}
              className={cn(
                'grid grid-cols-[minmax(0,2fr)_auto_minmax(0,1fr)] items-center gap-2.5 border-b border-border bg-surface-1 px-5 py-[0.4375rem]',
                bulkActive && DRAWER_HATCH,
              )}
            >
              {/* LES DEUX TIERS DE GAUCHE : l'ETAT COURANT. L'ecart de gap est
                  porteur de sens : 0.625rem entre les tags et le mot qui vide
                  tout, 0.375rem entre tags. C'est ce qui empeche le mot de se
                  lire comme un tag de plus. */}
              <div className="flex min-w-0 items-center gap-2.5">
                {/* 4a : les tags, dimensionnes par leur contenu, defilants.
                    Ils disent l'ECART a l'etat de base : rien a la base, l'etat
                    complet des qu'il y a un ecart. PILE DE TRI VIDE = l'ordre
                    du chargeur, et on n'affiche RIEN : le pseudo-tag "Ordre
                    d'origine" qui le disait etait du bruit, il a ete retire
                    (decision du fondateur du 07/08). L'entree "Ordre d'origine"
                    du popover de TRI, elle, reste : c'est le chemin qui vide la
                    pile. */}
                {hasTags && (
                  <div className="flex min-w-0 flex-[0_1_auto] items-center gap-1.5 overflow-x-auto">
                    {activeFilterIds.map((id) => (
                      <DrawerTag
                        key={`filter:${id}`}
                        kind="Filtre"
                        value={filterLabelOf(id)}
                        onRemove={() => (tools?.removeFilter ?? tools?.toggleFilter)?.(id)}
                      />
                    ))}
                    {sortStack.map((level, index) => (
                      <DrawerTag
                        key={`sort:${level.key}`}
                        kind="Tri"
                        value={sortLabelOf(level.key)}
                        dir={level.dir}
                        {...(sortStack.length > 1 ? { rank: index + 1 } : {})}
                        {...(tools?.invertSort != null
                          ? { onInvert: () => tools.invertSort?.(level.key) }
                          : {})}
                        {...(tools?.removeSort != null
                          ? { onRemove: () => tools.removeSort?.(level.key) }
                          : {})}
                      />
                    ))}
                  </div>
                )}
                {/* Le controle reste hors du conteneur defilant des tags et
                    vide filtres et tri en un seul rendu. */}
                {hasFilterOrSortTags && tools?.clearAll != null && (
                  <button
                    type="button"
                    onClick={() => tools.clearAll?.()}
                    title={clearAllLabel}
                    aria-label={clearAllLabel}
                    className={cn(
                      'flex h-8 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-pill border-none bg-transparent px-2.5',
                      'font-sans text-xs font-medium text-ink-500 transition-colors hover:bg-surface-3 hover:text-ink-900',
                      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-accent-border',
                    )}
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
              {showShortcuts && (
                <>
                  {/* LE FILET DES TIERS. Il ne separe plus des tags d'un bouton
                      (l'ancien filet, qui disparaissait avec les tags) : il
                      marque la frontiere entre L'ETAT COURANT et les
                      RACCOURCIS. Il est donc STRUCTUREL, et toujours rendu tant
                      que le tiers de droite existe, meme tiroir vide. Sur une
                      porte SANS TRANCHES il part avec lui : un filet qui ne
                      separe plus rien est un trait perdu. */}
                  <span aria-hidden className="h-6 w-px shrink-0 bg-border-strong" />
                  {/* LE TIERS DE DROITE : les RACCOURCIS, alignes a GAUCHE dans
                      leur tiers (pas a droite, pas centres). Le bouton en tete,
                      les favoris ensuite : c'est le bloc des favoris qui cede et
                      defile quand le tiers manque de place, jamais le bouton. */}
                  <div className="flex min-w-0 items-center justify-start gap-2.5">
                    {/* 4b : GARDER l'etat courant en favori. ICONE SEULE, carre
                        de 2rem, la taille des boutons d'outil de la rangee 3 :
                        le mot "Garder" ne parlait a personne.
                        PEAU NUE, ni contour ni fond, seulement un survol. Les
                        pilules bordees appartiennent aux tags et aux favoris,
                        qui sont des CHOSES ; effacer et garder sont des GESTES,
                        et les deux gestes du tiroir se ressemblent. Aucun lime.
                        Il ne se cache jamais A L'INTERIEUR de son tiers (voir le
                        commentaire des trois tiers) et porte sa raison en title
                        dans les DEUX etats (G16), avec l'aria-label qui va avec
                        puisqu'il n'a plus de texte.
                        IL S'ETEINT PAR `aria-disabled`, JAMAIS PAR `disabled` :
                        c'est la condition meme pour que sa raison soit
                        atteignable. Un bouton `disabled` ne recoit aucun
                        evenement de souris, donc son `title` ne s'affiche
                        jamais, et il sort de l'ordre de tabulation, donc son
                        `aria-label` n'est plus annonce non plus. Or l'etat le
                        plus courant du tiroir est justement l'ecran a sa base,
                        celui ou ce bouton est eteint : en `disabled` il serait
                        un rond gris sans texte, sans infobulle et sans focus,
                        exactement ce que G16 interdit.
                        Meme procede que le partage de la rangee 3c et que les
                        entrees grisees du selecteur de vues ; le geste, lui, est
                        garde ici, et le moteur se garde de son cote
                        (`canKeepFavorite`). */}
                    {tools?.keepFavorite != null && (
                      <button
                        type="button"
                        onClick={canKeep ? () => tools.keepFavorite?.() : undefined}
                        aria-disabled={!canKeep || undefined}
                        title={keepLabel}
                        aria-label={keepLabel}
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-pill border-none bg-transparent p-0',
                          'transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                          canKeep
                            ? 'cursor-pointer text-ink-500 hover:bg-surface-3 hover:text-ink-900'
                            : 'cursor-default text-ink-300',
                        )}
                      >
                        {/* L'ETOILE A PLUS du fondateur (`fav-ad-bigbig.svg`),
                            d'un seul tenant : le plus dit le geste (FABRIQUER un
                            favori) que le libelle ne dit plus depuis que le mot
                            "Garder" a disparu. La rangee 3b garde l'etoile
                            simple du meme set, elle OUVRE le tiroir. Deux
                            gestes, deux dessins. */}
                        <IconFavAddBigBig size={15} />
                      </button>
                    )}
                    {/* 4c : les favoris, a la suite du bouton Garder dans le
                        tiers de droite : leur position ne depend NI des tags NI
                        du bouton Garder, qui ne se cache jamais (ils ne bougent
                        pas quand on bascule de l'un a l'autre). Ce sont eux qui
                        cedent et defilent quand le tiers manque de place.
                        Actif = aplat encre (le meme vocabulaire que la pastille
                        active V1) : un favori actif et un tag ne se confondent
                        pas.
                        LE DEFILEMENT EST RAMENE EN BOUT quand la liste GRANDIT
                        (voir `favoritesScrollRef` plus haut) : la pastille qu'on
                        vient de fabriquer nait a la FIN, et sur une porte qui
                        declare deja plusieurs presets elle naitrait hors du
                        cadre, donc le geste de 4b n'aurait aucun retour a
                        l'ecran. */}
                    <div
                      ref={favoritesScrollRef}
                      className="flex min-w-0 flex-[0_1_auto] items-center justify-start gap-1.5 overflow-x-auto"
                    >
                      {favorites.map((favorite) => {
                        const active = favorite.id === (tools?.activeFavoriteId ?? null);
                        /* Seuls les favoris GARDES par l'utilisateur (`user-*`)
                           se retirent : les presets appartiennent a la porte. La
                           croix (meme anatomie que celle des tags 4a) n'apparait
                           qu'au survol de la pastille, et au focus clavier. */
                        const removable =
                          tools?.forgetFavorite != null && favorite.id.startsWith('user-');
                        return (
                          <span
                            key={favorite.id}
                            className={cn(
                              'group/fav flex h-8 shrink-0 items-stretch overflow-hidden rounded-pill border font-sans text-xs font-semibold transition-colors',
                              active
                                ? 'border-ink-900 bg-ink-900 text-surface'
                                : 'border-border bg-surface text-ink-500',
                              !active && 'hover:bg-surface-3 hover:text-ink-900',
                            )}
                          >
                            <button
                              type="button"
                              aria-pressed={active}
                              onClick={() => tools?.applyFavorite?.(favorite.id)}
                              className={cn(
                                'flex items-center border-none bg-transparent text-inherit',
                                removable ? 'pl-3.5 pr-1' : 'px-3.5',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border',
                                'cursor-pointer',
                              )}
                            >
                              {favorite.label}
                            </button>
                            {removable && (
                              <button
                                type="button"
                                onClick={() => tools?.forgetFavorite?.(favorite.id)}
                                title="Retirer ce favori"
                                aria-label={`Retirer le favori ${favorite.label}`}
                                className={cn(
                                  'flex w-6 shrink-0 items-center justify-center border-none bg-transparent transition-colors',
                                  'opacity-0',
                                  'cursor-pointer focus-visible:opacity-100 group-hover/fav:opacity-100',
                                  active
                                    ? 'text-surface hover:bg-ink-700'
                                    : 'text-ink-400 hover:bg-accent-tint hover:text-accent-ink',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border',
                                )}
                              >
                                <IconClose size={10} />
                              </button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Zone 5 : contenu, seule zone de contenu qui defile. `scrollRef` est
            PRETEE a l'ecran (donc au ViewHost et aux vues) : memoire de
            defilement et mesure de cadre. */}
        <PastilleDeStatutRetireeContext.Provider value={isRecordLevel && (etapes ?? []).some((enTete) => enTete.remplaceLaPastille === true)}>
          {railDeSuivi && suivi != null ? (
            /* LA FICHE ET SON SUIVI partagent la rangee de la zone 5 : la
               fiche a gauche, qui RECULE quand le volet s'ouvre (rien ne la
               recouvre), le volet puis le rail de suivi a droite, sur toute
               la hauteur. La fiche ne passe jamais sous le rail. */
            <div className="relative flex min-h-0 flex-1 overflow-hidden">
              <section ref={scrollRef} aria-busy={loading} className="min-h-0 min-w-0 flex-1 overflow-y-auto">
                {zone5}
              </section>
              <SuiviDeFiche suivi={suivi} />
            </div>
          ) : (
            <section ref={scrollRef} aria-busy={loading} className="min-h-0 flex-1 overflow-y-auto">
              {zone5}
            </section>
          )}
        </PastilleDeStatutRetireeContext.Provider>
      </div>

      {/* ------------------------------------------------------------------
          LES FENETRES DU CHASSIS, A LA RACINE de l'arbre et JAMAIS dans la
          rangee 3 : celle-ci est `relative z-10`, donc elle ouvre un contexte
          d'empilement dont un overlay ne sortirait plus.
          ------------------------------------------------------------------ */}

      {/* LA FENETRE D'ENTREE DU MODE. Elle explique ce que le mode change, et
          dit AUSSI par ou l'on sort : c'est la question qu'on se pose une fois
          dedans, et aucun menu ne peut y repondre en une ligne. Elle ne parait
          qu'a l'ENTREE.
          Depuis le 28/09/2026, filtres et tri marchent dans le mode comme
          ailleurs, et la sortie est le bouton qui prend la place d'« Ajouter ». */}
      <ConfirmDialog
        open={askArchivesMode}
        tone="neutral"
        title="Passer en mode archives ?"
        body={[
          "Les archives, et rien d'autre. Filtres et tri s'y appliquent comme ailleurs.",
          'On en sort par « Quitter Archives », à la place de « Ajouter ».',
        ]}
        confirmLabel="Voir les archives"
        onCancel={() => setAskArchivesMode(false)}
        onConfirm={() => {
          setAskArchivesMode(false);
          tools?.setArchivesMode?.(true);
        }}
      />

      {/* LA MEME FENETRE POUR LE MODE MODELES, et le meme partage : elle ne
          parait qu'a l'ENTREE, sortir ne redemande rien.
          « LES MODELES DE LA PORTE » ET NON « LES MODELES » : ils sont ranges
          PAR JEU, et quelqu'un qui a fabrique un modele ailleurs doit
          comprendre tout de suite pourquoi il ne le voit pas ici. C'est la
          meme precision que l'en-tete du menu de la fleche de « Ajouter ».
          Les deux fenetres disent la meme regle avec les memes mots. */}
      <ConfirmDialog
        open={askModelesMode}
        tone="neutral"
        title="Passer en mode modèles ?"
        body={[
          "Les modèles de la porte, et rien d'autre. Filtres et tri s'y appliquent comme ailleurs.",
          'On en sort par « Quitter Modèles », à la place de « Ajouter ».',
        ]}
        confirmLabel="Voir les modèles"
        onCancel={() => setAskModelesMode(false)}
        onConfirm={() => {
          setAskModelesMode(false);
          tools?.setModelesMode?.(true);
        }}
      />

      {/* LA FENETRE « FICHE NON CREEE ». Elle ne parait QUE quand le moteur la
          demande (`sortieEnAttente`) : c'est lui qui sait si quelque chose a ete
          saisi, et une fiche restee intacte sort sans rien demander.
          LES DEUX ISSUES SONT DES VERBES DU MOTEUR, jamais un etat local : la
          sortie qui attend derriere cette fenetre (une puce, `Echap`, un
          changement de porte) est gardee la-bas, et le chassis ne saurait pas la
          rejouer. « Revenir » porte le focus par defaut (comportement de
          `ConfirmDialog`), donc `Entree` ramene a la saisie ; Echap et le voile
          font la meme chose, par `onCancel`. */}
      <ConfirmDialog
        open={tools?.brouillon?.sortieEnAttente === true}
        {...brouillonAbandonConfirm(tools?.brouillon?.titre ?? '')}
        onCancel={() => tools?.reprendreBrouillon?.()}
        onConfirm={() => tools?.confirmerSortieDuBrouillon?.()}
      />

      {/* LA FENETRE D'UNE ACTION QUI PORTE UN `confirm`. C'est le chassis qui
          l'ouvre et qui n'appelle `onClick` qu'apres : l'appelant ne gere
          aucune fenetre, donc il ne peut pas en oublier une.
          L'action est gardee en etat pendant le fondu de fermeture ; on rappelle
          `onClick` sur l'objet CLIQUE, c'est-a-dire l'etat que l'utilisateur a
          vu et confirme, jamais un objet recompose entre-temps. */}
      {confirming != null && (
        <ConfirmDialog
          open={confirmOpen}
          tone={confirming.confirm.tone}
          title={confirming.confirm.title}
          body={confirming.confirm.body}
          confirmLabel={confirming.confirm.confirmLabel}
          {...(confirming.confirm.cancelLabel != null
            ? { cancelLabel: confirming.confirm.cancelLabel }
            : {})}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            confirming.action.onClick();
          }}
        />
      )}

      {/* LA FENETRE A CHAMP D'UNE ACTION QUI PORTE UN `prompt` (« Enregistrer
          comme modele »). Meme partage que la confirmation : le chassis ouvre
          et rend la reponse, le declarant ecrit. Elle ne se referme QUE sur une
          saisie acceptee ou sur une sortie : un echec la garde ouverte avec le
          nom deja tape (cf. `TemplateNameDialog`). */}
      {prompting?.prompt != null && (
        <TemplateNameDialog
          open={promptOpen}
          prompt={prompting.prompt}
          onClose={() => setPromptOpen(false)}
        />
      )}

      {/* LE RETRAIT D'UN MODELE, demande depuis le menu du bouton scinde. La
          fenetre est montee ICI, a la racine, jamais dans la rangee 3 : celle-ci
          est `relative z-10`, un `Modal` pose dedans passerait sous le contenu.
          Le MENU, lui, reste dans la rangee : c'est un panneau, pas une fenetre.
          Le modele vise reste monte pendant le fondu de sortie, sinon la
          question se viderait sous les yeux. */}
      {modeleARetirer != null && (
        <TemplateRemoveDialog
          open={retraitOpen}
          modele={modeleARetirer}
          onCancel={() => setRetraitOpen(false)}
          onConfirm={() => {
            const cible = modeleARetirer.id;
            setRetraitOpen(false);
            void retirerModele(cible).catch((cause: unknown) => {
              // UN RETRAIT QUI ECHOUE NE MENT PAS : la ligne reste dans la
              // liste (elle n'est retiree qu'apres l'ecriture reussie) et la
              // console porte la raison pour qui code.
              console.error('[taki/ui] Le retrait du modele a echoue :', cause);
            });
          }}
        />
      )}

      {/* ------------------------------------------------------------------
          LES FENETRES DU LOT (chantier 3_0), ELLES AUSSI A LA RACINE.
          Montees ICI et JAMAIS dans la rangee 3 : elle est `relative z-10` et
          ouvre un contexte d'empilement, un modal pose dedans passerait SOUS le
          contenu. Le piege a deja ete paye trois fois dans ce pack.

          CE QUI SE PASSE APRES L'ECRITURE N'EST PAS ECRIT ICI, ET C'EST VOULU :
          le chassis ne vide pas la selection a la fermeture. C'est le moteur qui
          recharge la porte ; l'invariant retire alors du lot les lignes qui ont
          quitte le jeu, et la barre se referme d'elle-meme si elle se vide. Un
          `clearSelection()` pose ici masquerait un echec partiel en donnant
          l'air d'un succes : les fiches TOMBEES doivent rester cochees.
          ------------------------------------------------------------------ */}

      {/* LA FENETRE "MODIFICATION GROUPEE". Elle ne se monte que si la porte
          sait ecrire une cellule ; sans ca, le bouton qui l'ouvre est deja
          eteint avec sa raison (voir `bulkAvailability` plus haut), donc il n'y
          a rien a ouvrir. Un champ modifiable manquant, lui, n'empeche PAS
          l'ouverture : c'est la fenetre qui le dit, elle ne s'ouvre pas vide.
          `onRunEnd` n'est pas branche ici non plus : meme raison qu'au
          `BulkRunner` plus bas, le moteur recharge deja de son cote. */}
      {writeCell != null && (
        <BulkEditDialog
          open={bulkEditOpen}
          onClose={() => setBulkEditOpen(false)}
          ids={bulkEdit?.ids ?? []}
          fields={tools?.bulkEditFields ?? []}
          writeCell={writeCell}
          {...(bulkEdit != null ? { labelOf: bulkEdit.labelOf } : {})}
        />
      )}

      {/* LES DEUX CONFIRMATIONS, CELLES DU CHANTIER 2_0, avec le NOMBRE. Meme
          boite, meme ton, meme ordre de boutons, meme focus par defaut sur
          Annuler : c'est le MEME geste que sur une fiche, il porte seulement un
          nombre. Rien de nouveau n'est ecrit ici, et rien ne doit l'etre. */}
      {bulkAsk?.kind === 'archive' && (
        <ArchiveConfirmDialog
          open={bulkAskOpen}
          count={bulkAsk.ids.length}
          onCancel={() => setBulkAskOpen(false)}
          onConfirm={() => {
            setBulkAskOpen(false);
            setBulkRun(bulkAsk);
            setBulkRunOpen(true);
          }}
        />
      )}
      {bulkAsk?.kind === 'remove' && (
        <RemoveConfirmDialog
          open={bulkAskOpen}
          count={bulkAsk.ids.length}
          onCancel={() => setBulkAskOpen(false)}
          onConfirm={() => {
            setBulkAskOpen(false);
            setBulkRun(bulkAsk);
            setBulkRunOpen(true);
          }}
        />
      )}

      {/* L'ECRITURE ELLE-MEME, une fiche a la fois. Le chassis ne BOUCLE PAS :
          il passe les ids et la primitive a document unique, et `BulkRunner`
          porte la progression ("4 sur 12"), le compte rendu, les fiches tombees
          NOMMEES et le rejeu DES ECHECS SEULEMENT. Un "Reessayer" qui rejouerait
          tout le lot reecrirait les fiches deja bonnes.

          `onRunEnd` N'EST PAS BRANCHE, ET C'EST UN CHOIX MOTIVE. Le rechargement
          de fin appartient au MOTEUR, qui le fait deja de son cote apres les
          ecritures, avec un rechargement qui GARDE la selection. Le `reload()`
          public, lui, la fait tomber : branche ici, il effacerait le lot au
          moment precis ou l'utilisateur en a le plus besoin, celui ou le compte
          rendu annonce "10 modifiees, 2 en echec" et ou les deux tombees
          doivent rester cochees pour etre rejouees. */}
      {bulkRun != null && (
        <BulkRunner
          open={bulkRunOpen}
          onClose={() => setBulkRunOpen(false)}
          ids={bulkRun.ids}
          verb={bulkRun.kind}
          runOne={bulkRun.runOne}
          labelOf={bulkRun.labelOf}
        />
      )}
    </div>
  );
}

export default ScreenLayout;
